// DESIGN_AUDIT Phase 11D: spacing and typography fixes
// DESIGN_AUDIT Phase 11C: hardcoded colors replaced with CSS vars
// DESIGN_AUDIT Phase 11B: all modals converted to ReactDOM.createPortal
import ReactDOM from 'react-dom';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Archive, RotateCcw, Trash2, Pencil, Eye, Calendar, Search,
  Download, Upload, ArrowUp, ArrowDown, ArrowLeft, ExternalLink, X, RefreshCw,
  Bell, MapPin, Tag, CheckSquare, ListChecks, Wrench, ChevronDown,
  Check, Users, Settings2, FileText, Phone, Mail, Globe, Clock, Star, Hash, AlertTriangle, Printer,
  Repeat, StickyNote, Link2, Play,
} from 'lucide-react';
import { api } from '../api/client.ts';
import DarkSelect from '../components/DarkSelect.tsx';
import DatePicker from '../components/DatePicker.tsx';
import { EquipmentLinkPicker, equipmentLabel, type BomEquipmentOption } from '../components/EquipmentLinkPicker.tsx';
import FormSchemaBuilder, { type FormField } from '../components/FormSchemaBuilder.tsx';
import {
  LogbookFormFieldInputs, LogbookFormRenderer, countLogbookCheckboxProgress, gridFieldKey,
  findLogbookRangeViolations, formatLogbookViolationMessage,
  type LogbookFormField, type LogbookFormSchema, type LogbookRangeViolation,
} from '../components/LogbookFormRenderer.tsx';
import { printFilledEntryPdf, type FilledEntry } from './Logbooks.tsx';
import { PdfViewerButton } from '../components/PdfViewer.tsx';
import RichTextEditor, { mdToHtml } from '../components/RichTextEditor.tsx';
import { downloadExport as triggerExport } from '../utils/exportHelper.ts';
import { format, isPast, parseISO } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useFacility, useFacilityLogoUrl } from '../contexts/FacilityContext';
import { spinIconOut } from '../utils/iconAnimations.ts';
import PageFocusToggle from '../components/PageFocusToggle.tsx';
import { useTakeoverFocusMode } from '../contexts/PageFocusContext.tsx';
import '../styles/buttonAnimations.css';
import type { VendorContact } from '../types/index.ts';

function ensurePdfExtension(url: string): string {
  if (/\.pdf($|\?)/i.test(url)) return url;
  return url.includes('?') ? url.replace('?', '.pdf?') : url + '.pdf';
}

function getProxyUrl(url: string): string {
  const baseUrl = import.meta.env.VITE_API_URL ?? '';
  const token = localStorage.getItem('accessToken');
  return `${baseUrl}/scheduled-tasks/report-proxy?url=${encodeURIComponent(url)}&token=${token}`;
}

// ─── Local Types ──────────────────────────────────────────────────────────────
interface Task {
  id: number; title: string; description: string | null;
  due_date: string | null; priority: string | null; status: string;
  notes: string | null; is_completed: boolean; is_recurring: boolean;
  req_id: string | null; location: string | null; wing: string | null;
  area: string | null; task_type: string | null; source: string;
  archived_at: string | null; vendor_id: number | null;
  sort_order: number; created_at: string; updated_at: string;
}
interface ScheduleItem {
  id: number; task_name: string; vendor_id: number | null;
  vendor_name: string | null; vendor_name_joined: string | null;
  occurrence_rate: string; sort_order: number;
  is_line_break: boolean; line_break_label: string | null; notes: string | null;
  jan:boolean;feb:boolean;mar:boolean;apr:boolean;may:boolean;jun:boolean;
  jul:boolean;aug:boolean;sep:boolean;oct:boolean;nov:boolean;dec:boolean;
}
interface ManagerItem {
  id:number; req_id:string; title:string; location:string|null;
  wing:string|null; area:string|null; assigned_to:string|null;
  assigned_initials:string|null; category:string|null; status:string;
  priority:string; created_date:string|null; due_date:string|null;
  moved_to_director:boolean; synced_at:string;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const WING_COLORS: Record<string,string> = {
  'EAST WING':'#3b82f6','WEST WING':'#8b5cf6','CENTER WING':'#14b8a6',
  'NORTH WING':'#22c55e','ATRIUM':'#f97316','OUTSIDE':'#6b7280',
  'FACILTY WIDE':'#6366f1','FACILITY WIDE':'#6366f1',
};
const CAT_COLORS: Record<string,string> = {
  'Routine Maint':'#3b82f6','Regulatory':'#f97316','Preventive Maint':'#14b8a6',
  'Safety':'#ef4444',
};
const STATUS_COLORS: Record<string,string> = {
  overdue:'#ef4444', open:'#3b82f6', active:'#10b981', pending:'#f97316',
  completed:'#6b7280', in_progress:'#8b5cf6', cancelled:'#4b5563',
};
const PRIORITY_COLORS: Record<string,string> = {
  high:'#ef4444', medium:'#f59e0b', low:'#10b981', normal:'#6b7280',
};
// combo-jello-spin data-tint reference (BUTTON_SYSTEM.md) — border/text color per tint
const TINT_BORDER: Record<string,string> = {
  neutral:'rgba(255,255,255,0.18)', red:'rgba(239,68,68,0.4)',
  amber:'rgba(245,158,11,0.4)', blue:'rgba(59,130,246,0.4)',
};
const TINT_COLOR: Record<string,string> = {
  neutral:'#cbd5e1', red:'#f87171', amber:'#fbbf24', blue:'#60a5fa',
};
const MONTHS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'] as const;
const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const OCCURRENCES = ['Monthly','Quarterly','Semi-Annual','Annual','Weekly'];

function autoMonths(occ: string): Record<string,boolean> {
  const m: Record<string,boolean> = {};
  MONTHS.forEach(k => m[k]=false);
  switch(occ?.toLowerCase()){
    case 'monthly': MONTHS.forEach(k=>m[k]=true); break;
    case 'quarterly': ['feb','may','aug','nov'].forEach(k=>m[k]=true); break;
    case 'semi-annual': ['feb','aug'].forEach(k=>m[k]=true); break;
  }
  return m;
}

function taskBorderColor(t: Task): string {
  if (t.status==='completed') return '#10b981';
  if (t.status==='overdue' || (t.due_date && isPast(parseISO(t.due_date)) && t.status!=='completed')) return '#ef4444';
  if (t.status==='pending') return '#f97316';
  return '#3b82f6';
}
function isOverdue(t: Task|ManagerItem): boolean {
  if (!t.due_date) return false;
  const s = String(t.due_date).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  const due = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today && t.status !== 'completed';
}

function Badge({ label, color, bg }: { label:string; color:string; bg?:string }) {
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', padding:'2px 8px',
      borderRadius:4, fontSize:'0.75rem', fontWeight:600,
      background: bg ?? `${color}22`, color,
    }}>{label}</span>
  );
}

function DeleteConfirmModal({ message, onConfirm, onClose }: {
  message?: string; onConfirm: () => void; onClose: () => void;
}) {
  const mouseDownTarget = useRef<EventTarget | null>(null);
  return ReactDOM.createPortal(
    <div className="modal-overlay"
      onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
      onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) onClose(); mouseDownTarget.current = null; }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{maxWidth:420,width:'100%'}}>
        <h3 style={{margin:'0 0 12px',fontSize:'1.0625rem',fontWeight:700}}>Delete Task</h3>
        <p style={{color:'var(--text-secondary)',margin:'0 0 24px',fontSize:'0.9375rem'}}>{message ?? 'Are you sure you want to delete this task? This cannot be undone.'}</p>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost combo-cancel-jello" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function RestoreConfirmModal({ onConfirm, onClose }: { onConfirm: () => void; onClose: () => void }) {
  const mouseDownTarget = useRef<EventTarget | null>(null);
  return ReactDOM.createPortal(
    <div className="modal-overlay"
      onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
      onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) onClose(); mouseDownTarget.current = null; }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{maxWidth:420,width:'100%'}}>
        <h3 style={{margin:'0 0 12px',fontSize:'1.0625rem',fontWeight:700,display:'flex',alignItems:'center',gap:8}}>
          <RotateCcw size={18} className="modal-icon-intro" style={{color:'#3b82f6'}}/>
          Restore Task
        </h3>
        <p style={{color:'var(--text-secondary)',margin:'0 0 24px',fontSize:'0.9375rem'}}>Restore this task to your active list?</p>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost combo-cancel-jello" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary combo2" onClick={onConfirm}>Restore</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Shown immediately after a report file is selected (ScheduleTab's handleReportUpload stages the
// file into pendingReport). Replaces a previous inline box that was appended below the Documents
// card's existing reports list -- no modal, no backdrop, no toast, so a user could select a file
// and see nothing that looked like it needed a second click ("does nothing after selecting a
// file" bug). Same two outcomes as before, just impossible to miss now: Confirm & Upload attaches
// the service date (used for fire-logbook auto-linking); Skip still uploads the file, just without
// recording a service date. Backdrop click maps to Skip rather than discarding the file silently
// -- a bare dismiss-with-no-feedback would just reproduce the original bug this modal exists to fix.
function ReportServiceDateModal({ taskName, serviceDate, onServiceDateChange, onConfirm, onSkip }: {
  taskName: string; serviceDate: string; onServiceDateChange: (v: string) => void; onConfirm: () => void; onSkip: () => void;
}) {
  const mouseDownTarget = useRef<EventTarget | null>(null);
  return ReactDOM.createPortal(
    <div className="modal-overlay"
      onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
      onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) onSkip(); mouseDownTarget.current = null; }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420, width: '100%' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: '1.0625rem', fontWeight: 700 }}>When was this service performed?</h3>
        <p style={{ color: 'var(--text-secondary)', margin: '0 0 16px', fontSize: '0.9375rem' }}>Attaching a report to <strong>{taskName}</strong>.</p>
        <div style={{ marginBottom: 20 }}>
          <DatePicker value={serviceDate} onChange={(v) => onServiceDateChange(v)} placeholder="Select a date" />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost combo-cancel-jello" onClick={onSkip}>Skip</button>
          <button className="btn btn-primary combo2" onClick={onConfirm}>Confirm &amp; Upload</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Pre-save confirmation shown only when at least one field is out of range AND the "auto-create a
// follow-up ticket" checkbox is checked (see WoChecklistPanel and the Add Work Order form's
// logbook-preview block, both of which gate opening this behind that same condition -- this modal
// itself has no opinion on when it should appear, it just lists whatever violations it's given).
// Reused by both flows rather than duplicated since the UI is identical either way.
function RangeViolationConfirmModal({ violations, onConfirm, onClose }: {
  violations: LogbookRangeViolation[]; onConfirm: () => void; onClose: () => void;
}) {
  const mouseDownTarget = useRef<EventTarget | null>(null);
  return ReactDOM.createPortal(
    <div className="modal-overlay"
      // .modal-overlay's own default z-index (1000, global.css) sits well below the WO detail
      // takeover (9992) and the Add Work Order takeover (9991) this modal is opened from in both
      // callers -- without this override the dialog genuinely rendered (confirmOpen did flip
      // true) but painted fully behind the opaque takeover, invisible and unclickable, which is
      // exactly what read as "Save does nothing." 9999 matches this file's own existing
      // convention for anything that must sit above a takeover (see TaskDetailPanel's overlay,
      // and the two other 9999/10000-paired confirm dialogs further down this file).
      style={{ zIndex: 9999 }}
      onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
      onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) onClose(); mouseDownTarget.current = null; }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{maxWidth:440,width:'100%'}}>
        <h3 style={{margin:'0 0 12px',fontSize:'1.0625rem',fontWeight:700,display:'flex',alignItems:'center',gap:8}}>
          <AlertTriangle size={18} className="modal-icon-intro" style={{color:'var(--accent-danger)'}}/>
          Create follow-up ticket?
        </h3>
        <p style={{color:'var(--text-secondary)',margin:'0 0 12px',fontSize:'0.9375rem'}}>
          {violations.length === 1 ? 'One reading is' : `${violations.length} readings are`} outside the configured range. Saving will create a follow-up ticket for:
        </p>
        <ul style={{margin:'0 0 22px',paddingLeft:20,display:'flex',flexDirection:'column',gap:6}}>
          {violations.map((v, i) => (
            <li key={i} style={{fontSize:'0.875rem',color:'var(--text-primary)'}}>{formatLogbookViolationMessage(v)}</li>
          ))}
        </ul>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost combo-cancel-jello" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary combo2" onClick={onConfirm}>Save &amp; Create Ticket</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

type Tab = 'active'|'archived'|'schedule'|'manager';
const TABS: {id:Tab; label:string; icon:React.ReactNode}[] = [
  {id:'active',   label:'Reminders',     icon:<CheckSquare size={15} className="btn-icon order-tab-icon"/>},
  {id:'archived', label:'Archived',      icon:<Archive size={15} className="btn-icon order-tab-icon"/>},
  {id:'schedule', label:'Compliance PM', icon:<Calendar size={15} className="btn-icon order-tab-icon"/>},
  {id:'manager',  label:'Task Manager',  icon:<ListChecks size={15} className="btn-icon order-tab-icon"/>},
];

// Compliance PM's Smart View/Timeline/Cards sub-nav — module scope (not inside ScheduleTab)
// since TasksPage now renders it too, inline with the main tab row (item 1).
const PM_VIEWS: { id: 'smart' | 'timeline' | 'cards'; label: string }[] = [
  { id: 'smart', label: 'Smart View' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'cards', label: 'Cards' },
];

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function TasksPage() {
  const searchParams = new URLSearchParams(window.location.search);
  const highlightTaskId = searchParams.get('highlightTask');
  const urlTab = searchParams.get('tab');

  const [tab, setTab] = useState<Tab>(() => {
    if (urlTab === 'schedule') return 'schedule';
    return 'active';
  });

  // Real iPhone screenshot: this page had zero isMobile usage anywhere (unlike Inventory.tsx,
  // which already had this exact pattern in several places) — the earlier "mobile pass" for
  // this page never actually added responsive JS state here, so the main tab bar below had no
  // way to react to viewport width at all. Same window.innerWidth <= 768 pattern already used
  // throughout Inventory.tsx.
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Compliance PM's Smart View/Timeline/Cards switcher — lifted up from ScheduleTab (item 1)
  // so it can render inline with the main tab row instead of on its own row below it. Same
  // localStorage persistence ScheduleTab used to own.
  const [pmView, setPmView] = useState<'smart' | 'timeline' | 'cards'>(() => {
    const saved = localStorage.getItem('compliancePmView');
    if (saved === 'timeline' || saved === 'cards') return saved;
    return 'smart';
  });
  useEffect(() => { localStorage.setItem('compliancePmView', pmView); }, [pmView]);
  // Timeline's stacked mobile layout wasn't landing visually — rather than keep iterating on a
  // layout that isn't working, Timeline is just not offered as an option on mobile at all (see
  // PM_VIEWS.filter below). This falls back to Smart View if the viewport becomes mobile-sized
  // *while* Timeline was already selected (tablet rotation, window resize) — otherwise the
  // filtered switcher would just hide the active segment with nothing shown as selected, and
  // ScheduleTab would keep rendering ComplianceTimelineView (still a valid pmView value)
  // underneath the now-hidden switcher.
  useEffect(() => {
    if (isMobile && pmView === 'timeline') setPmView('smart');
  }, [isMobile, pmView]);

  return (
    <div style={{minHeight:'100%'}}>
      {/* Focus mode toggle — same floating-action-cluster (AppLayout.css) + PageFocusToggle
          Dashboard.tsx uses; covers every tab under this page (Reminders/Archived/Compliance
          PM/Task Manager/Schedule) since it's a route-level chrome toggle, not per-tab. */}
      <div className="floating-action-cluster"><PageFocusToggle /></div>
      <div style={{marginBottom:20}}>
        <h1 style={{fontSize:'1.5rem',fontWeight:700,margin:0}}>Tasks</h1>
        <p style={{color:'var(--text-muted)',margin:'4px 0 0',fontSize:'0.875rem'}}>
          Director task reminders, Compliance PM schedule, and Task Manager work orders, schedules &amp; staff view — includes the Reqqer ticket mirror
        </p>
      </div>
      {/* Tab bar — main tabs left, Compliance PM's Smart View/Timeline/Cards switcher right
          (item 1), only shown while that tab is active. pill-tab-sub gives it a light-blue
          tint (buttonAnimations.css) so it reads as sub-navigation, not a second copy of the
          main tabs — same pill-tab-container/pill-tab structure, just an added modifier class. */}
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10, marginBottom:16}}>
        {/* Same horizontal-scroll treatment just applied to Inventory's main tab switcher —
            NOT shared code (each page's own <div className="pill-tab-container"> gets its own
            inline overrides; the shared CSS class itself has no overflow-x), so this page needed
            its own copy. maxWidth:'100%' (not width:'100%' like Inventory's title+tabs row) since
            this row can also flex-wrap the Smart View/Timeline/Cards sub-switcher onto its own
            line — capping at 100% instead of forcing full width leaves that wrap behavior intact
            on viewports where both groups actually fit. */}
        <div className="pill-tab-container" style={{
          overflowX: isMobile ? 'auto' : undefined,
          maxWidth: isMobile ? '100%' : undefined,
          WebkitOverflowScrolling: isMobile ? 'touch' : undefined,
          touchAction: isMobile ? 'pan-x' : undefined,
        }}>
          {TABS.map(t=>(
            <button key={t.id} className={`order-tab pill-tab${tab===t.id ? ' active' : ''}`} onClick={()=>setTab(t.id)}>
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>
        {tab === 'schedule' && (
          <div className="pill-tab-container pill-tab-sub">
            {/* Timeline hidden on mobile — see the isMobile/pmView reset effect above */}
            {PM_VIEWS.filter(v => !(isMobile && v.id === 'timeline')).map(v => (
              <button key={v.id} className={`pill-tab pill-tab-sub${pmView === v.id ? ' active' : ''}`} onClick={() => setPmView(v.id)}>
                {v.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {tab==='active'   && <ActiveTasksTab highlightTaskId={highlightTaskId}/>}
      {tab==='archived' && <ArchivedTasksTab/>}
      {tab==='schedule' && <ScheduleTab highlightTaskId={highlightTaskId} pmView={pmView} setPmView={setPmView}/>}
      {tab==='manager'  && <TaskManagerTab/>}
    </div>
  );
}

// ─── Tab 1: Active Tasks ──────────────────────────────────────────────────────
function ActiveTasksTab({ highlightTaskId }: { highlightTaskId?: string | null }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [priority, setPriority] = useState('');
  const [selected, setSelected] = useState<Task|null>(null);
  const [modal, setModal] = useState<{open:boolean; task?:Task}>({open:false});
  const [deleteModal, setDeleteModal] = useState<{open:boolean; onConfirm:()=>void}>({open:false,onConfirm:()=>{}});
  const [archiveToast, setArchiveToast] = useState<{ task: Task } | null>(null);
  const [qapiFromTask, setQapiFromTask] = useState<Task | null>(null);
  const taskRowRefs = useRef<Record<number, HTMLDivElement | null>>({});
  // Real iPhone screenshot: this row crammed title + priority/status badges + 5 action icons
  // into one flex row (title in a flex:1 left block, badges+icons in a flexShrink:0 right
  // block that never shrinks or wraps) — the right block just overflowed off the edge on
  // narrow viewports, cutting off the delete icon entirely, while the title got squeezed into
  // 4 cramped lines. Same window.innerWidth<=768 pattern used throughout Inventory.tsx.
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const didHighlight = useRef(false);

  const {data:res, isLoading} = useQuery({
    queryKey:['tasks',search,priority],
    queryFn:()=>api.get<Task[]>(`/tasks?search=${encodeURIComponent(search)}&priority=${priority}`),
  });
  const tasks: Task[] = res?.data ?? [];

  useEffect(() => {
    if (!highlightTaskId || didHighlight.current || tasks.length === 0) return;
    const id = Number(highlightTaskId);
    const el = taskRowRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      didHighlight.current = true;
    }
  }, [tasks, highlightTaskId]);

  function showArchiveToast(task?: Task) {
    if (!task) return;
    setArchiveToast({ task });
    setTimeout(() => setArchiveToast(null), 8000);
  }

  const archiveMut = useMutation({
    mutationFn:(id:number)=>api.post(`/tasks/${id}/archive`,{}),
    onSuccess: (_: any, taskId: number) => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['scheduled-tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard-reminders'] });
      const task = tasks?.find(t => t.id === taskId);
      showArchiveToast(task);
    },
  });
  const deleteMut = useMutation({
    mutationFn:(id:number)=>api.delete(`/tasks/${id}`),
    onSuccess:()=>{qc.invalidateQueries({queryKey:['tasks']}); if(selected) setSelected(null);},
  });

  function download(url:string, filename:string) {
    triggerExport(url, filename);
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{display:'flex',gap:10,marginBottom:16,alignItems:'center',flexWrap:'wrap'}}>
        <div className="search-bar search-input-wrapper" style={{flex:'1 1 220px'}}>
          <Search size={14} className="btn-icon" color="var(--text-muted)"/>
          <input placeholder="Search tasks…"
            value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <select className="form-input" value={priority} onChange={e=>setPriority(e.target.value)} style={{width:140}}>
          <option value="">All Priority</option>
          {['high','medium','low'].map(p=><option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
        </select>
        <button className="btn combo5-hero" onClick={()=>setModal({open:true})} onMouseLeave={spinIconOut}>
          <Plus size={15} className="btn-icon"/> Add Task
        </button>
      </div>
      {/* Cards */}
      {isLoading && <div style={{color:'var(--text-muted)',textAlign:'center',padding:40}}>Loading…</div>}
      {!isLoading && tasks.length===0 && (
        <div style={{textAlign:'center',padding:'60px 0',color:'var(--text-muted)'}}>
          <CheckSquare size={40} style={{opacity:0.3,marginBottom:12}}/><br/>No reminders
        </div>
      )}
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {tasks.map(t=>(
          <div key={t.id}
            ref={el => { taskRowRefs.current[t.id] = el; }}
            className={t.id === Number(highlightTaskId) ? 'task-highlight-glow' : ''}
            style={{
              display:'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap:0,
              background: isOverdue(t) ? 'rgba(239,68,68,0.06)' : 'rgba(255,255,255,0.045)',
              backdropFilter:'blur(16px)', WebkitBackdropFilter:'blur(16px)',
              border:'1px solid var(--border-subtle)',borderRadius:'var(--radius-md)',
              borderLeft:`4px solid ${taskBorderColor(t)}`,overflow:'hidden',
            }}>
            <div style={{flex:1,padding:'14px 16px',minWidth:0}}>
              <div style={{fontWeight:700,fontSize:'1rem',marginBottom:4}}>{t.title}</div>
              {/* On mobile the priority/status badges move up here, next to the rest of the
                  row's meta, instead of sitting with the action icons below — gives the
                  stacked layout the requested 3-tier shape: title / badges+meta / icons. */}
              <div style={{display:'flex',gap:12,flexWrap:'wrap',alignItems:'center',fontSize:'0.8125rem',color:'var(--text-secondary)'}}>
                {isMobile && t.priority && <Badge label={t.priority} color={PRIORITY_COLORS[t.priority]||'#6b7280'}/>}
                {isMobile && <Badge label={t.status} color={STATUS_COLORS[t.status]||'#6b7280'}/>}
                {t.req_id && <span style={{color:'var(--text-muted)'}}>#{t.req_id}</span>}
                {t.location && <span style={{display:'flex',alignItems:'center',gap:4}}><MapPin size={11}/>{t.location}</span>}
                {t.task_type && <span style={{display:'flex',alignItems:'center',gap:4}}><Tag size={11}/>{t.task_type}</span>}
              </div>
              {t.due_date && (
                <div style={{display:'flex',alignItems:'center',gap:4,marginTop:4,fontSize:'0.8125rem',
                  color: isOverdue(t) ? '#ef4444' : 'var(--text-muted)'}}>
                  <Calendar size={11}/>
                  Due: {(() => {
                    const s = String(t.due_date).slice(0, 10);
                    const [y, m, d] = s.split('-').map(Number);
                    const date = new Date(y, m - 1, d);
                    return date.toLocaleDateString('en-US', { weekday: 'short', month: '2-digit', day: '2-digit', year: 'numeric' });
                  })()}
                </div>
              )}
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8,padding: isMobile ? '0 16px 14px' : '0 16px',flexShrink:0,flexWrap: isMobile ? 'wrap' : 'nowrap'}}>
              {/* Action buttons before the chips (was chips-then-buttons) — since the full-page-
                  takeover focus toggle floats at bottom-right (fixed position), a row landing near
                  the bottom of the viewport had its rightmost, clickable trash/archive icons
                  directly under that button. Chips are now rightmost instead — covering a status
                  pill is harmless, covering Delete isn't. */}
              <div style={{display:'flex',gap:4}}>
                {[
                  {icon:<Bell size={isMobile ? 18 : 14} className="btn-icon"/>,  title:'Reminder', tint:'amber',   action:()=>{}},
                  {icon:<Eye size={isMobile ? 18 : 14} className="btn-icon"/>,   title:'View',     tint:'blue',    action:()=>setSelected(t)},
                  {icon:<Pencil size={isMobile ? 18 : 14} className="btn-icon"/>,title:'Edit',     tint:'neutral', action:()=>setModal({open:true,task:t})},
                  {icon:<Archive size={isMobile ? 18 : 14} className="btn-icon"/>,title:'Archive', tint:'neutral', action:()=>archiveMut.mutate(t.id)},
                  {icon:<Trash2 size={isMobile ? 18 : 14} className="btn-icon"/>, title:'Delete',  tint:'red',     action:()=>setDeleteModal({open:true,onConfirm:()=>deleteMut.mutate(t.id)})},
                ].map((btn,i)=>(
                  <button key={i} onClick={btn.action} title={btn.title} onMouseLeave={spinIconOut}
                    className="combo-jello-spin"
                    data-tint={btn.tint}
                    style={{
                      border: `1px solid ${TINT_BORDER[btn.tint]}`, borderRadius:'var(--radius-sm)',
                      // 44x44 real touch target on mobile (standard baseline) — was padding:'5px
                      // 6px' at all sizes, roughly 24x26px with a 14px icon, too small to
                      // reliably tap once the row restructure (last round) moved these into
                      // their own dedicated icon row on mobile.
                      padding: isMobile ? undefined : '5px 6px',
                      width: isMobile ? 44 : undefined, height: isMobile ? 44 : undefined,
                      cursor:'pointer', color:TINT_COLOR[btn.tint], display:'flex',
                      alignItems:'center', justifyContent: isMobile ? 'center' : undefined,
                    }}
                  >{btn.icon}</button>
                ))}
              </div>
              {!isMobile && t.priority && <Badge label={t.priority} color={PRIORITY_COLORS[t.priority]||'#6b7280'}/>}
              {!isMobile && <Badge label={t.status} color={STATUS_COLORS[t.status]||'#6b7280'}/>}
            </div>
          </div>
        ))}
      </div>
      {/* Detail panel */}
      {selected && <TaskDetailPanel task={selected} onClose={()=>setSelected(null)}
        onEdit={()=>{setModal({open:true,task:selected});setSelected(null);}}
        onArchive={()=>{const task=selected;archiveMut.mutate(selected.id);setSelected(null);showArchiveToast(task);}}
        onDelete={()=>setDeleteModal({open:true,onConfirm:()=>{deleteMut.mutate(selected.id);setSelected(null);}})}/>}
      {/* Modal */}
      {modal.open && <TaskModal task={modal.task} onClose={()=>setModal({open:false})}
        onSaved={()=>{qc.invalidateQueries({queryKey:['tasks']});setModal({open:false});}}/>}
      {deleteModal.open && <DeleteConfirmModal
        onConfirm={()=>{deleteModal.onConfirm();setDeleteModal({open:false,onConfirm:()=>{}}); }}
        onClose={()=>setDeleteModal({open:false,onConfirm:()=>{}})}/>}
      {archiveToast && (
        <div style={{
          position: 'fixed', bottom: 32, right: 32, zIndex: 999,
          background: 'var(--bg-elevated)', border: '1px solid rgba(59,130,246,0.3)',
          borderRadius: 12, padding: '14px 18px',
          display: 'flex', alignItems: 'center', gap: 14,
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)', minWidth: 300,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', fontWeight: 500 }}>Task archived</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{archiveToast.task.title}</div>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => { setQapiFromTask(archiveToast.task); setArchiveToast(null); }}
            style={{ borderRadius: 8, padding: '7px 14px', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}
          >
            + Add to QAPI
          </button>
          <button
            onClick={() => setArchiveToast(null)}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18, padding: 0 }}
          >
            ×
          </button>
        </div>
      )}
      {qapiFromTask && (
        <AddQapiFromTaskModal
          task={qapiFromTask}
          onClose={() => setQapiFromTask(null)}
          onSaved={() => setQapiFromTask(null)}
        />
      )}
    </div>
  );
}

// ─── Task Detail Panel ────────────────────────────────────────────────────────
function TaskDetailPanel({task,onClose,onEdit,onArchive,onDelete}:{
  task:Task; onClose:()=>void; onEdit:()=>void; onArchive:()=>void; onDelete:()=>void;
}) {
  useEffect(()=>{document.body.style.overflow='hidden';return()=>{document.body.style.overflow='';};},[]);
  const border = taskBorderColor(task);
  return ReactDOM.createPortal(
    <>
      <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.72)',backdropFilter:'blur(3px)',zIndex:9999}}/>
      <div style={{
        position:'fixed',top:0,right:0,height:'100%',width:480,maxWidth:'100vw',
        background:'var(--bg-elevated)',borderLeft:'1px solid var(--border-subtle)',
        zIndex:10000,overflow:'auto',display:'flex',flexDirection:'column',
        boxShadow:'-8px 0 32px rgba(0,0,0,0.5)',
      }}>
        <div style={{borderTop:`4px solid ${border}`,padding:'20px 24px',borderBottom:'1px solid var(--border-subtle)',display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
          <div>
            <div style={{fontSize:'1.125rem',fontWeight:700,marginBottom:6}}>{task.title}</div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {task.priority && <Badge label={task.priority} color={PRIORITY_COLORS[task.priority]||'#6b7280'}/>}
              <Badge label={task.status} color={STATUS_COLORS[task.status]||'#6b7280'}/>
              {task.source==='reqqer' && <Badge label="Reqqer" color="#8b5cf6"/>}
            </div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-secondary)',padding:4}}><X size={18}/></button>
        </div>
        <div style={{padding:'20px 24px',flex:1,display:'flex',flexDirection:'column',gap:16}}>
          {[
            {label:'Req ID',    val: task.req_id},
            {label:'Location',  val: task.location || (task.wing && task.area ? `${task.wing} - ${task.area}` : task.wing||task.area||null)},
            {label:'Task Type', val: task.task_type},
            {label:'Due Date',  val: task.due_date ? format(parseISO(task.due_date),'EEE MM/dd/yyyy') : null},
            {label:'Description', val: task.description},
            {label:'Notes',     val: task.notes},
            {label:'Created',   val: format(parseISO(task.created_at),'MM/dd/yyyy HH:mm')},
          ].filter(r=>r.val).map(r=>(
            <div key={r.label}>
              <div style={{fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.08em',color:'var(--text-muted)',marginBottom:4}}>{r.label}</div>
              <div style={{fontSize:'0.9375rem',color:'var(--text-primary)'}}>{r.val}</div>
            </div>
          ))}
        </div>
        <div style={{padding:'16px 24px',borderTop:'1px solid var(--border-subtle)',display:'flex',gap:8}}>
          <button className="btn btn-ghost combo1" onClick={onEdit} onMouseLeave={spinIconOut}><Pencil size={14} className="btn-icon"/> Edit</button>
          <button className="btn btn-ghost combo1" onClick={onArchive} onMouseLeave={spinIconOut}><Archive size={14} className="btn-icon"/> Archive</button>
          <button className="btn btn-ghost combo3-danger" onClick={onDelete} onMouseLeave={spinIconOut} style={{marginLeft:'auto'}}><Trash2 size={14} className="btn-icon"/> Delete</button>
        </div>
      </div>
    </>,
    document.body
  );
}

// ─── Area/Location Multi-Select (Add Task modal's "Location (full)" field) ────
// Same chip-box-opens-portal-dropdown interaction convention as EquipmentLinkPicker
// (components/EquipmentLinkPicker.tsx) and Vendors.tsx's ServiceTagSelector — a searchable,
// drill-down multi-select rather than a new UI paradigm. Two levels here (area -> location)
// instead of those components' three (type -> brand -> model) or category tree, reusing the
// existing dropdown_type=area / dropdown_type=location&parent_id=<area> cascade already used
// elsewhere in this file (Work Order form ~line 6815, Scheduled Work Order form ~line 7033).
//
// Selection is stored/emitted as a single comma-joined "Area - Location" string (the `location`
// field's existing shape — free text today, unchanged on the wire) rather than an id array,
// since `location` sits alongside the separate structured `wing`/`area` fields as the "full"
// human-readable description (confirmed by TaskDetailPanel's display fallback: `task.location ||
// wing+area`, i.e. `location` is the primary value and wing/area are legacy/secondary) — this
// field is not replacing wing/area, so its saved shape must stay a plain string they can't parse.
interface AreaOption { id: number; label: string; value: string }
function AreaLocationMultiSelect({ value, onChange, facilityId }: {
  value: string;
  onChange: (v: string) => void;
  facilityId: number | null;
}) {
  const [open, setOpen] = useState(false);
  const [drillAreaId, setDrillAreaId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top?: number; bottom?: number; left: number; width: number }>({ left: 0, width: 0 });

  // Selected entries as "Area - Location" strings — parsed from the incoming comma-joined value
  // whenever it changes externally (e.g. opening the modal in edit mode), not re-derived every
  // render, so typing/toggling here doesn't fight with the parse.
  const [selected, setSelected] = useState<string[]>(() => value ? value.split(',').map(s => s.trim()).filter(Boolean) : []);
  useEffect(() => {
    setSelected(value ? value.split(',').map(s => s.trim()).filter(Boolean) : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const { data: areas = [] } = useQuery({
    queryKey: ['dropdown-areas-multiselect', facilityId],
    queryFn: async () => {
      const res = await api.get(`/dropdowns?dropdown_type=area&facility_id=${facilityId}`);
      return (res as any).options as AreaOption[];
    },
    enabled: !!facilityId,
  });
  const { data: drillLocations = [] } = useQuery({
    queryKey: ['dropdown-locations-multiselect', facilityId, drillAreaId],
    queryFn: async () => {
      const res = await api.get(`/dropdowns?dropdown_type=location&facility_id=${facilityId}&parent_id=${drillAreaId}`);
      return (res as any).options as AreaOption[];
    },
    enabled: !!facilityId && drillAreaId != null,
  });

  function commit(next: string[]) {
    setSelected(next);
    onChange(next.join(', '));
  }
  function toggleLocation(areaLabel: string, locLabel: string) {
    const entry = `${areaLabel} - ${locLabel}`;
    commit(selected.includes(entry) ? selected.filter((s) => s !== entry) : [...selected, entry]);
  }
  function removeEntry(entry: string) {
    commit(selected.filter((s) => s !== entry));
  }

  function close() { setOpen(false); setDrillAreaId(null); setSearchQuery(''); }

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target as Node))
      ) close();
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function openDropdown() {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const maxDropdownH = 322;
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < maxDropdownH) {
        setDropdownPos({ bottom: window.innerHeight - rect.top + 2, left: rect.left, width: rect.width });
      } else {
        setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
      }
    }
    setOpen((prev) => !prev);
  }

  const filteredAreas = searchQuery
    ? (areas as AreaOption[]).filter((a) => a.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : (areas as AreaOption[]);
  const activeArea = drillAreaId != null ? (areas as AreaOption[]).find((a) => a.id === drillAreaId) : null;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div
        onClick={openDropdown}
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center',
          padding: '6px 10px', border: '1px solid var(--border-default)',
          borderRadius: 6, background: 'var(--bg-elevated)', cursor: 'pointer',
          minHeight: 38, boxSizing: 'border-box',
        }}
      >
        {selected.length === 0 && <span style={{ fontSize: 13, color: 'var(--text-muted)', flex: 1 }}>Select location(s)…</span>}
        {selected.map((entry) => (
          <span key={entry} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px 2px 8px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 500, background: 'rgba(59,130,246,0.16)', color: '#93c5fd', border: '1px solid rgba(59,130,246,0.3)' }}>
            {entry}
            <button type="button" onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); removeEntry(entry); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#93c5fd', lineHeight: 1, fontSize: '1rem' }}>×</button>
          </span>
        ))}
        <ChevronDown size={14} style={{ opacity: 0.5, marginLeft: 'auto', flexShrink: 0 }} />
      </div>
      {open && ReactDOM.createPortal(
        <div ref={dropdownRef} style={{
          position: 'fixed',
          ...(dropdownPos.top !== undefined ? { top: dropdownPos.top } : { bottom: dropdownPos.bottom }),
          left: dropdownPos.left, minWidth: Math.max(dropdownPos.width || 0, 380),
          zIndex: 10001, background: 'var(--bg-elevated)',
          border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8,
          color: 'var(--text-primary)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          display: 'flex', flexDirection: 'column', maxHeight: 320,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '0.5px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
            <Search size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <input
              autoFocus
              placeholder="Search areas…"
              value={searchQuery}
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ fontSize: 12, background: 'transparent', border: 'none', outline: 'none', flex: 1, color: 'var(--text-primary)' }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', flex: 1, overflow: 'hidden', minHeight: 0 }}>
            <div style={{ borderRight: '0.5px solid rgba(255,255,255,0.08)', overflowY: 'auto', maxHeight: 260 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '5px 10px', borderBottom: '0.5px solid rgba(255,255,255,0.08)', position: 'sticky', top: 0, background: 'var(--bg-elevated)' }}>
                Area
              </div>
              {filteredAreas.map((a) => {
                const isActive = drillAreaId === a.id;
                return (
                  <div
                    key={a.id}
                    onMouseDown={(e) => { e.stopPropagation(); setDrillAreaId(isActive ? null : a.id); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 12px', cursor: 'pointer', background: isActive ? 'var(--bg-hover)' : 'transparent' }}
                    onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                    onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.label}</span>
                    <span style={{ color: 'var(--text-muted)', display: 'flex' }}><ChevronDown size={12} style={{ transform: isActive ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }} /></span>
                  </div>
                );
              })}
              {filteredAreas.length === 0 && (
                <div style={{ padding: '12px 10px', fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>No areas found</div>
              )}
            </div>
            <div style={{ overflowY: 'auto', maxHeight: 260 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '5px 10px', borderBottom: '0.5px solid rgba(255,255,255,0.08)', position: 'sticky', top: 0, background: 'var(--bg-elevated)' }}>
                Location
              </div>
              {!activeArea && (
                <div style={{ padding: '20px 10px', fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
                  Select an area to see locations
                </div>
              )}
              {activeArea && drillLocations.length === 0 && (
                <div style={{ padding: '20px 10px', fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
                  No locations under {activeArea.label}
                </div>
              )}
              {activeArea && drillLocations.map((l) => {
                const entry = `${activeArea.label} - ${l.label}`;
                const checked = selected.includes(entry);
                return (
                  <div
                    key={l.id}
                    onMouseDown={(e) => { e.stopPropagation(); toggleLocation(activeArea.label, l.label); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', cursor: 'pointer' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <span style={{ width: 13, height: 13, borderRadius: 3, border: '2px solid #3b82f6', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: checked ? '#3b82f6' : 'transparent', flexShrink: 0 }}>
                      {checked && <span style={{ color: '#fff', fontSize: 8, lineHeight: 1 }}>✓</span>}
                    </span>
                    <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{l.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Task Add/Edit Modal ──────────────────────────────────────────────────────
function TaskModal({task,onClose,onSaved}:{task?:Task; onClose:()=>void; onSaved:()=>void}) {
  const isEdit = !!task;
  const { facilityId } = useFacility();
  const [form, setForm] = useState({
    title: task?.title||'', description: task?.description||'',
    due_date: task?.due_date ? task.due_date.slice(0,10) : '',
    priority: (task?.priority && ['high','medium','low'].includes(task.priority)) ? task.priority : 'medium',
    status: (task && ['pending','in_progress','completed','cancelled'].includes(task.status)) ? task.status : 'pending',
    task_type: task?.task_type||'', req_id: task?.req_id||'',
    location: task?.location||'', wing: task?.wing||'', area: task?.area||'',
    notes: task?.notes||'',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const mouseDownTarget = useRef<EventTarget | null>(null);

  useEffect(()=>{document.body.style.overflow='hidden';return()=>{document.body.style.overflow='';};},[]);

  async function handleSubmit(e:React.FormEvent) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (isEdit) await api.put(`/tasks/${task!.id}`, form);
      else await api.post('/tasks', form);
      onSaved();
    } catch(e:any) { setError(e?.response?.data?.error||'Save failed'); }
    finally { setSaving(false); }
  }
  const F = (k:keyof typeof form, label:string, type='text', opts?:string[]) => (
    <div>
      <label className="form-label">{label}</label>
      {opts ? (
        <select className="form-input" value={form[k]} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))}>
          {opts.map(o=><option key={o} value={o}>{o}</option>)}
        </select>
      ) : type==='textarea' ? (
        <textarea className="form-input" rows={3} value={form[k]} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))}/>
      ) : (
        <input type={type} className="form-input" value={form[k]} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))}/>
      )}
    </div>
  );
  return ReactDOM.createPortal(
    <div className="modal-overlay"
      onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
      onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) onClose(); mouseDownTarget.current = null; }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{width:'100%',maxWidth:560,maxHeight:'90vh',overflow:'hidden',display:'flex',flexDirection:'column',padding:0}}>
        {/* Fixed Header */}
        <div style={{flexShrink:0,padding:'20px 24px',borderBottom:'1px solid var(--border-subtle)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <h3 style={{margin:0,display:'flex',alignItems:'center',gap:8}}>
            <CheckSquare size={18} className="modal-icon-intro" style={{color:'#3b82f6'}}/>
            {isEdit?'Edit Task':'Add Task'}
          </h3>
          <button className="modal-close" onClick={onClose}><X size={18} className="btn-icon"/></button>
        </div>
        <form onSubmit={handleSubmit} style={{display:'flex',flexDirection:'column',flex:1,overflow:'hidden',minHeight:0}}>
          {/* Scrollable body */}
          <div style={{flex:1,overflowY:'auto',padding:'20px 24px',display:'flex',flexDirection:'column',gap:14}}>
            {F('title','Title *')}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              {F('status','Status','text',['pending','in_progress','completed','cancelled'])}
              {F('priority','Priority','text',['high','medium','low'])}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              {F('due_date','Due Date','date')}
              {F('task_type','Task Type')}
            </div>
            {F('req_id','Req ID')}
            <div>
              <label className="form-label">Location (full)</label>
              {/* Wing/Area text inputs removed from this form (item 5) -- the picker above
                  covers that ground now. form.wing/form.area are NOT removed from state or from
                  the save payload below: both fields are still read server-side (GET /tasks
                  filter param, CSV export, Reqqer sync's move-to-director copy) and dropping them
                  from the submitted payload would silently null out an existing task's wing/area
                  on next save. This is a UI-only removal, per CLAUDE_RULES -- never subtract an
                  existing INSERT/UPDATE field from partial context. */}
              <AreaLocationMultiSelect
                value={form.location}
                onChange={(v) => setForm((p) => ({ ...p, location: v }))}
                facilityId={facilityId}
              />
            </div>
            {F('description','Description','textarea')}
            {F('notes','Notes','textarea')}
            {error && <div style={{color:'var(--accent-danger)',fontSize:'0.875rem'}}>{error}</div>}
          </div>
          {/* Fixed Footer */}
          <div style={{flexShrink:0,padding:'16px 24px 20px',borderTop:'1px solid var(--border-subtle)',display:'flex',gap:8,justifyContent:'flex-end'}}>
            <button type="button" className="btn btn-ghost combo-cancel-jello" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary combo2" disabled={saving||!form.title.trim()}>
              {saving?'Saving…':'Save Task'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// ─── QAPI From Task Modal ─────────────────────────────────────────────────────
function AddQapiFromTaskModal({ task, onClose, onSaved }: { task: Task; onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState(task.title ?? '');
  const [category, setCategory] = useState('Quality Improvement');
  const [description, setDescription] = useState(task.notes ?? task.description ?? '');
  const [entryDate, setEntryDate] = useState(new Date().toISOString().slice(0, 10));
  const [outcome, setOutcome] = useState('Completed');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mouseDownTarget = useRef<EventTarget | null>(null);

  useEffect(() => { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = ''; }; }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    try {
      await api.post('/reports/qapi', { title, category, description, entry_date: entryDate, outcome });
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return ReactDOM.createPortal(
    <div className="modal-overlay"
      onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
      onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) onClose(); mouseDownTarget.current = null; }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 480, maxWidth: '90vw' }}>
        <h3 style={{ margin: '0 0 20px', fontSize: '1.125rem', color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileText size={18} className="modal-icon-intro" style={{ color: '#3b82f6' }}/>
          Add to QAPI
        </h3>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: '0.8125rem', color: '#94a3b8', display: 'block', marginBottom: 6 }}>Title</label>
            <input className="form-input" value={title} onChange={e => setTitle(e.target.value)} style={{ width: '100%' }} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: '0.8125rem', color: '#94a3b8', display: 'block', marginBottom: 6 }}>Category</label>
            <select className="form-input" value={category} onChange={e => setCategory(e.target.value)} style={{ width: '100%' }}>
              <option>Quality Improvement</option>
              <option>Compliance</option>
              <option>Safety</option>
              <option>Preventive</option>
              <option>Regulatory</option>
              <option>Maintenance</option>
              <option>Other</option>
            </select>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: '0.8125rem', color: '#94a3b8', display: 'block', marginBottom: 6 }}>Description</label>
            <textarea className="form-input" value={description} onChange={e => setDescription(e.target.value)} rows={3} style={{ width: '100%', resize: 'vertical' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: '0.8125rem', color: '#94a3b8', display: 'block', marginBottom: 6 }}>Completed Date</label>
              <DatePicker value={entryDate} onChange={v => setEntryDate(v)} placeholder="Select a date" />
            </div>
            <div>
              <label style={{ fontSize: '0.8125rem', color: '#94a3b8', display: 'block', marginBottom: 6 }}>Outcome</label>
              <input className="form-input" value={outcome} onChange={e => setOutcome(e.target.value)} style={{ width: '100%' }} />
            </div>
          </div>
          {error && <div style={{ color: '#ef4444', fontSize: '0.875rem', marginBottom: 12 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost combo-cancel-jello" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary combo2" disabled={saving}>
              {saving ? 'Saving...' : 'Add to QAPI'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// ─── Tab 2: Archived Tasks ────────────────────────────────────────────────────
function ArchivedTasksTab() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [deleteModal, setDeleteModal] = useState<{open:boolean; onConfirm:()=>void}>({open:false,onConfirm:()=>{}});
  const [restoreModal, setRestoreModal] = useState<{open:boolean; task?:Task}>({open:false});
  // Doesn't share ActiveTasksTab's row component (checked — each tab has its own separate row
  // markup) but has the same structural bug: title in a flex:1 left block, icons in a
  // flexShrink:0 right block with nothing to shrink/wrap it. Only 2 icons here (vs Reminders'
  // 5) so it's less severe, but the same fix applies.
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const {data:res, isLoading} = useQuery({
    queryKey:['tasks-archived',search],
    queryFn:()=>api.get<Task[]>(`/tasks/archived?search=${encodeURIComponent(search)}`),
  });
  const tasks: Task[] = res?.data ?? [];

  const restoreMut = useMutation({
    mutationFn:(id:number)=>api.post(`/tasks/${id}/restore`,{}),
    onSuccess:()=>qc.invalidateQueries({queryKey:['tasks-archived']}),
  });
  const deleteMut = useMutation({
    mutationFn:(id:number)=>api.delete(`/tasks/${id}`),
    onSuccess:()=>qc.invalidateQueries({queryKey:['tasks-archived']}),
  });

  return (
    <div>
      <div style={{display:'flex',gap:10,marginBottom:16,alignItems:'center'}}>
        <div className="search-bar search-input-wrapper" style={{flex:'1 1 220px',maxWidth:340}}>
          <Search size={14} className="btn-icon" color="var(--text-muted)"/>
          <input placeholder="Search archived…" value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <span style={{marginLeft:'auto',fontSize:'0.875rem',color:'var(--text-muted)'}}>{tasks.length} archived</span>
      </div>
      {isLoading && <div style={{color:'var(--text-muted)',textAlign:'center',padding:40}}>Loading…</div>}
      {!isLoading && tasks.length===0 && (
        <div style={{textAlign:'center',padding:'60px 0',color:'var(--text-muted)'}}>
          <Archive size={40} style={{opacity:0.3,marginBottom:12}}/><br/>No archived tasks
        </div>
      )}
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {tasks.map(t=>(
          <div key={t.id} style={{
            display:'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center',
            background:'rgba(255,255,255,0.045)', backdropFilter:'blur(16px)', WebkitBackdropFilter:'blur(16px)',
            border:'1px solid var(--border-subtle)',
            borderRadius:'var(--radius-md)',borderLeft:'4px solid var(--text-muted)',padding:'14px 16px',gap:12,
          }}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontWeight:600,marginBottom:4}}>{t.title}</div>
              <div style={{display:'flex',gap:10,fontSize:'0.8125rem',color:'var(--text-muted)',flexWrap:'wrap'}}>
                {t.req_id && <span>#{t.req_id}</span>}
                {t.location && <span><MapPin size={11}/> {t.location}</span>}
                {t.archived_at && <span>Archived: {format(parseISO(t.archived_at),'MM/dd/yyyy')}</span>}
              </div>
            </div>
            <div style={{display:'flex',gap:4,flexShrink:0,flexWrap: isMobile ? 'wrap' : 'nowrap'}}>
              <button onClick={()=>setRestoreModal({open:true,task:t})} title="Restore" onMouseLeave={spinIconOut}
                className="combo-jello-spin" data-tint="blue"
                style={{border:`1px solid ${TINT_BORDER.blue}`,borderRadius:'var(--radius-sm)',padding:'5px 6px',cursor:'pointer',color:TINT_COLOR.blue,display:'flex',alignItems:'center'}}>
                <RotateCcw size={14} className="btn-icon"/>
              </button>
              <button onClick={()=>setDeleteModal({open:true,onConfirm:()=>deleteMut.mutate(t.id)})} title="Delete" onMouseLeave={spinIconOut}
                className="combo3-danger"
                style={{borderRadius:'var(--radius-sm)',padding:'5px 6px',cursor:'pointer',display:'flex',alignItems:'center'}}>
                <Trash2 size={14} className="btn-icon"/>
              </button>
            </div>
          </div>
        ))}
      </div>
      {deleteModal.open && <DeleteConfirmModal
        onConfirm={()=>{deleteModal.onConfirm();setDeleteModal({open:false,onConfirm:()=>{}}); }}
        onClose={()=>setDeleteModal({open:false,onConfirm:()=>{}})}/>}
      {restoreModal.open && restoreModal.task && <RestoreConfirmModal
        onConfirm={()=>{restoreMut.mutate(restoreModal.task!.id);setRestoreModal({open:false});}}
        onClose={()=>setRestoreModal({open:false})}/>}
    </div>
  );
}

// ─── Tab 3: Schedule (Compliance Tracker) ────────────────────────────────────

interface ScTask {
  id: number; name: string; vendor_name: string|null; vendor_2: string|null; vendor_id: number|null;
  icon: string; color: string; frequency: string; interval_months: number;
  notes: string|null; label: string|null; last_completed_date: string|null;
  first_due_date: string;
  sort_order: number; report_url: string|null; report_count: number;
  completions: number[]; // 1-indexed month numbers for the current year
  reports: { id: number; report_url: string; report_name: string|null; label: string|null; uploaded_at: string; analysis_json?: any; service_date?: string|null }[];
  created_at?: string; // already returned by GET /scheduled-tasks/list, just wasn't typed until the Task Detail page's Timeline card needed it
}

// Full vendor record — GET /vendors already returns every one of these columns (SELECT *),
// but ScheduleTab's own `vendors` variable narrows to {id,name} for the edit modal's <select>.
// The Task Detail page's Vendor card needs the real contact/contract fields, so this reads the
// same already-fetched vendorsRes with its actual shape instead of firing a second request.
// Extended for the Vendor Detail takeover (item 4) — every field below is already returned by
// the same GET /vendors (SELECT *) response the Vendor card above was already reading a subset
// of; adding fields here is just typing what's already in the payload, not a new fetch.
interface VendorFull {
  id: number; name: string;
  contact_name: string | null; contact_phone: string | null; contact_email: string | null;
  vendor_phone: string | null; vendor_email: string | null;
  contract_expiry_date: string | null;
  address: string | null; website: string | null; account_number: string | null;
  hours_of_operation: string | null; is_24_hour_service: boolean | null;
  notes: string | null; service_categories: string[] | null;
  contract_start_date: string | null; contract_notes: string | null;
  avg_rating: number | null; rating_count: number | null;
  logo_url: string | null;
  // Item 3 — the rest of the row GET /vendors (SELECT *) already returns, previously left
  // untyped/unused by the simplified takeover. primary_contact_name/phone are the query's own
  // computed columns (pulled from vendor_contacts, not the vendors table itself).
  is_main_vendor: boolean | null; is_preferred: boolean | null;
  risk_flags?: string[];
  primary_contact_name?: string | null; primary_contact_phone?: string | null;
}

const FREQ_COLORS: Record<string,{bg:string;color:string}> = {
  'Weekly':         { bg:'rgba(59,130,246,0.18)',  color:'#3b82f6' },
  'Monthly':        { bg:'rgba(59,130,246,0.18)',  color:'#3b82f6' },
  'Every 2 months': { bg:'rgba(20,184,166,0.18)',  color:'#14b8a6' },
  'Quarterly':      { bg:'rgba(245,158,11,0.18)',  color:'#f59e0b' },
  'Semi-Annual':    { bg:'rgba(139,92,246,0.18)',  color:'#8b5cf6' },
  'Yearly':         { bg:'rgba(16,185,129,0.18)',  color:'#10b981' },
  'Every 2 years':  { bg:'rgba(249,115,22,0.18)',  color:'#f97316' },
  'Every 3 years':  { bg:'rgba(249,115,22,0.18)',  color:'#f97316' },
  'Every 5 years':  { bg:'rgba(249,115,22,0.18)',  color:'#f97316' },
};
const TASK_EMOJIS = ['🔧','🧹','🔥','💧','❄️','⚡','🌿','🏥','🔒','📋','🧪','🚿','🛁','🌡️','🔑','🏗️','🧯','🚨','♻️','📦'];
const TASK_COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#f97316'];
const MO_LABELS = ['J','F','M','A','M','J','J','A','S','O','N','D'];
const MONTH_FULL_LABELS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
// Always derive current month/year from UTC to stay consistent across all browser timezones
const _schedNow = new Date();
const CURR_MONTH = _schedNow.getUTCMonth() + 1; // 1-indexed UTC
const CURR_YEAR  = _schedNow.getUTCFullYear();

// ─── Frontend schedule math (mirrors server/src/utils/scheduleUtils.ts) ────────

/** Returns 1-indexed months due in the given year for the given task frequency. */
function getOccurrenceMonths(firstDueDate: Date, frequency: string, year: number): number[] {
  const anchorMonth = firstDueDate.getUTCMonth() + 1; // 1-indexed
  const anchorYear = firstDueDate.getUTCFullYear();
  const freq = frequency.toLowerCase().trim();

  const multiYear: Record<string, number> = { 'every 2 years': 2, 'every 3 years': 3, 'every 4 years': 4, 'every 5 years': 5 };
  if (multiYear[freq] !== undefined) {
    const interval = multiYear[freq];
    const diff = year - anchorYear;
    if (diff < 0 || diff % interval !== 0) return [];
    return [anchorMonth];
  }
  if (freq === 'weekly' || freq === 'monthly') return [1,2,3,4,5,6,7,8,9,10,11,12];
  if (freq === 'every 2 months') {
    const months: number[] = [];
    for (let m = anchorMonth; m <= 12; m += 2) months.push(m);
    for (let m = anchorMonth - 2; m >= 1; m -= 2) months.push(m);
    return months.sort((a, b) => a - b);
  }
  if (freq === 'quarterly') {
    const months: number[] = [];
    for (let i = 0; i < 4; i++) months.push(((anchorMonth - 1 + i * 3) % 12) + 1);
    return months.sort((a, b) => a - b);
  }
  if (freq === 'semi-annual' || freq === 'biannually' || freq === 'bi-annual') {
    const m2 = ((anchorMonth - 1 + 6) % 12) + 1;
    return [anchorMonth, m2].sort((a, b) => a - b);
  }
  if (freq === 'yearly' || freq === 'annual') return [anchorMonth];

  // Custom "Every N months" — treat like bimonthly/quarterly but with N-month interval
  const nMonthsMatch = freq.match(/^every (\d+) months?$/);
  if (nMonthsMatch) {
    const n = parseInt(nMonthsMatch[1], 10);
    const months: number[] = [];
    for (let m = anchorMonth; m <= 12; m += n) months.push(m);
    for (let m = anchorMonth - n; m >= 1; m -= n) months.push(m);
    return months.sort((a, b) => a - b);
  }

  // Custom "Every N years" — treat like multi-year interval
  const nYearsMatch = freq.match(/^every (\d+) years?$/);
  if (nYearsMatch) {
    const n = parseInt(nYearsMatch[1], 10);
    const diff = year - anchorYear;
    if (diff < 0 || diff % n !== 0) return [];
    return [anchorMonth];
  }

  return [anchorMonth];
}

/** Returns next 1-indexed due month >= currentMonth that is not completed. */
function getNextDueMonth(occurrenceMonths: number[], completedMonths: Set<number>, currentMonth: number): number | null {
  const remaining = occurrenceMonths.filter(m => m >= currentMonth && !completedMonths.has(m));
  return remaining.length > 0 ? remaining[0] : null;
}

/** For multi-year frequencies, returns the next due year >= currentYear. */
function getNextDueYear(firstDueDate: Date, frequency: string, currentYear: number): number | null {
  const freq = frequency.toLowerCase().trim();
  const multiYear: Record<string, number> = { 'every 2 years': 2, 'every 3 years': 3, 'every 4 years': 4, 'every 5 years': 5 };
  const interval = multiYear[freq];
  if (!interval) return null;
  const anchorYear = firstDueDate.getUTCFullYear();
  let y = anchorYear;
  while (y < currentYear) y += interval;
  return y;
}

type CellState = 'empty' | 'completed' | 'missed' | 'due' | 'scheduled';

function getCellStateLocal(
  month: number, // 1-indexed
  occurrenceMonths: number[],
  completedMonths: Set<number>,
  currentMonth: number,
  nextDueMonth: number | null
): CellState {
  if (!occurrenceMonths.includes(month)) return 'empty';
  if (completedMonths.has(month)) return 'completed';
  if (month < currentMonth) return 'missed';
  if (month === nextDueMonth) return 'due';
  return 'scheduled';
}

function formatTaskDate(d: string | null | undefined): string {
  if (!d) return '—';
  const parts = d.split('T')[0].split('-').map(Number);
  const date = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function computeNextDueDisplay(task: ScTask): { label: string; color: string } {
  if (!task.first_due_date) return { label: '—', color: '#94a3b8' };
  const firstDueDate = new Date(task.first_due_date);
  const anchorYear = firstDueDate.getUTCFullYear();

  // If first_due_date is in the future, find the first occurrence year >= anchorYear
  const startYear = anchorYear > CURR_YEAR ? anchorYear : CURR_YEAR;
  const completedSet = new Set<number>(task.completions);

  // Search current year (or anchor year if in future) through next 5 years
  for (let yr = startYear; yr <= startYear + 5; yr++) {
    const occMonths = getOccurrenceMonths(firstDueDate, task.frequency, yr);
    if (occMonths.length === 0) continue;
    // For current year use CURR_MONTH as start, future years start from month 1
    // For past years (shouldn't happen with startYear logic) also use month 1
    const fromMonth = yr === CURR_YEAR ? CURR_MONTH : 1;
    const nextDue = getNextDueMonth(occMonths, yr === CURR_YEAR ? completedSet : new Set(), fromMonth);
    if (nextDue !== null) {
      const d = new Date(Date.UTC(yr, nextDue - 1, 15));
      const label = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
      const color = yr === CURR_YEAR && nextDue === CURR_MONTH ? '#ef4444' : '#3b82f6';
      return { label, color };
    }
  }

  return { label: 'Not scheduled', color: '#94a3b8' };
}

// position:sticky/top:0 matches the app's existing sticky-th pattern (Settings.tsx's
// stickyTh — applied per-<th>, not on <thead>/<tr>, for reliable cross-browser table sticky
// behavior). top:0 (not the topbar's 60px height) because the actual scrolling ancestor is
// .page-content (global.css), which already accounts for the fixed topbar via its own
// margin-top — sticky resolves relative to that ancestor's padding box, not the viewport.
const schThStyle: React.CSSProperties = {
  padding:'8px 4px', fontWeight:600, fontSize:'0.7em',
  textTransform:'uppercase', letterSpacing:'0.07em',
  color:'var(--text-secondary)', textAlign:'center', whiteSpace:'nowrap',
  borderBottom:'1px solid var(--border-subtle)', background:'var(--bg-base)',
  // zIndex:2 (was 1) so the sticky-top header row always wins over the sticky-left body cells
  // below (zIndex:1, see schStickyColStyle) at the moment a row's frozen drag-handle/Task
  // column scrolls vertically underneath the header — otherwise the passing row would paint
  // over the header instead of the header staying on top.
  position:'sticky', top:0, zIndex:2,
};

// Sticky-left ("frozen") column positioning for the drag-handle + Task name cells — the two
// columns that identify which row you're looking at. Real iPhone screenshot showed the Smart
// View grid's existing overflowX:'auto' (this file, ~line 2195) does let you scroll to the
// Next due/YTD/month columns, but nothing kept the Task name visible while doing so, so you'd
// lose track of which row's data you were looking at. `left` is the running sum of the
// preceding sticky columns' widths (drag-handle col1 is 32px, so the Task name cell's left
// offset is 32) so the two frozen cells sit flush against each other with no gap, matching the
// widths already declared in the <colgroup> (col1 32px, col2 260px).
// Deliberately doesn't set `background` here — a sticky cell needs SOME opaque-enough
// background to hide content scrolling underneath it, but header cells (schThStyle's own solid
// var(--bg-base), matching the rest of that already-sticky-top header row) and body cells
// (this glass-themed page's rgba(255,255,255,0.045)+blur(16px) card surface, matching
// DetailCard/KPI cards elsewhere on this page) need different values — set per call site below
// instead of baked in here, so each stays visually consistent with its own row.
const schStickyColStyle = (left: number): React.CSSProperties => ({
  position:'sticky', left, zIndex:1,
});
// NOTE: no inline `background` here (was 'none') — an inline background always beats an
// external :hover rule regardless of specificity (same bug fixed on Orders.tsx's Mail icon),
// which would silently block combo-jello-spin's data-tint hover background on Edit/Expand below.
const schActionBtn: React.CSSProperties = {
  border:'1px solid var(--border-subtle)',
  borderRadius:4, padding:'4px 5px', cursor:'pointer',
  color:'#94a3b8', display:'flex', alignItems:'center',
};

interface SortableTaskRowProps {
  task: ScTask;
  year: number;
  nowMonth: number; // effective "current month" anchor for this row's year (see effectiveCurrentMonth)
  isExpanded: boolean;
  selectedMonth: number | null;
  onCellClick: (task: ScTask, month: number) => void; // month is 1-indexed
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isHighlighted?: boolean;
  // Passed down from ScheduleTab (one shared resize listener there) rather than each row
  // running its own — this is a mapped list, N rows would mean N redundant listeners.
  isMobile: boolean;
}

function SortableTaskRow({ task, year, nowMonth, isExpanded, selectedMonth, onCellClick, onToggleExpand, onEdit, onDelete, isHighlighted, isMobile }: SortableTaskRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const freqStyle = FREQ_COLORS[task.frequency] ?? { bg:'rgba(107,114,128,0.15)', color:'#6b7280' };
  const rowStyle: React.CSSProperties = {
    cursor: 'pointer',
    background: isExpanded ? 'rgba(59,130,246,0.08)' : 'transparent',
    borderBottom: '1px solid var(--border-subtle)',
    boxShadow: isExpanded ? 'inset 2px 0 0 #3b82f6' : 'none',
    transition: transition ?? 'background 120ms',
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : 1,
    position: isDragging ? 'relative' : undefined,
    zIndex: isDragging ? 999 : undefined,
  };

  // FIX (round 2, real iPhone screenshot): the previous rgba(255,255,255,0.045)+blur(16px)
  // "glass" background was only 4.5% opaque — backdrop-filter blurs whatever's behind an
  // element, it doesn't hide it, so at that opacity the Next due/YTD/month cells scrolling
  // underneath the sticky column remained almost fully visible through it (e.g. "Fire
  // Extinguishers" visibly overlapping a progress ring from a scrolled-under cell). Switched to
  // var(--bg-elevated) — a genuinely solid, theme-aware hex (not glass) — so scrolled content is
  // actually hidden, not just softened. Z-index was NOT the cause and needed no change: a
  // position:sticky element (schStickyColStyle below) always paints above position:static
  // siblings regardless of z-index per the CSS stacking spec, so the sticky cells were already
  // painting on top of the scrolling ones — they just had nothing opaque to paint WITH. Keeps
  // layering the isExpanded tint on top via the same gradient technique used for the "NOW" month
  // header tint elsewhere in this file, now over the solid base instead of the transparent one.
  const stickyColBg: React.CSSProperties = {
    background: isExpanded
      ? 'linear-gradient(rgba(59,130,246,0.08), rgba(59,130,246,0.08)), var(--bg-elevated)'
      : 'var(--bg-elevated)',
  };

  const firstDueDate = task.first_due_date ? new Date(task.first_due_date) : null;
  const occMonths = firstDueDate ? getOccurrenceMonths(firstDueDate, task.frequency, year) : [];
  const completedSet = new Set<number>(task.completions);
  const nextDue = getNextDueMonth(occMonths, completedSet, nowMonth);
  const nextDueYr = occMonths.length === 0 && firstDueDate
    ? getNextDueYear(firstDueDate, task.frequency, year)
    : null;

  // "Next due" pill + YTD ring — mockups/compliance-pm-redesign.html's Option A has these as
  // two separate columns (Next due, YTD) alongside the existing Occurrence column; same
  // ok/soon/warn/late variant logic as ComplianceCardsView's per-card computation, and reuses
  // its CARDS_NEXTDUE_STYLE + the shared ProgressRing component rather than duplicating either.
  const monthStates: CellState[] = Array.from({ length:12 }, (_, i) => getCellStateLocal(i + 1, occMonths, completedSet, nowMonth, nextDue));
  const completedCount = occMonths.filter(m => completedSet.has(m)).length;
  const totalCount = occMonths.length;
  const hasMissed = monthStates.includes('missed');
  const hasDueNow = monthStates.includes('due');
  let pillVariant: 'ok' | 'soon' | 'warn' | 'late';
  let pillLabel: string;
  let ringColor: string;
  if (hasMissed) {
    pillVariant = 'late'; pillLabel = `${monthStates.filter(s => s === 'missed').length} missed`; ringColor = '#f87171';
  } else if (hasDueNow) {
    pillVariant = 'soon'; pillLabel = 'Due now'; ringColor = '#60a5fa';
  } else {
    ringColor = '#34d399';
    const upcoming = occMonths.filter(m => m > nowMonth && !completedSet.has(m));
    if (upcoming.length > 0) {
      pillVariant = 'warn'; pillLabel = `Next ${MONTH_FULL_LABELS[upcoming[0] - 1].slice(0, 3)}`;
    } else {
      pillVariant = 'ok'; pillLabel = computeNextDueDisplay(task).label;
    }
  }
  const nextDuePillStyle = CARDS_NEXTDUE_STYLE[pillVariant];

  // Item 2: row hover — ported from mockup's `table.pm tbody tr:hover{background:rgba(255,255,255,0.03)}`,
  // but using the app's existing theme-aware row-hover token (var(--bg-hover), already used by the
  // generic <table> tbody tr:hover rule in global.css) instead of that dark-theme-only value. Uses
  // JS onMouseEnter/onMouseLeave rather than a CSS :hover class because rowStyle.background is set
  // inline above — an inline background always wins over an external :hover rule regardless of
  // specificity (the same trap fixed on Orders.tsx's Mail icon and this file's schActionBtn).
  // Skipped while expanded so the row-hover never overrides the selected/expanded highlight.
  const onRowMouseEnter = (e: React.MouseEvent<HTMLTableRowElement>) => { if (!isExpanded) e.currentTarget.style.background = 'var(--bg-hover)'; };
  const onRowMouseLeave = (e: React.MouseEvent<HTMLTableRowElement>) => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; };

  return (
    <tr id={`sched-task-${task.id}`} ref={setNodeRef} style={rowStyle} className={isHighlighted ? 'task-highlight-glow' : ''} {...attributes} onClick={onToggleExpand} onMouseEnter={onRowMouseEnter} onMouseLeave={onRowMouseLeave}>
      {/* Drag handle. Sticky positioning skipped on mobile — the frozen column was eating too
          much of a narrow viewport's width, leaving barely any room for the actual month/status
          cells; on mobile this scrolls away with the rest of the row like a normal cell. */}
      <td {...listeners} onClick={e=>e.stopPropagation()} style={{ width:32, textAlign:'center', padding:'0 4px', cursor:'grab', userSelect:'none', touchAction:'none', ...(isMobile ? {} : schStickyColStyle(0)), ...(isMobile ? {} : stickyColBg) }}>
        <div style={{ display:'flex', flexDirection:'column', gap:3, alignItems:'center', padding:'11px 0' }}>
          {[0,1,2].map(i=><div key={i} style={{ width:14, height:1.5, background:'#3b5c80', borderRadius:1 }}/>)}
        </div>
      </td>
      {/* Task name — same isMobile skip as the drag-handle cell above */}
      <td style={{ padding:'10px 8px 10px 12px', width:260, maxWidth:260, ...(isMobile ? {} : schStickyColStyle(32)), ...(isMobile ? {} : stickyColBg) }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:'1.1em', flexShrink:0 }}>{task.icon}</span>
          <div style={{ minWidth:0 }}>
            <div style={{ fontWeight:700, color:'#e2e8f0', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', display:'flex', alignItems:'center', gap:4 }}>
              {task.name}
              {(task.report_count ?? 0) > 0 && <span title="Report attached"><FileText size={11} color="#3b82f6" style={{ flexShrink:0 }}/></span>}
            </div>
            {(task.vendor_name || task.last_completed_date) && (
              <div style={{ fontSize:'0.75em', color:'#94a3b8', marginTop:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                {task.vendor_name && <span>{task.vendor_name}</span>}
                {task.last_completed_date && <span>{task.vendor_name ? ' · ' : ''}Last: {formatTaskDate(task.last_completed_date)}</span>}
              </div>
            )}
          </div>
        </div>
      </td>
      {/* Occurrence badge — shows "Next: [year]" for multi-year tasks not due this year */}
      <td style={{ textAlign:'center', padding:'10px 4px', width:100 }}>
        {nextDueYr !== null ? (
          <span style={{ fontSize:'0.7em', color:'#94a3b8', fontStyle:'italic' }}>Next: {nextDueYr}</span>
        ) : (
          <span style={{ padding:'2px 7px', borderRadius:20, fontSize:'0.7em', fontWeight:600, background:freqStyle.bg, color:freqStyle.color, whiteSpace:'nowrap' }}>
            {task.frequency}
          </span>
        )}
      </td>
      {/* Next due — mockup's own separate column, additive alongside Occurrence above (not a
          replacement for it). */}
      <td style={{ textAlign:'center', padding:'10px 4px', width:90 }}>
        <span style={{
          display:'inline-flex', alignItems:'center', gap:5, fontSize:'0.68em', fontWeight:600,
          padding:'3px 9px', borderRadius:99, whiteSpace:'nowrap', fontFamily:"'JetBrains Mono', monospace",
          background:nextDuePillStyle.bg, color:nextDuePillStyle.color,
        }}>{pillLabel}</span>
      </td>
      {/* YTD — mockup's .ring, reusing the same ProgressRing component Cards/Task Detail use */}
      <td style={{ textAlign:'center', padding:'6px 4px', width:50 }}>
        <div style={{ display:'flex', justifyContent:'center' }}>
          <ProgressRing completed={completedCount} total={totalCount} color={ringColor} />
        </div>
      </td>
      {/* 12 month cells — month is 1-indexed. The "now" column gets the mockup's
          td.now background + inset vertical highlight (matches th.now in the header). */}
      {Array.from({ length:12 }, (_, i) => i + 1).map(month => {
        const isNow = year === CURR_YEAR && month === CURR_MONTH;
        return (
          <td key={month} style={{
            padding:'3px 2px', textAlign:'center',
            background: isNow ? 'rgba(59,130,246,0.07)' : undefined,
            boxShadow: isNow ? 'inset 1px 0 0 rgba(59,130,246,0.18), inset -1px 0 0 rgba(59,130,246,0.18)' : undefined,
          }}>
            <CellStatus
              state={getCellStateLocal(month, occMonths, completedSet, nowMonth, nextDue)}
              isSelected={selectedMonth === month}
              onClick={() => onCellClick(task, month)}
            />
          </td>
        );
      })}
      {/* Actions */}
      <td onClick={e=>e.stopPropagation()} style={{ padding:'6px 8px', width:96 }}>
        <div style={{ display:'flex', gap:3, justifyContent:'flex-end', alignItems:'center' }}>
          <button onClick={onEdit} className="combo-jello-spin" data-tint="neutral" onMouseLeave={spinIconOut} style={schActionBtn} title="Edit"><Pencil size={13} className="btn-icon"/></button>
          <button onClick={onDelete} className="combo3-danger" onMouseLeave={spinIconOut} style={{ borderRadius:4, padding:'4px 5px', cursor:'pointer', display:'flex', alignItems:'center' }} title="Delete"><Trash2 size={13} className="btn-icon"/></button>
        </div>
      </td>
    </tr>
  );
}

function CellStatus({ state, isSelected, onClick }: { state: CellState; isSelected?: boolean; onClick: () => void }) {
  const selectedOutline: React.CSSProperties = isSelected
    ? { outline: '2px solid rgba(255,255,255,0.6)', outlineOffset: 1 }
    : {};

  // combo-jello-spin gives the bounce on :hover (an `animation` property, untouched by the
  // inline `background` below). data-tint's hover BACKGROUND is deliberately not used here —
  // each cell already sets its status color via inline `background: bg`, and an inline
  // background always beats an external :hover rule regardless of specificity (same bug as
  // Orders.tsx's Mail icon), so a data-tint background would be silently blocked. Using the
  // filter:brightness() hover pattern instead — already documented for KpiCard in
  // DESIGN_SYSTEM.md #16 — since `filter` isn't set inline anywhere here, no collision.
  const brightenOn = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.filter = 'brightness(1.4)'; };
  const brightenOff = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.filter = ''; };

  if (state === 'empty') {
    return (
      <div
        className="combo-jello-spin cell-hover-tip"
        data-tip="No service scheduled"
        onMouseEnter={brightenOn}
        onMouseLeave={brightenOff}
        style={{
          width: 28, height: 28, borderRadius: 6,
          border: '1.5px solid rgba(255,255,255,0.18)',
          background: 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto', cursor: 'default', ...selectedOutline,
        }}/>
    );
  }
  let inner: React.ReactNode = null;
  let bg = 'var(--bg-surface)';
  let border = '1px solid var(--border-subtle)';
  let tip = '';
  if (state === 'completed') {
    inner = <span style={{ color:'#10b981', fontWeight:800, fontSize:'0.875em', lineHeight:1 }}>✓</span>;
    bg = '#064e3b'; border = '1px solid #10b981'; tip = 'Completed';
  } else if (state === 'due') {
    inner = <div style={{ width:12, height:12, borderRadius:'50%', border:'2px solid #3b82f6', background:'transparent' }}/>;
    bg = '#1d3a6e'; border = '2px solid #3b82f6'; tip = 'Due now — click to mark done';
  } else if (state === 'scheduled') {
    inner = <div style={{ width:7, height:7, borderRadius:'50%', background:'#f59e0b' }}/>;
    bg = '#1e1a07'; border = '1px solid #f59e0b'; tip = 'Scheduled';
  } else if (state === 'missed') {
    inner = <span style={{ color:'#ef4444', fontWeight:800, fontSize:'0.875em', lineHeight:1 }}>✕</span>;
    bg = '#450a0a'; border = '1px solid #ef4444'; tip = 'Missed — click to log or excuse';
  }
  return (
    <div
      className={`combo-jello-spin cell-hover-tip${state === 'due' ? ' cell-due-pulse' : ''}`}
      onClick={e => { e.stopPropagation(); onClick(); }}
      data-tip={tip}
      onMouseEnter={brightenOn}
      onMouseLeave={brightenOff}
      style={{
        width: 26, height: 26, borderRadius: 4, background: bg, border,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', margin: '0 auto', ...selectedOutline,
      }}
    >{inner}</div>
  );
}

function ExpandedNotes({ taskId, facilityId, initialNotes }: { taskId: number; facilityId: number; initialNotes: string | null }) {
  const qc = useQueryClient();
  const [notes, setNotes] = useState(initialNotes ?? '');
  const [saveState, setSaveState] = useState<'idle'|'saving'|'saved'>('idle');
  const savedRef = useRef(initialNotes ?? '');

  async function handleBlur() {
    if (notes === savedRef.current) return;
    setSaveState('saving');
    try {
      await api.put(`/scheduled-tasks/${taskId}`, { notes: notes || null });
      savedRef.current = notes;
      setSaveState('saved');
      qc.invalidateQueries({ queryKey: ['scheduled-tasks', facilityId] });
      setTimeout(() => setSaveState('idle'), 2000);
    } catch {
      setSaveState('idle');
    }
  }

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
        <span style={{ fontSize:'0.625rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'#3b5c80' }}>Notes</span>
        {saveState === 'saving' && <span style={{ fontSize:'0.625rem', color:'#94a3b8' }}>Saving…</span>}
        {saveState === 'saved'  && <span style={{ fontSize:'0.625rem', color:'#10b981' }}>✓ Saved</span>}
      </div>
      <textarea
        className="form-input"
        rows={2}
        value={notes}
        onChange={e => setNotes(e.target.value)}
        onBlur={handleBlur}
        placeholder="Add notes…"
        style={{ fontSize:'0.8125rem', resize:'vertical' }}
      />
    </div>
  );
}

// ─── Equipment destination routing ───────────────────────────────────────────
// shortLabel is used only in the AI Report Analysis modal's inline per-row "+ Add to…" button
// (item 1 fix) -- "Facility Supplies (Other)" at full length was the one label long enough to
// push that button past the modal's right edge with no visible scrollbar (confirmed live at
// narrower viewport widths, e.g. 1152px, on the Fire Pump & Electrician report's Jockey Pump/FACP
// rows). The destination picker popup (EQUIP_DESTINATIONS.map further down) still shows the full
// `label` -- only the inline button uses the short form.
const EQUIP_DESTINATIONS = [
  { tab: 'extinguishers',     label: 'Extinguishers',             shortLabel: 'Extinguishers', sub: '',      color: '#10b981' },
  { tab: 'concentrators',     label: 'Concentrators',             shortLabel: 'Concentrators', sub: '',      color: '#8b5cf6' },
  { tab: 'hvac',              label: 'HVAC',                      shortLabel: 'HVAC',           sub: 'hvac',  color: '#6366f1' },
  { tab: 'facility-supplies', label: 'Facility Supplies (Other)', shortLabel: 'Other',          sub: 'other', color: '#94a3b8' },
];

function getEquipmentDestination(type: string): typeof EQUIP_DESTINATIONS[number] {
  const t = (type ?? '').toLowerCase();
  if (t.includes('extinguisher')) return EQUIP_DESTINATIONS[0];
  if (t.includes('concentrator') || t.includes('o2') || t.includes('oxygen')) return EQUIP_DESTINATIONS[1];
  if (t.includes('hvac') || t.includes('air handler') || t.includes('filter') || t.includes('furnace')) return EQUIP_DESTINATIONS[2];
  return EQUIP_DESTINATIONS[3];
}

function ScheduleTab({ highlightTaskId, pmView, setPmView }: {
  highlightTaskId?: string | null;
  pmView: 'smart' | 'timeline' | 'cards';
  setPmView: React.Dispatch<React.SetStateAction<'smart' | 'timeline' | 'cards'>>;
}) {
  const { user } = useAuth();
  const { facilityId, facilityName } = useFacility();
  const navigate = useNavigate();
  const isEnterprise = user?.orgPlanTier === 'enterprise' || user?.role === 'admin';
  const isPro = user?.orgPlanTier === 'pro';
  const PRO_MONTHLY_LIMIT = 15;
  // Drives the Smart View grid's sticky task-name column (item 2, round 3) — computed once
  // here and passed down to each SortableTaskRow instead of each row running its own listener.
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  function getAnalysisStorageKey() {
    const now = new Date();
    const ym = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
    return `ai_analysis_count_${facilityId}_${ym}`;
  }
  function getAnalysisCount(): number {
    return parseInt(localStorage.getItem(getAnalysisStorageKey()) ?? '0', 10);
  }
  function incrementAnalysisCount() {
    const key = getAnalysisStorageKey();
    localStorage.setItem(key, String(getAnalysisCount() + 1));
  }
  const qc = useQueryClient();
  const [freqFilter, setFreqFilter] = useState('');
  const [panelTaskId, setPanelTaskId] = useState<number|null>(null);
  // Vendor Detail takeover (item 4) — opens ON TOP of the Task Detail takeover (higher z-index,
  // TaskDetailPage stays mounted underneath) instead of navigating to /vendors, so closing it
  // returns the user to exactly where they left off in the task.
  const [vendorDetailId, setVendorDetailId] = useState<number|null>(null);
  const [modal, setModal] = useState<{open:boolean; task?:ScTask}>({open:false});
  const [deleteModal, setDeleteModal] = useState<{open:boolean; onConfirm:()=>void}>({open:false,onConfirm:()=>{}});
  const [aiModal, setAiModal] = useState<{open:boolean; summary:string; loading:boolean}>({open:false,summary:'',loading:false});
  const [analysisModal, setAnalysisModal] = useState<{open:boolean; reportName:string; taskName:string; analysis:any; loading:boolean; taskId:number|null; reportId:number|null}>({open:false,reportName:'',taskName:'',analysis:null,loading:false,taskId:null,reportId:null});
  const [equipDestPicker, setEquipDestPicker] = useState<Record<number, boolean>>({});
  const [equipDestSelected, setEquipDestSelected] = useState<Record<number, string>>({});
  const [pickerPos, setPickerPos] = useState<{ top: number; right: number; idx: number } | null>(null);
  const [existingEquipKeys, setExistingEquipKeys] = useState<Set<string>>(new Set());
  const [selectedDeficiencies, setSelectedDeficiencies] = useState<Record<string,boolean>>({});
  const [analysisSections, setAnalysisSections] = useState<Record<string,boolean>>({equipment:false,deficiencies:false,recommendations:false});
  const [dueToast, setDueToast] = useState('');
  // Rendered inline inside the AI Report Analysis modal (zIndex 10001) rather than as the
  // page-level fixed dueToast (zIndex 9998, bottom:24/right:24) -- that toast renders behind
  // this modal's opaque panel, so a "N tasks created" confirmation there was never visible.
  const [deficiencyToast, setDeficiencyToast] = useState<string | null>(null);
  const [selectedMonths, setSelectedMonths] = useState<Record<number, number | null>>({});
  const [scheduleOpenId, setScheduleOpenId] = useState<number | null>(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [addedEquipItems, setAddedEquipItems] = useState<Record<number, 'added'|'failed'>>({});
  const [addingAll, setAddingAll] = useState(false);
  const reportFileRef = useRef<HTMLInputElement>(null);
  const attachReportTaskRef = useRef<ScTask|null>(null);
  const [serviceEditId, setServiceEditId] = useState<number|null>(null);
  const [serviceEditValue, setServiceEditValue] = useState('');
  const [confirmRemoveReport, setConfirmRemoveReport] = useState<{taskId: number; reportId: number} | null>(null);
  const [pendingReport, setPendingReport] = useState<{file: File; task: ScTask} | null>(null);
  const [pendingServiceDate, setPendingServiceDate] = useState('');

  // Item 4: year switcher. `effectiveCurrentMonth` anchors occurrence/cell-state math for the
  // selected year — CURR_MONTH (today) when viewing the real current year (unchanged behavior),
  // 13 for past years (every occurrence this year is necessarily in the past, so unfinished ones
  // are 'missed'), 1 for future years (nothing is due yet, so the earliest occurrence of that
  // year becomes the 'due' highlight, matching how the current year highlights its own next-due
  // cell, just anchored at the year's start instead of "today").
  const [selectedYear, setSelectedYear] = useState<number>(CURR_YEAR);
  const effectiveCurrentMonth = selectedYear === CURR_YEAR ? CURR_MONTH : selectedYear < CURR_YEAR ? 13 : 1;

  const { data: tasksRes, isLoading } = useQuery({
    queryKey: ['scheduled-tasks', facilityId, selectedYear],
    queryFn: async () => {
      const res = await api.get<any>('/scheduled-tasks/list', { year: selectedYear });
      // api.get wraps response in { data: ... }, server returns { success, data: [...] }
      // so we need to unwrap both layers
      const body = (res as any)?.data ?? res;
      const arr = body?.data ?? body;
      return Array.isArray(arr) ? arr : [];
    },
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
  const { data: vendorsRes } = useQuery({
    queryKey: ['vendors-list'],
    queryFn: () => api.get('/vendors', { limit: 200 }),
  });

  const tasks: ScTask[] = (tasksRes as any) ?? [];
  const vendors: { id: number; name: string }[] = (vendorsRes as any)?.data ?? [];
  const vendorsFull: VendorFull[] = (vendorsRes as any)?.data ?? [];

  const [orderedTasks, setOrderedTasks] = useState<ScTask[]>([]);

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.delete(`/scheduled-tasks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scheduled-tasks', facilityId] }),
  });
  const reorderMut = useMutation({
    mutationFn: ({ id, sort_order }: { id: number; sort_order: number }) =>
      api.patch(`/scheduled-tasks/${id}`, { sort_order }),
  });

  const didHighlight = useRef(false);

  // Close panel on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setPanelTaskId(null); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Sync orderedTasks with server data, preserving local drag order
  useEffect(() => {
    const allFreqs = ['Weekly','Monthly','Every 2 months','Quarterly','Semi-Annual','Yearly','Every 2 years','Every 3 years','Every 5 years'];
    const filtered = tasks.filter(t => !freqFilter || t.frequency === freqFilter || (freqFilter === 'other' && !allFreqs.includes(t.frequency)));
    setOrderedTasks(prev => {
      const filteredIds = new Set(filtered.map(t => t.id));
      const prevKept = prev.filter(t => filteredIds.has(t.id));
      const prevIds = new Set(prevKept.map(t => t.id));
      const newTasks = filtered.filter(t => !prevIds.has(t.id));
      const updated = prevKept.map(t => filtered.find(f => f.id === t.id) ?? t);
      return [...updated, ...newTasks];
    });
  }, [tasksRes, freqFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-expand and scroll to highlighted task from URL param
  useEffect(() => {
    if (!highlightTaskId || didHighlight.current || orderedTasks.length === 0) return;
    const id = Number(highlightTaskId);
    const task = orderedTasks.find(t => t.id === id);
    if (task) {
      setPanelTaskId(id);
      didHighlight.current = true;
      setTimeout(() => {
        const el = document.getElementById(`sched-task-${id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    }
  }, [orderedTasks, highlightTaskId]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleCellClick(task: ScTask, month: number) {
    // month is 1-indexed; clicking toggles selection — does NOT immediately complete. Also opens
    // the Task Detail Page for this task (matching the page's own "Click any cell to mark done /
    // reschedule" hint) -- previously this only toggled selectedMonths, and CellStatus's own
    // e.stopPropagation() on its onClick (see CellStatus below) meant the click never reached the
    // row's onClick (onToggleExpand/handleTogglePanel), which is the only thing that actually
    // opened the page. Only "No Service" cells (no click handler at all) worked by accident,
    // since their clicks fell through to the row. setPanelTaskId here is intentionally a direct
    // open (not handleTogglePanel's toggle-closed-if-already-open behavior) so re-clicking a cell
    // to change/clear the month selection never unexpectedly closes an already-open page.
    setSelectedMonths(prev => ({ ...prev, [task.id]: prev[task.id] === month ? null : month }));
    setPanelTaskId(task.id);
  }

  // Shared "open/close sliding detail panel for this task" toggle — factored out of Smart
  // View's SortableTaskRow onToggleExpand closure so Cards view can reuse the exact same
  // behavior (click card header -> open panel) without duplicating the logic.
  function handleTogglePanel(taskId: number) {
    setPanelTaskId(prev => {
      const next = prev === taskId ? null : taskId;
      if (prev === taskId) { setSelectedMonths(p => ({ ...p, [taskId]: null })); setScheduleOpenId(null); }
      return next;
    });
  }

  async function handleMarkComplete(task: ScTask) {
    const month = selectedMonths[task.id] ?? null;
    if (!month) return;
    const token = localStorage.getItem('accessToken');
    const baseUrl = import.meta.env.VITE_API_URL ?? '';
    try {
      const res = await fetch(`${baseUrl}/scheduled-tasks/${task.id}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ month, year: selectedYear }),
      });
      const data = await res.json();
      if (data.success) {
        qc.invalidateQueries({ queryKey: ['scheduled-tasks', facilityId, selectedYear] });
        setDueToast(data.completed ? 'Marked complete ✓' : 'Unmarked');
        setTimeout(() => setDueToast(''), 2500);
      }
    } catch { /* silent */ }
  }

  // Timeline view's "Mark items done" — bulk-applies the exact same handleMarkComplete
  // (and its underlying toggle endpoint) to every task currently holding a selectedMonths
  // selection, reusing Smart View's own selection state and completion action unchanged.
  async function handleMarkAllSelected() {
    const ids = Object.entries(selectedMonths).filter(([, m]) => m != null).map(([id]) => Number(id));
    for (const id of ids) {
      const t = tasks.find(x => x.id === id);
      if (t) await handleMarkComplete(t);
    }
  }

  async function handleScheduleNext(task: ScTask) {
    if (!scheduleDate) return;
    try {
      const token = localStorage.getItem('accessToken');
      const baseUrl = import.meta.env.VITE_API_URL ?? '';
      const res = await fetch(`${baseUrl}/scheduled-tasks/${task.id}/schedule-calendar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ date: scheduleDate }),
      });
      const data = await res.json();
      if (data.success) {
        setScheduleOpenId(null);
        setScheduleDate('');
        setDueToast('✓ Added to calendar');
        setTimeout(() => setDueToast(''), 3000);
      }
    } catch { /* silent */ }
  }

  async function handleReportUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !attachReportTaskRef.current) return;
    const today = new Date();
    const localToday = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
    setPendingServiceDate(localToday);
    setPendingReport({ file, task: attachReportTaskRef.current });
    e.target.value = '';
  }

  async function doUploadReport(file: File, task: ScTask, serviceDate: string | null) {
    const fd = new FormData();
    fd.append('file', file);
    // Never set Content-Type manually with FormData — browser sets it with boundary.
    // Uses the shared api client (not a raw fetch) so this request gets the same VITE_API_URL
    // scheme normalization (client/src/api/client.ts:4-8) and Bearer-token interceptor as every
    // other request in the app, instead of building its own URL/auth header by hand.
    try {
      // Server returns { success, report } (scheduled-tasks.ts's POST /:id/report), not the
      // { success, data } shape api.postForm's generic assumes -- cast to read .report below.
      const res = await api.postForm<any>(`/scheduled-tasks/${task.id}/report`, fd);
      const report = (res as any).report as { id: number } | undefined;
      qc.invalidateQueries({ queryKey: ['scheduled-tasks', facilityId] });
      setDueToast('Report attached');
      setTimeout(() => setDueToast(''), 2500);
      if (serviceDate && report?.id) {
        try {
          await api.patch('/fire-logbook/report-service-date', {
            scheduled_task_report_id: report.id,
            entry_date_actual: serviceDate,
          });
          qc.invalidateQueries({ queryKey: ['scheduled-tasks', facilityId] });
        } catch { /* silent */ }
      }
    } catch (err: any) {
      setDueToast(err?.response?.data?.error ?? 'Upload failed');
      setTimeout(() => setDueToast(''), 3000);
    }
  }

  async function handleDeleteReport(taskId: number, reportId: number) {
    const token = localStorage.getItem('accessToken');
    const baseUrl = import.meta.env.VITE_API_URL ?? '';
    try {
      await fetch(`${baseUrl}/scheduled-tasks/${taskId}/report/${reportId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      qc.invalidateQueries({ queryKey: ['scheduled-tasks', facilityId] });
    } catch { /* silent */ }
  }

  async function handleSaveServiceDate(reportId: number) {
    if (!serviceEditValue) return;
    try {
      await api.patch('/fire-logbook/report-service-date', {
        scheduled_task_report_id: reportId,
        entry_date_actual: serviceEditValue,
      });
      qc.invalidateQueries({ queryKey: ['scheduled-tasks', facilityId] });
      setServiceEditId(null);
      setDueToast('Service date saved');
      setTimeout(() => setDueToast(''), 2500);
    } catch {
      setDueToast('Failed to save service date');
      setTimeout(() => setDueToast(''), 3000);
    }
  }

  async function checkExistingEquipment(equipList: any[]) {
    if (equipList.length === 0) { setExistingEquipKeys(new Set()); return; }
    const items = equipList.map(eq => ({
      serial_number: eq.serial_number ?? null,
      model: eq.model ?? null,
      manufacturer: eq.manufacturer ?? null,
      name: eq.type ?? null,
    }));
    try {
      const [concRes, supRes] = await Promise.all([
        api.post('/concentrators/check-exists', { items }) as Promise<any>,
        api.post('/inventory/supplies/check-exists', { items }) as Promise<any>,
      ]);
      const keys = new Set<string>();
      const concRows: any[] = (concRes as any)?.data ?? [];
      const supRows: any[] = (supRes as any)?.data ?? [];
      concRows.forEach((r: any) => {
        if (r.serial_number) keys.add(r.serial_number);
        else if (r.model && r.manufacturer) keys.add(`${r.model}-${r.manufacturer}`);
      });
      supRows.forEach((r: any) => {
        if (r.serial_number) keys.add(r.serial_number);
        else if (r.name) keys.add(r.name);
      });
      setExistingEquipKeys(keys);
    } catch { /* silent */ }
  }

  function isInInventory(eq: any): boolean {
    if (!existingEquipKeys.size) return false;
    if (eq.serial_number) return existingEquipKeys.has(eq.serial_number);
    if (existingEquipKeys.has(`${eq.model ?? ''}-${eq.manufacturer ?? ''}`)) return true;
    if (eq.type && existingEquipKeys.has(eq.type)) return true;
    return false;
  }

  function openAnalysisModal(analysis: any, reportName: string | null, taskName = '') {
    setSelectedDeficiencies({});
    setAnalysisSections({ equipment:false, deficiencies:false, recommendations:false });
    setAddedEquipItems({});
    setEquipDestPicker({});
    setEquipDestSelected({});
    setPickerPos(null);
    setExistingEquipKeys(new Set());
    setAnalysisModal({ open:true, reportName: reportName || 'Report', taskName, analysis, loading:false, taskId:null, reportId:null });
    const equipList = Array.isArray(analysis?.equipment) ? analysis.equipment : [];
    if (equipList.length > 0) checkExistingEquipment(equipList);
  }

  // Rule 8: AI analysis may take >10s — use VITE_API_URL directly, not Vercel proxy
  async function handleAnalyzeReport(taskId: number, reportId: number, reportName: string, forceRefresh = false, taskName = '') {
    console.log('[Tasks analyze] taskId:', taskId, 'reportId:', reportId, 'force:', forceRefresh);
    const token = localStorage.getItem('accessToken');
    const baseUrl = import.meta.env.VITE_API_URL ?? '';
    setAnalysisModal({ open:true, reportName, taskName, analysis:null, loading:true, taskId, reportId });
    setSelectedDeficiencies({});
    setAnalysisSections({ equipment:false, deficiencies:false, recommendations:false });
    setAddedEquipItems({});
    setEquipDestPicker({});
    setEquipDestSelected({});
    setPickerPos(null);
    setExistingEquipKeys(new Set());
    try {
      const url = `${baseUrl}/scheduled-tasks/${taskId}/report/${reportId}/analyze${forceRefresh ? '?force=true' : ''}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token ?? ''}` },
      });
      const data = await res.json();
      if (data.success) {
        if (isPro) incrementAnalysisCount();
        setAnalysisModal(p => ({ ...p, analysis: data.analysis, loading:false }));
        qc.invalidateQueries({ queryKey: ['scheduled-tasks', facilityId] });
        const equipList = Array.isArray(data.analysis?.equipment) ? data.analysis.equipment : [];
        if (equipList.length > 0) checkExistingEquipment(equipList);
      } else {
        setAnalysisModal(p => ({ ...p, loading:false }));
        setDueToast(data.error ?? 'Analysis failed');
        setTimeout(() => setDueToast(''), 3000);
      }
    } catch {
      setAnalysisModal(p => ({ ...p, loading:false }));
      setDueToast('Analysis failed');
      setTimeout(() => setDueToast(''), 3000);
    }
  }

  async function handleAddToExtinguishers(eq: any, idx: number) {
    try {
      const notesParts = [
        eq.manufacturer ? `MFR: ${eq.manufacturer}` : null,
        eq.model ? `Model: ${eq.model}` : null,
        eq.notes ?? null,
      ].filter(Boolean);
      await api.post('/extinguishers', {
        type_model: eq.type ?? null,
        location: eq.location ?? null,
        quantity: eq.quantity ?? 1,
        serial_number: eq.serial_number ?? null,
        notes: notesParts.length > 0 ? notesParts.join(' | ') : null,
      });
      setAddedEquipItems(p => ({ ...p, [idx]: 'added' }));
      setDueToast('Added to extinguishers ✓');
      setTimeout(() => setDueToast(''), 2500);
    } catch {
      setAddedEquipItems(p => ({ ...p, [idx]: 'failed' }));
      setDueToast('Failed to add extinguisher');
      setTimeout(() => setDueToast(''), 3000);
    }
  }

  async function handleAddToConcentrators(eq: any, idx: number) {
    try {
      const condMap = (c: string) => c === 'pass' ? 'good' : c === 'fail' ? 'poor' : (c ?? 'good');
      await api.post('/concentrators', {
        model: eq.model ?? eq.type ?? null,
        manufacturer: eq.manufacturer ?? null,
        serial_number: eq.serial_number ?? null,
        resident_room: eq.location ?? null,
        condition: condMap(eq.condition),
        notes: eq.notes ?? null,
      });
      setAddedEquipItems(p => ({ ...p, [idx]: 'added' }));
      setDueToast('Added to concentrators ✓');
      setTimeout(() => setDueToast(''), 2500);
    } catch {
      setAddedEquipItems(p => ({ ...p, [idx]: 'failed' }));
      setDueToast('Failed to add concentrator');
      setTimeout(() => setDueToast(''), 3000);
    }
  }

  async function handleAddEquipment(eq: any, idx: number, tab: string) {
    setEquipDestPicker({});
    setPickerPos(null);
    if (tab === 'extinguishers') {
      await handleAddToExtinguishers(eq, idx);
    } else if (tab === 'concentrators') {
      await handleAddToConcentrators(eq, idx);
    } else if (tab === 'hvac') {
      try {
        const condMap = (c: string) => c === 'pass' ? 'good' : c === 'fail' ? 'poor' : (c ?? 'good');
        await api.post('/hvac', {
          name: eq.type ?? 'HVAC Unit',
          brand: eq.manufacturer ?? null,
          model: eq.model ?? null,
          location: eq.location ?? null,
          serial_number: eq.serial_number ?? null,
          condition: condMap(eq.condition),
          notes: eq.notes ?? null,
        });
        setAddedEquipItems(p => ({ ...p, [idx]: 'added' }));
        setDueToast('Added to HVAC ✓');
        setTimeout(() => setDueToast(''), 3000);
      } catch {
        setAddedEquipItems(p => ({ ...p, [idx]: 'failed' }));
        setDueToast('Failed to add to HVAC');
        setTimeout(() => setDueToast(''), 3000);
      }
    } else {
      // Facility Supplies > Other — direct API call, no navigation
      try {
        const condMap = (c: string) => c === 'pass' ? 'good' : c === 'fail' ? 'poor' : (c ?? 'good');
        await api.post('/inventory/supplies', {
          supply_type: 'other',
          name: eq.type ?? 'Supply Item',
          brand: eq.manufacturer ?? null,
          model: eq.model ?? null,
          serial_number: eq.serial_number ?? null,
          location: eq.location ?? null,
          quantity: eq.quantity ?? 1,
          condition: condMap(eq.condition),
          notes: eq.notes ?? null,
        });
        setAddedEquipItems(p => ({ ...p, [idx]: 'added' }));
        setDueToast('Added to Facility Supplies ✓');
        setTimeout(() => setDueToast(''), 3000);
      } catch {
        setAddedEquipItems(p => ({ ...p, [idx]: 'failed' }));
        setDueToast('Failed to add to Facility Supplies');
        setTimeout(() => setDueToast(''), 3000);
      }
    }
  }

  async function handleAddAllToExtinguishers(equipList: any[]) {
    setAddingAll(true);
    let addedCount = 0;
    for (let i = 0; i < equipList.length; i++) {
      if (addedEquipItems[i] === 'added') continue;
      const eq = equipList[i];
      try {
        await api.post('/extinguishers', {
          type_model: eq.type ?? null,
          location: eq.location ?? null,
          quantity: eq.quantity ?? 1,
          notes: eq.condition ? `Condition: ${eq.condition}` : null,
        });
        setAddedEquipItems(p => ({ ...p, [i]: 'added' }));
        addedCount++;
      } catch {
        setAddedEquipItems(p => ({ ...p, [i]: 'failed' }));
      }
    }
    setAddingAll(false);
    setDueToast(`✓ All Added (${addedCount})`);
    setTimeout(() => setDueToast(''), 3000);
  }

  async function handleCreateTasksFromDeficiencies(deficiencies: any[]) {
    const selected = deficiencies.filter((_, i) => selectedDeficiencies[String(i)]);
    if (selected.length === 0) return;
    const priorityMap: Record<string, string> = { critical:'high', major:'medium', minor:'low' };
    let created = 0;
    for (const d of selected) {
      try {
        await api.post('/tasks', {
          title: String(d.description ?? '').slice(0, 100),
          priority: priorityMap[d.severity] ?? 'low',
          status: 'open',
        });
        created++;
      } catch { /* silent */ }
    }
    setDeficiencyToast(`${created} task${created !== 1 ? 's' : ''} created`);
    setTimeout(() => setDeficiencyToast(null), 3500);
    setSelectedDeficiencies({});
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setOrderedTasks(prev => {
      const oldIndex = prev.findIndex(t => t.id === active.id);
      const newIndex = prev.findIndex(t => t.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const reordered = arrayMove(prev, oldIndex, newIndex);
      reordered.forEach((t, idx) => {
        if (prev[idx]?.id !== t.id) reorderMut.mutate({ id: t.id, sort_order: idx });
      });
      return reordered;
    });
  }

  async function handleAiSummary() {
    setAiModal({ open: true, summary: '', loading: true });
    try {
      const res = await api.post('/scheduled-tasks/ai-summary', {
        tasks: tasks.map(t => ({ name: t.name, frequency: t.frequency, last_completed: t.last_completed_date })),
      });
      setAiModal({ open: true, summary: (res as any).data?.summary ?? 'No summary available.', loading: false });
    } catch {
      setAiModal({ open: true, summary: 'Failed to generate AI summary.', loading: false });
    }
  }

  // Redesigned per mockups/compliance-pdf-export-redesign.html. Round 1 printed whichever of
  // Smart View/Timeline/Cards happened to be on screen. Rounds 2-3 replaced that with a
  // dedicated, always-mounted ComplianceExportSheet in the MAIN document, shown for print via a
  // display:none/@media print toggle — round 2 toggled it with a JS-injected stylesheet, round 3
  // made that stylesheet static instead of injected. Both still shared the main document's print
  // context with everything else in the app (sidebar, topbar, whatever else is mounted), which
  // is what round 4 investigation traced the remaining bugs to:
  //
  // ITEM 2 (mixed portrait/landscape pages) — the named-page approach (`@page compliance-pdf` +
  // `page: compliance-pdf` on the sheet) asks the browser to apply landscape sizing to ONE
  // element's page context while the rest of the (display:none, but still page-context-eligible)
  // document implicitly uses the default page context. Real-world browser support for mixing
  // page contexts within a single print job this way is inconsistent — exactly the mixed
  // portrait/landscape symptom reported. Reports.tsx's generateQapiPDF already solves this
  // differently for its own PDF export: it opens an isolated `window.open('', '_blank')`, writes
  // a fully separate HTML document into it, and prints THAT window. An isolated document only
  // ever has ONE page context, so a plain unscoped `@page { size: landscape }` inside it can't
  // conflict with or bleed into anything else — no named-page feature needed at all. This function
  // now follows that exact established pattern instead.
  //
  // ITEM 1 (same two rows always faded, deterministically, survived removing all JS timing) —
  // ruled out an on-screen scroll-fade mask as the cause: grepped the whole client/ tree for
  // `mask-image`/`mask`/`.grid-wrap` and found none anywhere in this codebase. And structurally,
  // even if one existed on the live Smart View grid, it could not have leaked into the export:
  // the export sheet has always been its own self-contained markup with its own inline styles,
  // sharing no DOM/CSS with the live grid. The real, deterministic explanation is in the data
  // itself: dotStyle()'s 'empty' state used very low-contrast colors (#f4f5f8 bg / #e3e7ee
  // border, chosen to match the mockup's deliberately-subtle "no service" dots). A task with a
  // low-frequency schedule (e.g. an annual fire-safety check) has up to 11 of its 12 month cells
  // in that pale 'empty' state — so THAT SPECIFIC TASK's row reads as visually washed-out next to
  // a monthly task's row full of saturated done/due dots, on every single export, regardless of
  // timing — matching "same rows every time" exactly. Fixed by darkening the 'empty' dot/legend
  // colors enough to stay legible on their own while remaining visually subordinate to
  // done/due/missed/scheduled (still reads as "empty", just no longer print-illegible).
  function handleExportPDF() {
    const mo3 = (m: number) => MONTH_FULL_LABELS[m - 1].slice(0, 3);
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const rows = [...tasks].sort((a, b) => a.sort_order - b.sort_order).map(task => {
      const firstDueDate = task.first_due_date ? new Date(task.first_due_date) : null;
      const occMonths = firstDueDate ? getOccurrenceMonths(firstDueDate, task.frequency, selectedYear) : [];
      const completedSet = new Set<number>(task.completions);
      const nextDue = getNextDueMonth(occMonths, completedSet, effectiveCurrentMonth);
      const monthStates: CellState[] = Array.from({ length:12 }, (_, i) =>
        getCellStateLocal(i + 1, occMonths, completedSet, effectiveCurrentMonth, nextDue));
      const completedCount = occMonths.filter(m => completedSet.has(m)).length;
      const missedIdx = monthStates.findIndex(s => s === 'missed');

      let nextDueText: string, nextDueColor: string;
      if (missedIdx !== -1) {
        nextDueText = `Missed ${mo3(missedIdx + 1)}`; nextDueColor = '#c8302f';
      } else if (nextDue === effectiveCurrentMonth) {
        nextDueText = `Due ${mo3(effectiveCurrentMonth)}`; nextDueColor = '#1d63d8';
      } else if (nextDue !== null) {
        nextDueText = mo3(nextDue); nextDueColor = '#0a8a5f';
      } else if (firstDueDate) {
        let found: { yr: number; month: number } | null = null;
        for (let yr = selectedYear + 1; yr <= selectedYear + 6; yr++) {
          const occ = getOccurrenceMonths(firstDueDate, task.frequency, yr);
          if (occ.length > 0) { found = { yr, month: occ[0] }; break; }
        }
        nextDueText = found ? `${mo3(found.month)} '${String(found.yr).slice(2)}` : 'Not scheduled';
        nextDueColor = found ? '#0a8a5f' : '#8890a0';
      } else {
        nextDueText = 'Not scheduled'; nextDueColor = '#8890a0';
      }

      return { task, monthStates, completedCount, totalCount: occMonths.length, nextDueText, nextDueColor };
    });

    // Item 1 fix: 'empty' darkened from #f4f5f8/#e3e7ee (illegible on a mostly-empty row) to
    // #eceef2/#c3cad4 — still clearly the palest/most neutral state, just no longer reads as a
    // rendering defect on annual/low-frequency tasks' rows.
    const dotStyles: Record<CellState, { bg: string; border: string; color: string }> = {
      completed: { bg:'#e3f7ef', border:'#34b581', color:'#0a8a5f' },
      due:       { bg:'#e6f0fd', border:'#1d63d8', color:'#1d63d8' },
      scheduled: { bg:'#fdf3e0', border:'#f0b53a', color:'#f0b53a' },
      missed:    { bg:'#fbe4e4', border:'#e35a5a', color:'#c8302f' },
      empty:     { bg:'#eceef2', border:'#c3cad4', color:'#c3cad4' },
    };
    const dotHtml = (state: CellState) => {
      const s = dotStyles[state];
      const inner = state === 'completed' ? '&#10003;' : state === 'missed' ? '&#10007;'
        : state === 'scheduled' ? `<span style="width:5px;height:5px;border-radius:99px;background:${s.color};display:inline-block"></span>` : '';
      return `<span style="width:16px;height:16px;border-radius:5px;display:inline-flex;align-items:center;justify-content:center;font-size:10px;font-weight:800;border:1.3px solid ${s.border};background:${s.bg};color:${s.color}">${inner}</span>`;
    };

    const freqTagColors: Record<string, { bg: string; color: string }> = {
      'Weekly': { bg:'#fde3ef', color:'#c22a72' }, 'Monthly': { bg:'#fde3ef', color:'#c22a72' },
      'Every 2 months': { bg:'#fdecd2', color:'#b3760a' }, 'Quarterly': { bg:'#fdecd2', color:'#b3760a' },
      'Semi-Annual': { bg:'#ece3fb', color:'#6d3fc9' },
      'Yearly': { bg:'#e3f7ef', color:'#0a8a5f' }, 'Every 2 years': { bg:'#e3f7ef', color:'#0a8a5f' },
      'Every 3 years': { bg:'#e3f7ef', color:'#0a8a5f' }, 'Every 4 years': { bg:'#e3f7ef', color:'#0a8a5f' },
      'Every 5 years': { bg:'#e3f7ef', color:'#0a8a5f' },
    };

    const kpiCards = [
      { label:'Compliance YTD', value:`${complianceKpis.ytdPct}%`, sub:complianceKpis.ytdSubtitle, accent:'#10b981', valueColor:'#0a8a5f' },
      { label:'Due this month', value:String(complianceKpis.dueCount), sub:complianceKpis.dueSubtitle, accent:'#3b82f6', valueColor:'#1d63d8' },
      { label:'Missed', value:String(complianceKpis.missedCount), sub:complianceKpis.missedSubtitle, accent:'#ef4444', valueColor:'#c8302f' },
      { label:'Scheduled ahead', value:String(complianceKpis.scheduledAheadCount), sub:complianceKpis.scheduledSubtitle, accent:'#f59e0b', valueColor:'#b3760a' },
    ];

    const generatedDate = new Date().toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
    const generatedBy = user?.username ?? 'Unknown user';
    const legend: { label: string; color: string }[] = [
      { label:'Completed', color:'#34b581' }, { label:'Due now', color:'#1d63d8' },
      { label:'Scheduled', color:'#f0b53a' }, { label:'Missed', color:'#e35a5a' }, { label:'No service', color:'#c3cad4' },
    ];

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Compliance PM Report — ${esc(facilityName)}</title>
<style>
  * { box-sizing: border-box; }
  @page { size: landscape; margin: 12mm 10mm; }
  body { font-family: 'Inter','Helvetica Neue',Arial,sans-serif; color: #1a2233; background: #fff; margin: 0; padding: 0; }
  .sheet { padding: 34px 40px 28px; }
  table { width: 100%; border-collapse: collapse; font-size: 0.72rem; }
  thead th { text-align: center; font-size: 0.62rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #5b6577; padding: 7px 6px; border-bottom: 2px solid #14213d; }
  tbody td { padding: 8px 6px; border-bottom: 1px solid #eef0f4; text-align: center; }
</style>
</head>
<body>
<div class="sheet">
  <div style="display:flex;align-items:flex-start;justify-content:space-between;border-bottom:2px solid #14213d;padding-bottom:16px;margin-bottom:18px">
    <div>
      <h1 style="margin:0;font-size:1.3rem;font-weight:700;color:#0e1526">Compliance PM Report</h1>
      <div style="font-size:0.78rem;color:#5b6577;margin-top:3px">${esc(facilityName)}</div>
    </div>
    <div style="text-align:right;font-size:0.72rem;color:#5b6577;line-height:1.6">
      <div><b style="color:#14213d">Year:</b> ${selectedYear} &middot; <b style="color:#14213d">Generated:</b> ${generatedDate}</div>
      <div>By ${esc(generatedBy)} &middot; ProQure Compliance PM</div>
    </div>
  </div>

  <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:20px">
    ${kpiCards.map(k => `
    <div style="border:1px solid #e3e7ee;border-top:3px solid ${k.accent};border-radius:8px;padding:11px 14px;background:#fbfcfe">
      <div style="font-size:0.62rem;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#8890a0">${esc(k.label)}</div>
      <div style="font-size:1.3rem;font-weight:700;font-family:'JetBrains Mono',monospace;margin-top:2px;color:${k.valueColor}">${esc(k.value)}</div>
      <div style="font-size:0.66rem;color:#8890a0;margin-top:1px">${esc(k.sub)}</div>
    </div>`).join('')}
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:190px;text-align:left;padding-left:4px">Task</th>
        <th style="width:70px">Next due</th>
        <th style="width:40px">YTD</th>
        ${MONTH_FULL_LABELS.map((m, i) => {
          const isNow = i + 1 === effectiveCurrentMonth;
          return `<th style="${isNow ? 'color:#1d63d8' : ''}">${m.slice(0, 3)}${isNow ? '<span style="display:block;font-size:0.55rem;font-weight:700;color:#fff;background:#1d63d8;border-radius:99px;padding:1px 7px;margin:2px auto 0;width:max-content">NOW</span>' : ''}</th>`;
        }).join('')}
      </tr>
    </thead>
    <tbody>
      ${rows.map(({ task, monthStates, completedCount, totalCount, nextDueText, nextDueColor }, idx) => {
        const freqTag = freqTagColors[task.frequency];
        return `
      <tr style="${idx % 2 === 1 ? 'background:#fafbfd' : ''}">
        <td style="text-align:left">
          <div style="font-weight:600;font-size:0.76rem;color:#0e1526">${esc(task.name)}</div>
          <div style="font-size:0.64rem;color:#8890a0;margin-top:1px;display:flex;align-items:center;gap:6px">
            ${esc(task.vendor_name ?? '')}
            ${freqTag ? `<span style="font-size:0.6rem;font-weight:700;padding:2px 7px;border-radius:99px;white-space:nowrap;background:${freqTag.bg};color:${freqTag.color}">${esc(task.frequency)}</span>` : ''}
          </div>
        </td>
        <td><span style="font-size:0.62rem;font-weight:700;font-family:'JetBrains Mono',monospace;color:${nextDueColor}">${esc(nextDueText)}</span></td>
        <td>${completedCount}/${totalCount}</td>
        ${monthStates.map((state, i) => `<td style="${i + 1 === effectiveCurrentMonth ? 'background:rgba(29,99,216,0.05)' : ''}">${dotHtml(state)}</td>`).join('')}
      </tr>`;
      }).join('')}
    </tbody>
  </table>

  <div style="display:flex;align-items:center;justify-content:space-between;margin-top:18px;padding-top:12px;border-top:1px solid #e3e7ee">
    <div style="display:flex;gap:14px;font-size:0.66rem;color:#5b6577">
      ${legend.map(l => `<span style="display:inline-flex;align-items:center;gap:5px"><span style="width:11px;height:11px;border-radius:3px;display:inline-block;background:${l.color}"></span>${esc(l.label)}</span>`).join('')}
    </div>
    <div style="font-size:0.66rem;color:#8890a0">Generated via ProQure</div>
  </div>
</div>
</body>
</html>`;

    // Isolated print window — same established pattern as Reports.tsx's generateQapiPDF. An
    // isolated document has exactly one page context, so the plain `@page { size: landscape }`
    // above can't conflict with (or lose to) anything else, unlike round 3's named-page approach
    // sharing the main document's print context.
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 500);
  }

  // pmView/setPmView are now props — lifted up to TasksPage (item 1) so the Smart View/
  // Timeline/Cards switcher can render inline with the main tab row instead of its own row here.

  // KPI strip — computed from the existing getOccurrenceMonths/getNextDueMonth/getCellStateLocal
  // helpers already used by the Classic grid below, so the numbers always agree with what the
  // grid itself shows. Does not read orderedTasks (which is filtered by freqFilter) — the KPI
  // strip reflects the full compliance picture regardless of the Classic grid's own filter.
  const complianceKpis = useMemo(() => {
    let done = 0, missed = 0, dueInPast = 0, scheduledAhead = 0;
    // ytdItems tracks every completed/missed (task, month) pair through the current month —
    // this is exactly the numerator+denominator of Compliance YTD, shown in its hover popover.
    const ytdItems: { name: string; month: number; done: boolean }[] = [];
    const missedItems: { name: string; month: number }[] = [];
    // FIX (was: `if (nextDue !== null)`, which counted any task with ANY outstanding occurrence
    // left in the year — nearly every task — not just ones due in CURR_MONTH specifically.
    // nextDue === CURR_MONTH is exactly the condition getCellStateLocal uses to paint a cell
    // 'due' (blue/pulsing) in the current-month column, so this now matches the Classic grid.
    const dueItems: { name: string; month: number }[] = [];
    const scheduledItems: { name: string; month: number }[] = [];

    for (const task of tasks) {
      if (!task.first_due_date) continue;
      const firstDueDate = new Date(task.first_due_date);
      const occMonths = getOccurrenceMonths(firstDueDate, task.frequency, selectedYear);
      const completedSet = new Set<number>(task.completions);
      const nextDue = getNextDueMonth(occMonths, completedSet, effectiveCurrentMonth);
      if (nextDue === effectiveCurrentMonth) dueItems.push({ name: task.name, month: effectiveCurrentMonth });
      for (const month of occMonths) {
        const state = getCellStateLocal(month, occMonths, completedSet, effectiveCurrentMonth, nextDue);
        if (state === 'completed') { done++; if (month <= effectiveCurrentMonth) ytdItems.push({ name: task.name, month, done: true }); }
        else if (state === 'missed') { missed++; missedItems.push({ name: task.name, month }); ytdItems.push({ name: task.name, month, done: false }); }
        else if (state === 'due' && month < effectiveCurrentMonth) dueInPast++;
        else if (state === 'scheduled') { scheduledAhead++; scheduledItems.push({ name: task.name, month }); }
      }
    }

    missedItems.sort((a, b) => a.month - b.month);
    scheduledItems.sort((a, b) => a.month - b.month);
    ytdItems.sort((a, b) => a.month - b.month);

    const ytdDenom = done + missed + dueInPast;
    const ytdPct = ytdDenom > 0 ? Math.round((done / ytdDenom) * 100) : 100;
    const firstMissed = missedItems[0];
    const nextScheduled = scheduledItems[0] ?? null;

    const mo = (m: number) => MONTH_FULL_LABELS[m - 1].slice(0, 3);
    const capList = (lines: string[], cap = 6) => {
      if (lines.length === 0) return 'No items';
      const shown = lines.slice(0, cap);
      const extra = lines.length - shown.length;
      return extra > 0 ? [...shown, `+${extra} more`].join('\n') : shown.join('\n');
    };

    return {
      ytdPct,
      ytdSubtitle: `${done} of ${ytdDenom} completed on time`,
      ytdTip: capList(ytdItems.map(i => `${i.name} — ${mo(i.month)} ${i.done ? '✓ Done' : '✕ Missed'}`)),
      dueCount: dueItems.length,
      dueSubtitle: dueItems.length > 0 ? dueItems.slice(0, 2).map(i => i.name).join(' · ') : 'Nothing due',
      dueTip: capList(dueItems.map(i => `${i.name} — ${mo(i.month)}`)),
      missedCount: missed,
      missedSubtitle: firstMissed ? `${firstMissed.name} · ${mo(firstMissed.month)}` : 'None missed',
      missedTip: capList(missedItems.map(i => `${i.name} — ${mo(i.month)}`)),
      scheduledAheadCount: scheduledAhead,
      scheduledSubtitle: nextScheduled ? `Next: ${nextScheduled.name} · ${mo(nextScheduled.month)}` : 'None scheduled',
      scheduledTip: capList(scheduledItems.map(i => `${i.name} — ${mo(i.month)}`)),
    };
  }, [tasks, selectedYear, effectiveCurrentMonth]);

  return (
    // .card's actual background is a hardcoded rgba(255,255,255,0.045) literal, not
    // var(--bg-surface) — that variable resolves to a solid #162032 in this app's dark theme
    // (global.css :root), not the transparent glass value DESIGN_SYSTEM.md documents for it.
    // Every Compliance PM card in this file was built using var(--bg-surface) directly, which
    // is why they rendered flat/opaque instead of matching .card's glass look used everywhere
    // else. Fixed by using .card's literal value instead of the var.
    <div style={{ background:'rgba(255,255,255,0.045)', backdropFilter:'blur(16px)', WebkitBackdropFilter:'blur(16px)', borderRadius:12, border:'1px solid var(--border-subtle)', padding:16 }}>
      {/* KPI strip — Phase 1 redesign, purely additive.
          pm-scale-16: rescales this ported-from-mockup block to the mockup's 16px baseline
          (see .pm-scale-16 comment in buttonAnimations.css) — em values below resolve off it.
          Real iPhone screenshot: the mockup's own @media(max-width:900px){.kpis{grid-template-
          columns:repeat(2,1fr)}} rule was never actually ported — this div just had a hardcoded
          gridTemplateColumns:'repeat(4,1fr)' inline style with no responsive collapse at all, so
          all 4 KPI cards ran off the right edge on any phone. Inline styles can't express a media
          query, so the 900px breakpoint lives in a .compliance-kpi-grid class in
          buttonAnimations.css instead (same reason/pattern as .task-detail-grid there). */}
      <div className="pm-scale-16 compliance-kpi-grid" style={{ display:'grid', gap:12, marginBottom:14 }}>
        {([
          { key:'ytd', color:'#10b981', icon:'✓', label:'Compliance YTD', value:`${complianceKpis.ytdPct}%`, sub:complianceKpis.ytdSubtitle, tip:complianceKpis.ytdTip },
          { key:'due', color:'#3b82f6', icon:'◷', label:'Due this month', value:String(complianceKpis.dueCount), sub:complianceKpis.dueSubtitle, tip:complianceKpis.dueTip },
          { key:'missed', color:'#ef4444', icon:'!', label:'Missed', value:String(complianceKpis.missedCount), sub:complianceKpis.missedSubtitle, tip:complianceKpis.missedTip },
          { key:'ahead', color:'#f59e0b', icon:'◔', label:'Scheduled ahead', value:String(complianceKpis.scheduledAheadCount), sub:complianceKpis.scheduledSubtitle, tip:complianceKpis.scheduledTip },
        ] as { key:string; color:string; icon:string; label:string; value:string; sub:string; tip:string }[]).map(k => (
          <div key={k.key} className="kpi-hover-tip" data-tip={k.tip}
            // Same lift + border-highlight treatment as Vendors.tsx's .vendor-card:hover
            // (translateY(-3px), border rgba(59,130,246,0.4/0.5), shadow rgba(0,0,0,0.3/0.12)
            // dark/light) — done via JS, not a CSS :hover rule, because this card already sets
            // `border` + `borderTop` inline (the colored per-KPI top accent); an inline style
            // always wins over an external :hover rule regardless of specificity (the same
            // trap documented elsewhere in this file for Orders.tsx's Mail icon / schActionBtn),
            // so a CSS hover rule for border-color would be silently ignored here. Only the
            // right/bottom/left border color is touched so the colored top accent is preserved.
            onMouseEnter={e => {
              const isLight = document.documentElement.getAttribute('data-theme') === 'light';
              const c = isLight ? 'rgba(59,130,246,0.5)' : 'rgba(59,130,246,0.4)';
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.borderRightColor = c;
              e.currentTarget.style.borderBottomColor = c;
              e.currentTarget.style.borderLeftColor = c;
              e.currentTarget.style.boxShadow = isLight ? '0 8px 24px rgba(0,0,0,0.12)' : '0 8px 24px rgba(0,0,0,0.3)';
            }}
            onMouseLeave={e => {
              // Reset to the literal resting value ('var(--border-subtle)'), not '' — the
              // initial border was set via the `border` shorthand, so clearing just the
              // -color longhand with '' doesn't fall back to that shorthand's color, it falls
              // back to the CSS default border-color (currentColor, i.e. the card's inherited
              // TEXT color) which is near-white in dark theme. That's the bug: border going
              // white after hover-out instead of returning to var(--border-subtle).
              e.currentTarget.style.transform = '';
              e.currentTarget.style.borderRightColor = 'var(--border-subtle)';
              e.currentTarget.style.borderBottomColor = 'var(--border-subtle)';
              e.currentTarget.style.borderLeftColor = 'var(--border-subtle)';
              e.currentTarget.style.boxShadow = '';
              spinIconOut(e);
            }}
            style={{
              background:'rgba(255,255,255,0.045)', backdropFilter:'blur(16px)', WebkitBackdropFilter:'blur(16px)', border:'1px solid var(--border-subtle)',
              borderRadius:'var(--radius-lg)', borderTop:`2px solid ${k.color}`,
              padding:'14px 18px', display:'flex', alignItems:'center', gap:14,
              transition:'all 0.2s ease',
            }}>
            <div style={{ width:38, height:38, borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', fontSize:17, flexShrink:0, background:`${k.color}24` }}>
              <span className="btn-icon">{k.icon}</span>
            </div>
            <div>
              <div style={{ fontSize:'0.68em', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-muted)' }}>{k.label}</div>
              <div style={{ fontSize:'1.45em', fontWeight:700, lineHeight:1.15, fontFamily:'JetBrains Mono, monospace', color:k.color }}>{k.value}</div>
              <div style={{ fontSize:'0.7em', color:'var(--text-muted)' }}>{k.sub}</div>
            </div>
          </div>
        ))}
      </div>

      {/* View switcher moved to TasksPage's main tab row (item 1) — pmView/setPmView are now
          props, the switcher itself no longer renders here. */}

      {pmView === 'timeline' && (
        <ComplianceTimelineView
          tasks={tasks}
          year={selectedYear}
          nowMonth={effectiveCurrentMonth}
          onYearChange={setSelectedYear}
          selectedMonths={selectedMonths}
          onCellClick={handleCellClick}
          onOpenDetail={handleTogglePanel}
          onMarkSelected={handleMarkAllSelected}
          onExportPDF={handleExportPDF}
          onAiSummary={handleAiSummary}
          isEnterprise={isEnterprise}
        />
      )}

      {pmView === 'cards' && (
        <ComplianceCardsView
          tasks={tasks}
          year={selectedYear}
          nowMonth={effectiveCurrentMonth}
          onYearChange={setSelectedYear}
          selectedMonths={selectedMonths}
          onCellClick={handleCellClick}
          onOpenPanel={handleTogglePanel}
          onAddTask={() => setModal({ open:true })}
          onEditTask={(task) => setModal({ open:true, task })}
          onDeleteTask={(task) => setDeleteModal({ open:true, onConfirm:()=>deleteMut.mutate(task.id) })}
          onExportPDF={handleExportPDF}
          onAiSummary={handleAiSummary}
          isEnterprise={isEnterprise}
        />
      )}

      {pmView === 'smart' && (
      /* pm-scale-16: rescales the ported-from-mockup toolbar/legend/grid to the mockup's
         16px baseline (see .pm-scale-16 comment in buttonAnimations.css). */
      <div className="pm-scale-16">
      {/* Toolbar */}
      <div style={{ display:'flex', gap:8, marginBottom:14, alignItems:'center', flexWrap:'wrap' }}>
        <button className="btn combo5-hero" onClick={()=>setModal({ open:true })} onMouseLeave={spinIconOut}>
          <Plus size={14} className="btn-icon"/> Add Task
        </button>
        <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <select className="form-input" value={freqFilter} onChange={e=>setFreqFilter(e.target.value)} style={{ width:180 }}>
            <option value="">All Frequencies</option>
            {['Weekly','Monthly','Every 2 months','Quarterly','Semi-Annual','Yearly','Every 2 years','Every 3 years','Every 5 years'].map(f=><option key={f} value={f}>{f}</option>)}
          </select>
          {/* Year switcher — ported from mockups/compliance-pm-redesign.html .year-nav */}
          <div className="year-nav">
            <button onClick={() => setSelectedYear(y => y - 1)} title="Previous year">‹</button>
            <span className="yr">{selectedYear}</span>
            <button onClick={() => setSelectedYear(y => y + 1)} title="Next year">›</button>
          </div>
          <button className="btn btn-ghost combo1" onClick={handleExportPDF} onMouseLeave={spinIconOut}>
            <Download size={14} className="btn-icon"/> Export PDF
          </button>
          {isEnterprise && (
            <button className="btn combo4-ai" onClick={handleAiSummary} onMouseLeave={spinIconOut}>
              <Settings2 size={14} className="btn-icon"/> AI Summary
            </button>
          )}
        </div>
      </div>

      {/* Legend — ported from mockups/compliance-pm-redesign.html .legend/.lg/.sw exactly,
          including the done/missed glyphs, "Due now"/"No service" labels, and trailing hint */}
      <div style={{ display:'flex', alignItems:'center', gap:16, padding:'10px 18px', borderBottom:'1px solid var(--border-subtle)', marginBottom:14, flexWrap:'wrap', fontSize:'0.72em', color:'var(--text-secondary)' }}>
        {([
          { label:'Completed',  bg:'rgba(16,185,129,0.16)', border:'rgba(16,185,129,0.55)', glyph:'✓', glyphColor:'#34d399' },
          { label:'Due now',    bg:'rgba(59,130,246,0.12)',  border:'#3b82f6',               glyph:'',  glyphColor:'' },
          { label:'Scheduled',  bg:'rgba(245,158,11,0.09)',  border:'rgba(245,158,11,0.45)', glyph:'',  glyphColor:'' },
          { label:'Missed',     bg:'rgba(239,68,68,0.13)',   border:'rgba(239,68,68,0.55)',  glyph:'✕', glyphColor:'#f87171' },
          { label:'No service', bg:'rgba(255,255,255,0.025)',border:'var(--border-subtle)',  glyph:'',  glyphColor:'' },
        ] as {label:string;bg:string;border:string;glyph:string;glyphColor:string}[]).map(({ label, bg, border, glyph, glyphColor }) => (
          <span key={label} style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
            <span style={{
              width:14, height:14, borderRadius:4, background:bg, border:`1.5px solid ${border}`, flexShrink:0,
              display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:8, fontWeight:700, color:glyphColor,
            }}>{glyph}</span>
            {label}
          </span>
        ))}
        <span style={{ marginLeft:'auto', fontSize:'0.68em', color:'var(--text-muted)' }}>Click any cell to mark done / reschedule</span>
      </div>

      {/* Table */}
      {isLoading ? (
        <div style={{ textAlign:'center', padding:40, color:'#94a3b8' }}>Loading…</div>
      ) : orderedTasks.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:'#94a3b8' }}>
          <Calendar size={40} style={{ opacity:0.3, marginBottom:12 }}/><br/>
          No tasks yet. Click "+ Add Task" to create your first compliance task.
        </div>
      ) : (
        // Bounded height + overflowY:'auto' matches the app's existing sticky-header pattern
        // (Settings.tsx's stickyTh user table, Financials.tsx's order list) rather than relying
        // on page-level scroll: overflowX:'auto' alone would make the browser compute this
        // div's overflow-y as 'auto' too (CSS coerces overflow-x/y to match when only one is
        // set), and since this div's height was previously unconstrained, that computed
        // overflow-y never actually created a scroll offset here — the page above it did the
        // real scrolling, which is a different scroll context than what position:sticky's `th`
        // elements resolve against, undermining stickiness. Giving this div a real bounded
        // height with overflowY explicitly set removes that ambiguity entirely.
        <div style={{ overflowX:'auto', overflowY:'auto', maxHeight:'calc(100vh - 340px)' }}>
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            {/* minWidth 1080 matches mockup's table.pm min-width:1080px exactly, now that the
                Next due + YTD columns bring the live grid's column count in line with it. */}
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.8125em', minWidth:1080, tableLayout:'fixed' }}>
              <colgroup>
                <col style={{ width:32 }}/>
                <col style={{ width:260 }}/>
                <col style={{ width:100 }}/>
                <col style={{ width:90 }}/>
                <col style={{ width:50 }}/>
                {Array.from({length:12}).map((_,i)=><col key={i} style={{ width:34 }}/>)}
                <col style={{ width:96 }}/>
              </colgroup>
              <thead>
                <tr>
                  {/* Sticky-left skipped on mobile, matching SortableTaskRow's body cells below —
                      see the isMobile comment there. */}
                  <th style={{ ...schThStyle, ...(isMobile ? {} : { ...schStickyColStyle(0), zIndex:2 }), padding:0 }}/>
                  <th style={{ ...schThStyle, ...(isMobile ? {} : { ...schStickyColStyle(32), zIndex:2 }), textAlign:'left', paddingLeft:12 }}>Task</th>
                  <th style={{ ...schThStyle }}>Occurrence</th>
                  <th style={{ ...schThStyle }}>Next due</th>
                  <th style={{ ...schThStyle }}>YTD</th>
                  {MONTH_FULL_LABELS.map((full, i) => {
                    const isNow = selectedYear === CURR_YEAR && i+1===CURR_MONTH;
                    return (
                      <th key={i} style={{
                        ...schThStyle, padding:'8px 2px',
                        color: isNow ? '#93c5fd' : '#94a3b8',
                        fontWeight: isNow ? 700 : 600,
                        // Never `undefined` — that would clear schThStyle's own solid
                        // background (React drops style properties set to undefined instead of
                        // falling back to the earlier spread value), leaving the header
                        // transparent and defeating the point of a sticky header (rows would
                        // show through it while scrolling). The isNow tint is layered as a
                        // gradient ON TOP of the same solid var(--bg-base), never replacing it.
                        background: isNow ? 'linear-gradient(rgba(59,130,246,0.07), rgba(59,130,246,0.07)), var(--bg-base)' : 'var(--bg-base)',
                      }}>
                        {full.slice(0, 3)}
                        {isNow && (
                          <span style={{
                            display:'block', fontSize:'0.55em', fontWeight:700, color:'#0d1526',
                            background:'#3b82f6', borderRadius:99, padding:'1px 6px', margin:'3px auto 0',
                            width:'max-content', letterSpacing:'0.04em',
                          }}>NOW</span>
                        )}
                      </th>
                    );
                  })}
                  <th style={{ ...schThStyle }}/>
                </tr>
              </thead>
              <SortableContext items={orderedTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                <tbody>
                  {orderedTasks.map(task => {
                    const isExpanded = panelTaskId === task.id;
                    return (
                      <SortableTaskRow
                        key={`r-${task.id}`}
                        task={task}
                        year={selectedYear}
                        nowMonth={effectiveCurrentMonth}
                        isExpanded={isExpanded}
                        selectedMonth={selectedMonths[task.id] ?? null}
                        onCellClick={handleCellClick}
                        isHighlighted={highlightTaskId ? task.id === Number(highlightTaskId) : false}
                        onToggleExpand={() => handleTogglePanel(task.id)}
                        onEdit={() => handleTogglePanel(task.id)}
                        onDelete={() => setDeleteModal({ open:true, onConfirm:()=>deleteMut.mutate(task.id) })}
                        isMobile={isMobile}
                      />
                    );
                  })}
                </tbody>
              </SortableContext>
            </table>
          </DndContext>
        </div>
      )}
      </div>
      )}

      {/* Add/Edit Modal */}
      {modal.open && (
        <TaskScheduleModal
          task={modal.task}
          vendors={vendors}
          facilityName={facilityName}
          onClose={()=>setModal({ open:false })}
          onSaved={()=>{ qc.invalidateQueries({ queryKey:['scheduled-tasks', facilityId] }); setModal({ open:false }); }}
        />
      )}

      {/* Delete Confirm */}
      {deleteModal.open && (
        <DeleteConfirmModal
          onConfirm={()=>{ deleteModal.onConfirm(); setDeleteModal({ open:false, onConfirm:()=>{} }); }}
          onClose={()=>setDeleteModal({ open:false, onConfirm:()=>{} })}
        />
      )}

      {/* Hidden file input for report attachments */}
      <input ref={reportFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style={{display:'none'}} onChange={handleReportUpload}/>

      {/* Toast notification */}
      {dueToast && (
        <div style={{ position:'fixed', bottom:24, right:24, zIndex:9998, background:'#064e3b', color:'#10b981', border:'1px solid #10b981', borderRadius:8, padding:'10px 18px', fontWeight:600, fontSize:'0.875rem', boxShadow:'0 4px 20px rgba(0,0,0,0.4)' }}>
          {dueToast}
        </div>
      )}

      {/* AI Report Analysis Modal */}
      {analysisModal.open && ReactDOM.createPortal(
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', backdropFilter:'blur(6px)', zIndex:10001, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:16, overflowY:'auto' }}
          onClick={() => setAnalysisModal(p => ({ ...p, open:false }))}>
          <div style={{ background:'#0f1923', borderRadius:12, border:'1px solid #1e2d45', width:'90vw', maxWidth:1200, maxHeight:'90vh', marginTop:20, marginBottom:20, display:'flex', flexDirection:'column', overflow:'hidden' }}
            onClick={e => e.stopPropagation()}>
            {/* Header — fixed, does not scroll */}
            <div style={{ flexShrink:0, padding:'28px 28px 20px', display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <h3 style={{ margin:0, color:'#e2e8f0', fontSize:'1.1rem' }}>AI Report Analysis</h3>
                <div style={{ color:'#64748b', fontSize:'0.8rem', marginTop:4 }}>{analysisModal.reportName}</div>
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                {!analysisModal.loading && analysisModal.analysis && (
                  <button onClick={() => { if (analysisModal.taskId && analysisModal.reportId) handleAnalyzeReport(analysisModal.taskId, analysisModal.reportId, analysisModal.reportName, true, analysisModal.taskName); }}
                    style={{ background:'none', border:'1px solid #334155', borderRadius:6, cursor:'pointer', color:'#94a3b8', padding:'4px 10px', fontSize:'0.8rem' }}>
                    🔄 Re-analyze
                  </button>
                )}
                <button onClick={() => { setPickerPos(null); setExistingEquipKeys(new Set()); setAnalysisModal({ open:false, reportName:'', taskName:'', analysis:null, loading:false, taskId:null, reportId:null }); }}
                  style={{ background:'none', border:'none', cursor:'pointer', color:'#64748b' }}>
                  <X size={20}/>
                </button>
              </div>
            </div>

            {/* Scrollable body — only this area scrolls, header stays fixed */}
            <div style={{ flex:1, overflowY:'auto', overflowX:'auto', padding:'0 28px 28px' }}>
            {analysisModal.loading ? (
              <div style={{ textAlign:'center', padding:60, color:'#64748b' }}>
                <div style={{ fontSize:'2.5rem', marginBottom:12 }}>🤖</div>
                <div>Analyzing report…</div>
              </div>
            ) : analysisModal.analysis ? (() => {
              const a = analysisModal.analysis;
              const insp = a.inspector ?? {};
              const equip: any[] = Array.isArray(a.equipment) ? a.equipment : [];
              const defs: any[] = Array.isArray(a.deficiencies) ? a.deficiencies : [];
              const recs: string[] = Array.isArray(a.recommendations) ? a.recommendations : [];
              const sevColor = (s: string) => s === 'critical' ? '#ef4444' : s === 'major' ? '#f59e0b' : '#3b82f6';
              const isSprinklerTask = /sprinkler/i.test(analysisModal.taskName);
              return (
                <>
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  {/* Section 1 — Inspector Info (always expanded) */}
                  <div style={{ border:'1px solid #1e2d45', borderRadius:8, overflow:'hidden' }}>
                    <div style={{ background:'#0d1b2a', padding:'10px 14px', fontSize:'0.8rem', fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em' }}>
                      Inspector Info
                    </div>
                    <div style={{ padding:'12px 14px', display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                      {(['name','date','certification'] as const).map(k => (
                        <div key={k}>
                          <div style={{ fontSize:'0.7rem', color:'#475569', textTransform:'uppercase', marginBottom:3 }}>{k}</div>
                          <div style={{ color:'#e2e8f0', fontSize:'0.85rem' }}>{insp[k] ?? '—'}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Section 2 — Equipment */}
                  <div style={{ border:'1px solid #1e2d45', borderRadius:8, overflow:'hidden' }}>
                    <button onClick={() => setAnalysisSections(p => ({ ...p, equipment: !p.equipment }))}
                      style={{ width:'100%', background:'#0d1b2a', border:'none', cursor:'pointer', padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ fontSize:'0.8rem', fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em' }}>Equipment Found ({equip.length})</span>
                      <ChevronDown size={14} color="#475569" style={{ transform: analysisSections.equipment ? 'rotate(180deg)' : 'none', transition:'transform 0.15s' }}/>
                    </button>
                    {!analysisSections.equipment && equip.length > 0 && (
                      <div style={{ overflowX:'auto', paddingRight:20 }}>
                        {/* "Add All to Extinguishers" — only for extinguisher inspection tasks, never for sprinkler */}
                        {isEnterprise && !isSprinklerTask && /extinguisher/i.test(analysisModal.taskName) && (
                          <div style={{ padding:'8px 12px', borderBottom:'1px solid #1e2d45', display:'flex', justifyContent:'flex-end' }}>
                            <button
                              onClick={() => handleAddAllToExtinguishers(equip)}
                              disabled={addingAll || equip.every((_, i) => addedEquipItems[i] === 'added')}
                              style={{ background:'none', border:'1px solid #10b981', borderRadius:4, cursor: (addingAll || equip.every((_, i) => addedEquipItems[i] === 'added')) ? 'not-allowed' : 'pointer', color:'#10b981', padding:'3px 10px', fontSize:'0.75rem', opacity: (addingAll || equip.every((_, i) => addedEquipItems[i] === 'added')) ? 0.5 : 1 }}>
                              {addingAll ? 'Adding…' : equip.every((_, i) => addedEquipItems[i] === 'added') ? `✓ All Added (${equip.length})` : `+ Add All to Extinguishers`}
                            </button>
                          </div>
                        )}
                        {/* minWidth (sum of the 8 column minWidths below, plus slack) forces a
                            real horizontal scrollbar on the wrapping overflowX:'auto' div once
                            content genuinely needs more room, instead of silently letting the
                            last column's content (the "+ Add to…" button) clip past the modal's
                            right edge with no way to reach it -- confirmed live: table-layout:auto
                            without an explicit table minWidth doesn't reliably force the ancestor
                            scroll container to grow, so an unusually long destination label (e.g.
                            "Facility Supplies (Other)") clipped outright rather than scrolling. */}
                        <table style={{ width:'100%', minWidth:990, borderCollapse:'collapse', fontSize:'0.8rem' }}>
                          <thead>
                            <tr style={{ borderBottom:'1px solid #1e2d45' }}>
                              {[
                                { label:'Type', w:160 }, { label:'Manufacturer', w:130 },
                                { label:'Model', w:110 }, { label:'Serial #', w:120 },
                                { label:'Location', w:90 }, { label:'Qty', w:50 },
                                { label:'Status', w:70 }, { label:'', w:200 },
                              ].map(h => (
                                <th key={h.label} style={{ padding:'6px 12px', textAlign:'left', color:'#475569', fontWeight:600, fontSize:'0.72rem', textTransform:'uppercase', whiteSpace:'nowrap', minWidth:h.w }}>{h.label}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {equip.map((eq, i) => {
                              const autoDest = getEquipmentDestination(eq.type ?? '');
                              const pickerOpen = !!equipDestPicker[i];
                              return (
                                <tr key={i} style={{ borderBottom:'1px solid #0f1923' }}>
                                  <td style={{ padding:'7px 12px', color:'#e2e8f0', whiteSpace:'nowrap' }}>{eq.type ?? '—'}</td>
                                  <td style={{ padding:'7px 12px', color:'#94a3b8', whiteSpace:'nowrap' }}>{eq.manufacturer ?? '—'}</td>
                                  <td style={{ padding:'7px 12px', color:'#94a3b8', whiteSpace:'nowrap' }}>{eq.model ?? '—'}</td>
                                  <td style={{ padding:'7px 8px', color:'#94a3b8', fontFamily:'monospace', fontSize:'0.72rem', whiteSpace:'nowrap' }}>{eq.serial_number ?? '—'}</td>
                                  <td style={{ padding:'7px 12px', color:'#94a3b8' }}>{eq.location ?? '—'}</td>
                                  <td style={{ padding:'7px 12px', color:'#94a3b8' }}>{eq.quantity ?? '—'}</td>
                                  <td style={{ padding:'7px 12px' }}>
                                    <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                                      {eq.condition === 'pass' ? (
                                        <span style={{ background:'#064e3b', color:'#10b981', borderRadius:4, padding:'1px 7px', fontSize:'0.7rem', fontWeight:700 }}>Pass</span>
                                      ) : eq.condition === 'fail' ? (
                                        <span style={{ background:'#450a0a', color:'#ef4444', borderRadius:4, padding:'1px 7px', fontSize:'0.7rem', fontWeight:700 }}>Fail</span>
                                      ) : (
                                        <span style={{ color:'#94a3b8', fontSize:'0.72rem' }}>{eq.condition ?? '—'}</span>
                                      )}
                                      {eq.notes && <div style={{ fontSize:10, color:'var(--text-muted)', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{eq.notes}</div>}
                                    </div>
                                  </td>
                                  <td style={{ padding:'7px 20px 7px 12px', minWidth:200, whiteSpace:'nowrap' }}>
                                    {!isSprinklerTask && (isEnterprise ? (
                                      addedEquipItems[i] === 'added' ? (
                                        <span style={{ fontSize:11, color:'#3b82f6', display:'inline-flex', alignItems:'center', gap:4, padding:'3px 8px', borderRadius:6, background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.2)' }}>✓ Added</span>
                                      ) : addedEquipItems[i] === 'failed' ? (
                                        <span style={{ color:'#ef4444', fontSize:'0.7rem' }}>Failed</span>
                                      ) : isInInventory(eq) ? (
                                        <span style={{ fontSize:11, color:'var(--accent-success)', display:'inline-flex', alignItems:'center', gap:4, padding:'3px 8px', borderRadius:6, background:'rgba(16,185,129,0.1)', border:'1px solid rgba(16,185,129,0.2)' }}>✓ In Inventory</span>
                                      ) : pickerOpen ? (
                                        <span style={{ color:'#64748b', fontSize:'0.7rem' }}>…</span>
                                      ) : (
                                        <button
                                          onClick={(e) => {
                                            const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                                            setPickerPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right, idx: i });
                                            setEquipDestPicker(p => ({ ...p, [i]: true }));
                                          }}
                                          style={{ background:'none', border:`1px solid ${autoDest.color}`, borderRadius:4, cursor:'pointer', color:autoDest.color, padding:'2px 7px', fontSize:'0.7rem', whiteSpace:'nowrap' }}>
                                          + Add to {autoDest.shortLabel}
                                        </button>
                                      )
                                    ) : (
                                      <span title="Enterprise feature" style={{ color:'#475569', fontSize:'0.7rem', cursor:'default' }}>🔒 Enterprise</span>
                                    ))}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Section 3 — Deficiencies */}
                  <div style={{ border:'1px solid #1e2d45', borderRadius:8, overflow:'hidden' }}>
                    <button onClick={() => setAnalysisSections(p => ({ ...p, deficiencies: !p.deficiencies }))}
                      style={{ width:'100%', background:'#0d1b2a', border:'none', cursor:'pointer', padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ fontSize:'0.8rem', fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em' }}>Deficiencies ({defs.length})</span>
                      <ChevronDown size={14} color="#475569" style={{ transform: analysisSections.deficiencies ? 'rotate(180deg)' : 'none', transition:'transform 0.15s' }}/>
                    </button>
                    {!analysisSections.deficiencies && defs.length > 0 && (
                      <div style={{ padding:'10px 14px', display:'flex', flexDirection:'column', gap:8 }}>
                        {defs.map((d, i) => (
                          <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                            <input type="checkbox" checked={!!selectedDeficiencies[String(i)]}
                              onChange={e => setSelectedDeficiencies(p => ({ ...p, [String(i)]: e.target.checked }))}
                              style={{ marginTop:3, flexShrink:0, cursor:'pointer' }}/>
                            <div style={{ flex:1 }}>
                              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
                                <span style={{ background:sevColor(d.severity), color:'#fff', borderRadius:4, padding:'1px 7px', fontSize:'0.7rem', fontWeight:700, textTransform:'uppercase' }}>
                                  {d.severity ?? 'minor'}
                                </span>
                                <span style={{ color:'#e2e8f0', fontSize:'0.85rem' }}>{d.description}</span>
                              </div>
                              {(d.manufacturer || d.model || d.serial_number) && (
                                <div style={{ color:'#64748b', fontSize:'0.75rem', marginBottom:2 }}>
                                  {[d.equipment_type, d.manufacturer, d.model, d.serial_number ? `S/N: ${d.serial_number}` : null].filter(Boolean).join(' · ')}
                                </div>
                              )}
                              {d.location && <div style={{ color:'#64748b', fontSize:'0.78rem' }}>{d.location}</div>}
                            </div>
                          </div>
                        ))}
                        <button onClick={() => handleCreateTasksFromDeficiencies(defs)}
                          disabled={!Object.values(selectedDeficiencies).some(Boolean)}
                          style={{ alignSelf:'flex-start', background:'#3b82f6', border:'none', borderRadius:6, cursor:'pointer', color:'#fff', padding:'6px 14px', fontSize:'0.8rem', fontWeight:600, marginTop:4, opacity: Object.values(selectedDeficiencies).some(Boolean) ? 1 : 0.4 }}>
                          Create Tasks for Selected
                        </button>
                        {deficiencyToast && (
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '8px 12px', borderRadius: 'var(--radius-md)',
                            background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.35)',
                            color: '#10b981', fontSize: '0.8125rem', fontWeight: 500,
                          }}>
                            <Check size={14} /> {deficiencyToast}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Section 4 — Recommendations */}
                  <div style={{ border:'1px solid #1e2d45', borderRadius:8, overflow:'hidden' }}>
                    <button onClick={() => setAnalysisSections(p => ({ ...p, recommendations: !p.recommendations }))}
                      style={{ width:'100%', background:'#0d1b2a', border:'none', cursor:'pointer', padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ fontSize:'0.8rem', fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em' }}>Recommendations ({recs.length})</span>
                      <ChevronDown size={14} color="#475569" style={{ transform: analysisSections.recommendations ? 'rotate(180deg)' : 'none', transition:'transform 0.15s' }}/>
                    </button>
                    {!analysisSections.recommendations && recs.length > 0 && (
                      <ol style={{ margin:0, padding:'10px 14px 10px 32px', display:'flex', flexDirection:'column', gap:6 }}>
                        {recs.map((rec, i) => (
                          <li key={i} style={{ color:'#94a3b8', fontSize:'0.85rem', lineHeight:1.5 }}>{rec}</li>
                        ))}
                      </ol>
                    )}
                  </div>
                </div>
                {pickerPos !== null && ReactDOM.createPortal(
                  <div
                    style={{ position:'fixed', top:pickerPos.top, right:pickerPos.right, background:'#0d1b2a', border:'1px solid #1e2d45', borderRadius:8, padding:'8px 10px', zIndex:20000, minWidth:180, boxShadow:'0 4px 16px rgba(0,0,0,0.6)' }}
                    onClick={e => e.stopPropagation()}>
                    <div style={{ fontSize:'0.68rem', color:'#475569', marginBottom:6, fontWeight:600, textTransform:'uppercase' }}>Add to…</div>
                    {EQUIP_DESTINATIONS.map(d => {
                      const selTab = equipDestSelected[pickerPos!.idx] ?? getEquipmentDestination(equip[pickerPos!.idx]?.type ?? '').tab;
                      return (
                        <label key={d.tab} style={{ display:'flex', alignItems:'center', gap:6, padding:'3px 0', cursor:'pointer', fontSize:'0.78rem', color: selTab === d.tab ? d.color : '#94a3b8' }}>
                          <input type="radio" name="dest-portal" value={d.tab}
                            checked={selTab === d.tab}
                            onChange={() => setEquipDestSelected(p => ({ ...p, [pickerPos!.idx]: d.tab }))}
                            style={{ accentColor: d.color, cursor:'pointer' }} />
                          {d.label}
                        </label>
                      );
                    })}
                    <div style={{ display:'flex', gap:6, marginTop:8 }}>
                      <button onClick={() => {
                        const selTab = equipDestSelected[pickerPos!.idx] ?? getEquipmentDestination(equip[pickerPos!.idx]?.type ?? '').tab;
                        handleAddEquipment(equip[pickerPos!.idx], pickerPos!.idx, selTab);
                      }}
                        style={{ flex:1, background:'#3b82f6', border:'none', borderRadius:4, cursor:'pointer', color:'#fff', padding:'4px 0', fontSize:'0.72rem', fontWeight:600 }}>
                        Add here
                      </button>
                      <button onClick={() => { setPickerPos(null); setEquipDestPicker(p => ({ ...p, [pickerPos!.idx]: false })); }}
                        style={{ background:'none', border:'1px solid #334155', borderRadius:4, cursor:'pointer', color:'#64748b', padding:'4px 8px', fontSize:'0.72rem' }}>
                        ✕
                      </button>
                    </div>
                  </div>,
                  document.body
                )}
                </>
              );
            })() : null}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* AI Summary Modal */}
      {aiModal.open && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', backdropFilter:'blur(4px)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'var(--bg-elevated)', borderRadius:'var(--radius-xl)', border:'1px solid var(--border-default)', padding:28, maxWidth:560, width:'100%', maxHeight:'80vh', overflowY:'auto', zIndex:10000 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <h3 style={{ margin:0, display:'flex', alignItems:'center', gap:8 }}>
                <Settings2 size={16} color="#8b5cf6"/> AI Compliance Summary
              </h3>
              <button onClick={()=>setAiModal({ open:false, summary:'', loading:false })}
                style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-secondary)' }}><X size={18}/></button>
            </div>
            {aiModal.loading
              ? <div style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>Generating summary…</div>
              : <p style={{ color:'var(--text-primary)', lineHeight:1.7, whiteSpace:'pre-wrap' }}>{aiModal.summary}</p>
            }
          </div>
        </div>
      )}

      {/* Task Detail page — Full Takeover Page (mockups/compliance-task-detail-redesign.html
          Option C). Replaces the old sliding panel as the shared detail surface. Opens from
          Smart View's row/edit-icon clicks and Cards' card-header click (both already call
          handleTogglePanel), and from Timeline's agenda item clicks (onOpenDetail below) — all
          three set the same panelTaskId, so this single instance serves all of them. */}
      {panelTaskId !== null && (() => {
        const panelTask = orderedTasks.find(t => t.id === panelTaskId) ?? null;
        if (!panelTask) return null;
        const vendor = vendorsFull.find(v => v.id === panelTask.vendor_id) ?? null;
        return (
          <TaskDetailPage
            task={panelTask}
            vendor={vendor}
            facilityName={facilityName}
            year={selectedYear}
            nowMonth={effectiveCurrentMonth}
            selectedMonth={selectedMonths[panelTask.id] ?? null}
            onCellClick={(month) => handleCellClick(panelTask, month)}
            onClose={() => setPanelTaskId(null)}
            onMarkComplete={() => handleMarkComplete(panelTask)}
            onAttachReportClick={() => { attachReportTaskRef.current = panelTask; reportFileRef.current?.click(); }}
            scheduleOpen={scheduleOpenId === panelTask.id}
            scheduleDate={scheduleDate}
            onScheduleDateChange={setScheduleDate}
            onOpenSchedule={() => { setScheduleOpenId(panelTask.id); setScheduleDate(''); }}
            onCloseSchedule={() => { setScheduleOpenId(null); setScheduleDate(''); }}
            onConfirmSchedule={() => handleScheduleNext(panelTask)}
            onEditTask={() => setModal({ open:true, task: panelTask })}
            onDeleteTask={() => setDeleteModal({ open:true, onConfirm:()=>deleteMut.mutate(panelTask.id) })}
            isEnterprise={isEnterprise}
            isPro={isPro}
            getAnalysisCount={getAnalysisCount}
            proMonthlyLimit={PRO_MONTHLY_LIMIT}
            onAnalyzeReport={(reportId, reportName, force) => handleAnalyzeReport(panelTask.id, reportId, reportName, force, panelTask.name)}
            onOpenAnalysisModal={(analysis, reportName) => openAnalysisModal(analysis, reportName, panelTask.name)}
            onDeleteReport={(reportId) => handleDeleteReport(panelTask.id, reportId)}
            confirmRemoveReportId={confirmRemoveReport?.taskId === panelTask.id ? confirmRemoveReport.reportId : null}
            onRequestRemoveReport={(reportId) => setConfirmRemoveReport({ taskId: panelTask.id, reportId })}
            onCancelRemoveReport={() => setConfirmRemoveReport(null)}
            serviceEditId={serviceEditId}
            serviceEditValue={serviceEditValue}
            onServiceEditIdChange={setServiceEditId}
            onServiceEditValueChange={setServiceEditValue}
            onSaveServiceDate={handleSaveServiceDate}
            facilityId={facilityId!}
            onOpenVendor={setVendorDetailId}
          />
        );
      })()}

      {/* Vendor Detail takeover (item 4) — reuses the same already-fetched vendorsFull record
          the Vendor card reads from (same GET /vendors payload, no new request), presented via
          the same full-page-takeover pattern as TaskDetailPage. Renders on top of it (higher
          z-index) rather than replacing it, so closing this returns straight to the task. */}
      {vendorDetailId !== null && (() => {
        const v = vendorsFull.find(x => x.id === vendorDetailId) ?? null;
        if (!v) return null;
        return <VendorDetailTakeover vendor={v} isEnterprise={isEnterprise} onClose={() => setVendorDetailId(null)} />;
      })()}

      {pendingReport && (
        <ReportServiceDateModal
          taskName={pendingReport.task.name}
          serviceDate={pendingServiceDate}
          onServiceDateChange={setPendingServiceDate}
          onConfirm={async () => {
            const { file, task } = pendingReport;
            setPendingReport(null);
            await doUploadReport(file, task, pendingServiceDate);
          }}
          onSkip={async () => {
            const { file, task } = pendingReport;
            setPendingReport(null);
            await doUploadReport(file, task, null);
          }}
        />
      )}
    </div>
  );
}

// ─── Compliance PM: Timeline view ──────────────────────────────────────────────
// Ported from mockups/compliance-pm-redesign.html Option B (id="view-b"). Additive only —
// reuses ScheduleTab's already-fetched `tasks`/`selectedMonths` and its existing
// getOccurrenceMonths/getNextDueMonth/getCellStateLocal helpers + handleMarkComplete action;
// no new API calls, no changes to Smart View/SortableTaskRow/CellStatus.
//
// Two data-granularity notes vs. the mockup (which shows exact days like "Jul 21"):
// ScTask only tracks completions at month granularity (no day-of-month for any occurrence),
// so all date tags here are month-only (e.g. "JUL" not "JUL 21") — same adaptation already
// used for CellStatus's tooltip and the KPI popovers.
//
// Left agenda panel is select/highlight-only (onCellClick), never opens the shared Task Detail
// page — it exists so Timeline's own "Mark items done" bulk action knows which items are
// selected. Completed-this-month items don't even get onCellClick: POST
// /scheduled-tasks/:id/complete toggles, so selecting an already-completed item and hitting
// "Mark items done" would silently un-mark it — a bad trap for a section literally labeled
// "Completed". The shared Task Detail page (onOpenDetail) opens from the RIGHT year-timeline
// panel's event rows instead — clicking a specific month's event there opens that task.

const TIMELINE_TAG_COLORS: Record<'r' | 'b' | 'g' | 'a', { bg: string; color: string }> = {
  r: { bg: 'rgba(239,68,68,0.15)', color: '#f87171' },
  b: { bg: 'rgba(59,130,246,0.15)', color: '#60a5fa' },
  g: { bg: 'rgba(16,185,129,0.15)', color: '#34d399' },
  a: { bg: 'rgba(245,158,11,0.15)', color: '#fbbf24' },
};

function AgendaItem({ task, tag, tagText, selected, onClick }: {
  task: ScTask; tag: 'r' | 'b' | 'g' | 'a'; tagText: string; selected?: boolean; onClick?: () => void;
}) {
  const tc = TIMELINE_TAG_COLORS[tag];
  return (
    <div
      onClick={onClick}
      onMouseEnter={onClick ? e => { e.currentTarget.style.filter = 'brightness(1.3)'; } : undefined}
      onMouseLeave={onClick ? e => { e.currentTarget.style.filter = ''; } : undefined}
      style={{
        display:'flex', alignItems:'center', gap:11, margin:'0 10px 6px', padding:'9px 10px',
        borderRadius:10,
        border: selected ? '1px solid var(--accent-primary)' : '1px solid var(--border-subtle)',
        background: selected ? 'rgba(59,130,246,0.08)' : 'rgba(255,255,255,0.045)',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <span style={{ fontSize:17, flexShrink:0 }}>{task.icon}</span>
      <div style={{ minWidth:0, flex:1 }}>
        <div style={{ fontSize:'0.8em', fontWeight:600, color:'var(--text-primary)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{task.name}</div>
        <div style={{ fontSize:'0.68em', color:'var(--text-muted)' }}>{task.vendor_name ?? '—'}</div>
      </div>
      <span style={{
        marginLeft:'auto', fontSize:'0.62em', fontWeight:700, padding:'3px 8px', borderRadius:99,
        fontFamily:"'JetBrains Mono', monospace", whiteSpace:'nowrap', flexShrink:0,
        background:tc.bg, color:tc.color,
      }}>{tagText}</span>
    </div>
  );
}

interface ComplianceTimelineViewProps {
  tasks: ScTask[];
  year: number;
  nowMonth: number; // effectiveCurrentMonth from ScheduleTab
  onYearChange: React.Dispatch<React.SetStateAction<number>>;
  selectedMonths: Record<number, number | null>;
  onCellClick: (task: ScTask, month: number) => void;
  onOpenDetail: (taskId: number) => void;
  onMarkSelected: () => void;
  onExportPDF: () => void;
  onAiSummary: () => void;
  isEnterprise: boolean;
}

function ComplianceTimelineView({ tasks, year, nowMonth, onYearChange, selectedMonths, onCellClick, onOpenDetail, onMarkSelected, onExportPDF, onAiSummary, isEnterprise }: ComplianceTimelineViewProps) {
  const mo = (m: number) => (MONTH_FULL_LABELS[m - 1] ?? '').slice(0, 3).toUpperCase();

  const data = useMemo(() => {
    type Item = { task: ScTask; month: number };
    const missed: Item[] = [];
    const due: Item[] = [];
    const completed: Item[] = [];
    const upcoming: Item[] = [];
    const byMonth: { month: number; events: { task: ScTask; state: CellState }[] }[] =
      Array.from({ length:12 }, (_, i) => ({ month: i + 1, events: [] }));

    for (const task of tasks) {
      if (!task.first_due_date) continue;
      const firstDueDate = new Date(task.first_due_date);
      const occMonths = getOccurrenceMonths(firstDueDate, task.frequency, year);
      const completedSet = new Set<number>(task.completions);
      const nextDue = getNextDueMonth(occMonths, completedSet, nowMonth);
      for (const month of occMonths) {
        const state = getCellStateLocal(month, occMonths, completedSet, nowMonth, nextDue);
        if (state === 'empty') continue;
        byMonth[month - 1].events.push({ task, state });
        if (state === 'missed') missed.push({ task, month });
        else if (state === 'due') due.push({ task, month });
        else if (state === 'completed' && month === nowMonth) completed.push({ task, month });
        else if (state === 'scheduled') upcoming.push({ task, month });
      }
    }
    missed.sort((a, b) => a.month - b.month);
    due.sort((a, b) => a.month - b.month);
    upcoming.sort((a, b) => a.month - b.month);
    return { missed, due, completed, upcoming, byMonth };
  }, [tasks, year, nowMonth]);

  const agendaMonthLabel = year === CURR_YEAR ? `${MONTH_FULL_LABELS[CURR_MONTH - 1]} ${year}` : String(year);
  const agendaSubLabel = year === CURR_YEAR ? 'This month at a glance' : year < CURR_YEAR ? 'Year in review' : 'Year ahead';
  const cap = 6;
  const isEmpty = data.missed.length === 0 && data.due.length === 0 && data.completed.length === 0 && data.upcoming.length === 0;

  return (
    // pm-scale-16: rescales the ported-from-mockup Timeline view to the mockup's 16px
    // baseline (see .pm-scale-16 comment in buttonAnimations.css) — em values below resolve off it.
    // Real iPhone screenshot: the right "Year timeline" panel wasn't missing from the DOM at
    // all (verified below, it's unconditionally rendered, no isMobile gate) — this grid just had
    // a hardcoded gridTemplateColumns:'340px 1fr' with no responsive collapse, so on a narrow
    // viewport the fixed 340px left column consumed nearly all available width and squeezed the
    // right '1fr' column to near-zero/off-screen. Same bug pattern as the KPI strip fixed last
    // round — moved to a real CSS media query (.compliance-timeline-grid, buttonAnimations.css)
    // since inline styles can't express one, same 900px breakpoint as .compliance-kpi-grid.
    <div className="pm-scale-16 compliance-timeline-grid" style={{ display:'grid', gap:14, alignItems:'start' }}>
      {/* LEFT — agenda. position:sticky moved into .compliance-timeline-agenda (same file) so it
          can be disabled at the same 900px breakpoint — once the grid stacks to one column,
          staying sticky would pin the agenda over the timeline content scrolling beneath it
          instead of letting the user scroll agenda-then-timeline like two normal stacked
          sections. */}
      <div className="compliance-timeline-agenda" style={{
        background:'rgba(255,255,255,0.045)', backdropFilter:'blur(16px)', WebkitBackdropFilter:'blur(16px)',
        border:'1px solid var(--border-subtle)', borderRadius:'var(--radius-lg)', overflow:'hidden',
      }}>
        <div style={{ padding:'16px 18px 12px', borderBottom:'1px solid var(--border-subtle)' }}>
          <div style={{ fontSize:'1.05em', fontWeight:700, color:'var(--text-primary)' }}>{agendaMonthLabel}</div>
          <div style={{ fontSize:'0.72em', color:'var(--text-muted)', marginTop:2 }}>{agendaSubLabel}</div>
        </div>

        {data.missed.length > 0 && <>
          <div style={{ padding:'12px 18px 4px', fontSize:'0.66em', fontWeight:700, letterSpacing:'0.07em', textTransform:'uppercase', color:'#f87171' }}>⚠ Missed — needs action</div>
          {data.missed.slice(0, cap).map((it, i) => (
            <AgendaItem key={`m-${it.task.id}-${it.month}-${i}`} task={it.task} tag="r" tagText={`${mo(it.month)} ✕`}
              selected={selectedMonths[it.task.id] === it.month} onClick={() => onCellClick(it.task, it.month)} />
          ))}
        </>}

        {data.due.length > 0 && <>
          <div style={{ padding:'12px 18px 4px', fontSize:'0.66em', fontWeight:700, letterSpacing:'0.07em', textTransform:'uppercase', color:'#60a5fa' }}>◎ Due this month</div>
          {data.due.slice(0, cap).map((it, i) => (
            <AgendaItem key={`d-${it.task.id}-${it.month}-${i}`} task={it.task} tag="b" tagText={mo(it.month)}
              selected={selectedMonths[it.task.id] === it.month} onClick={() => onCellClick(it.task, it.month)} />
          ))}
        </>}

        {/* Completed items have no onClick at all — they're not eligible for onCellClick
            selection (selecting an already-completed item and hitting "Mark items done" would
            silently un-mark it via the POST .../complete toggle), and the left panel is
            select/highlight-only now, so there's nothing left for a click here to do. Opening
            the detail page moved to the right year-timeline panel's rows (see onOpenDetail
            below). */}
        {data.completed.length > 0 && <>
          <div style={{ padding:'12px 18px 4px', fontSize:'0.66em', fontWeight:700, letterSpacing:'0.07em', textTransform:'uppercase', color:'#34d399' }}>✓ Completed this month</div>
          {data.completed.slice(0, cap).map((it, i) => (
            <AgendaItem key={`c-${it.task.id}-${it.month}-${i}`} task={it.task} tag="g" tagText={mo(it.month)} />
          ))}
        </>}

        {data.upcoming.length > 0 && <>
          <div style={{ padding:'12px 18px 4px', fontSize:'0.66em', fontWeight:700, letterSpacing:'0.07em', textTransform:'uppercase', color:'#fbbf24' }}>◔ Coming up next</div>
          {data.upcoming.slice(0, cap).map((it, i) => (
            <AgendaItem key={`u-${it.task.id}-${it.month}-${i}`} task={it.task} tag="a" tagText={mo(it.month)}
              selected={selectedMonths[it.task.id] === it.month} onClick={() => onCellClick(it.task, it.month)} />
          ))}
        </>}

        {isEmpty && (
          <div style={{ padding:'24px 18px', fontSize:'0.8em', color:'var(--text-muted)', textAlign:'center' }}>No compliance activity for {year}.</div>
        )}

        <button className="btn btn-primary" onClick={onMarkSelected} style={{ margin:'8px 18px 16px', width:'calc(100% - 36px)' }}>
          ✓ Mark items done
        </button>
      </div>

      {/* RIGHT — year timeline */}
      <div style={{ background:'rgba(255,255,255,0.045)', backdropFilter:'blur(16px)', WebkitBackdropFilter:'blur(16px)', border:'1px solid var(--border-subtle)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'14px 18px', borderBottom:'1px solid var(--border-subtle)', flexWrap:'wrap' }}>
          <span style={{ fontWeight:600, fontSize:'0.9em', color:'var(--text-primary)' }}>Year timeline</span>
          <div style={{ flex:1 }} />
          {/* Reuses the shared .year-nav class built for Smart View — not rebuilt */}
          <div className="year-nav">
            <button onClick={() => onYearChange(y => y - 1)} title="Previous year">‹</button>
            <span className="yr">{year}</span>
            <button onClick={() => onYearChange(y => y + 1)} title="Next year">›</button>
          </div>
          <button className="btn btn-ghost combo1" onClick={onExportPDF} onMouseLeave={spinIconOut}>
            <Download size={14} className="btn-icon"/> Export PDF
          </button>
          {isEnterprise && (
            <button className="btn combo4-ai" onClick={onAiSummary} onMouseLeave={spinIconOut}>
              <Settings2 size={14} className="btn-icon"/> AI Summary
            </button>
          )}
        </div>
        <div style={{ padding:'8px 0' }}>
          {data.byMonth.map(({ month, events }) => {
            const isNow = year === CURR_YEAR && month === nowMonth;
            const isPast = month < nowMonth;
            return (
              <div key={month} style={{ display:'grid', gridTemplateColumns:'96px 1fr', gap:0, position:'relative' }}>
                <div style={{ position:'relative', display:'flex', flexDirection:'column', alignItems:'center', padding:'16px 0' }}>
                  <div style={{ position:'absolute', top:0, bottom:0, width:2, background:'var(--border-subtle)' }} />
                  <span style={{
                    width:12, height:12, borderRadius:99, zIndex:1, marginTop:6,
                    background: isNow ? 'var(--accent-primary)' : isPast ? 'rgba(16,185,129,0.35)' : 'var(--bg-elevated)',
                    border: `2px solid ${isNow ? '#93c5fd' : isPast ? 'var(--accent-success)' : 'var(--text-muted)'}`,
                    boxShadow: isNow ? '0 0 0 5px rgba(59,130,246,0.18)' : 'none',
                  }} />
                  <span style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:'0.72em', fontWeight:600, marginTop:8, color: isNow ? '#93c5fd' : 'var(--text-muted)' }}>
                    {mo(month)}
                  </span>
                </div>
                <div style={{
                  padding: isNow ? '12px 14px' : '12px 18px 12px 6px',
                  display:'flex', flexDirection:'column', gap:7,
                  borderBottom: month < 12 ? '1px dashed var(--border-subtle)' : 'none',
                  background: isNow ? 'rgba(59,130,246,0.05)' : undefined,
                  border: isNow ? '1px solid rgba(59,130,246,0.18)' : undefined,
                  borderRadius: isNow ? 12 : undefined,
                  margin: isNow ? '8px 6px' : undefined,
                }}>
                  {events.length === 0 ? (
                    <span style={{ fontSize:'0.72em', color:'var(--text-muted)', fontStyle:'italic', padding:'4px 12px' }}>No compliance activity</span>
                  ) : events.map(({ task, state }, i) => (
                    <div key={`${task.id}-${i}`}
                      onClick={() => onOpenDetail(task.id)}
                      onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.2)'; }}
                      onMouseLeave={e => { e.currentTarget.style.filter = ''; }}
                      style={{
                        display:'flex', alignItems:'center', gap:10, padding:'8px 12px', borderRadius:9,
                        border:'1px solid var(--border-subtle)', background:'rgba(255,255,255,0.045)',
                        fontSize:'0.8em', opacity: state === 'scheduled' ? 0.55 : 1, cursor:'pointer',
                      }}>
                      <span style={{
                        width:9, height:9, borderRadius:99, flexShrink:0,
                        background: state === 'completed' ? 'var(--accent-success)' : state === 'due' ? 'var(--accent-primary)'
                          : state === 'missed' ? 'var(--accent-danger)' : 'var(--accent-warning)',
                        boxShadow: state === 'due' ? '0 0 0 3px rgba(59,130,246,0.25)' : 'none',
                      }} />
                      <span style={{ fontWeight:600, color:'var(--text-primary)' }}>{task.name}</span>
                      <span style={{ fontSize:'0.68em', color:'var(--text-muted)' }}>{task.vendor_name ?? '—'}</span>
                      <span style={{ marginLeft:'auto', fontSize:'0.66em', color:'var(--text-muted)', fontFamily:"'JetBrains Mono', monospace" }}>
                        {state === 'completed' ? '✓ Done' : state === 'missed' ? '✕ Missed' : state === 'due' ? 'DUE' : 'Scheduled'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Compliance PM: Cards view ──────────────────────────────────────────────
// Ported from mockups/compliance-pm-redesign.html Option C (id="view-c"). Additive only —
// reuses ScheduleTab's already-fetched `tasks`/`selectedMonths` and its existing
// getOccurrenceMonths/getNextDueMonth/getCellStateLocal helpers + handleCellClick/
// handleTogglePanel actions; no new API calls, no changes to Smart View/Timeline.
//
// Dot click selects a month (same handleCellClick Smart View's cells use). Marking a month
// complete still happens via the sliding detail panel's "Mark Complete" button, opened by
// clicking the card header — reuses Smart View's existing select-then-confirm flow instead of
// inventing a second completion path with no confirm affordance on the card face.

function ProgressRing({ completed, total, color }: { completed: number; total: number; color: string }) {
  const C = 88; // circumference for r=14 (2*pi*14 ≈ 87.96) — matches mockup's rounded value
  const pct = total > 0 ? completed / total : 0;
  const offset = total > 0 ? C * (1 - pct) : C;
  return (
    <div style={{ width:34, height:34, position:'relative', flexShrink:0 }}>
      <svg width={34} height={34} style={{ transform:'rotate(-90deg)' }}>
        <circle cx={17} cy={17} r={14} fill="none" stroke="var(--border-subtle)" strokeWidth={3} />
        <circle cx={17} cy={17} r={14} fill="none" stroke={color} strokeWidth={3}
          strokeDasharray={C} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      <span style={{
        position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:'0.56em', fontWeight:700, fontFamily:"'JetBrains Mono', monospace", color:'var(--text-secondary)',
      }}>
        {total > 0 ? `${completed}/${total}` : '—'}
      </span>
    </div>
  );
}

// Same status vocabulary + exact colors as Smart View's own legend (buttonAnimations.css
// .kpi-hover-tip block / ScheduleTab's legend render) — not the mockup's slightly different
// rgba/hex values, so Cards visually matches Smart View rather than the raw mockup.
const CARD_DOT_STYLE: Record<CellState, { bg:string; border:string; glyph:string; glyphColor:string }> = {
  completed: { bg:'rgba(16,185,129,0.16)', border:'rgba(16,185,129,0.55)', glyph:'✓', glyphColor:'#34d399' },
  due:       { bg:'rgba(59,130,246,0.12)',  border:'#3b82f6',               glyph:'◎', glyphColor:'#60a5fa' },
  scheduled: { bg:'rgba(245,158,11,0.09)',  border:'rgba(245,158,11,0.45)', glyph:'',  glyphColor:'' },
  missed:    { bg:'rgba(239,68,68,0.13)',   border:'rgba(239,68,68,0.55)',  glyph:'✕', glyphColor:'#f87171' },
  empty:     { bg:'rgba(255,255,255,0.025)',border:'var(--border-subtle)',  glyph:'',  glyphColor:'' },
};

function CardMonthDot({ state, isSelected, onClick }: { state: CellState; isSelected: boolean; onClick?: () => void }) {
  const s = CARD_DOT_STYLE[state];
  // Same jello-bounce + JS brightness hover treatment as Smart View's CellStatus — inline
  // `background` always beats an external :hover rule, so brightness is done in JS here too.
  const brightenOn = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.filter = 'brightness(1.4)'; };
  const brightenOff = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.filter = ''; };
  return (
    <div
      className={`combo-jello-spin${state === 'due' ? ' tcard-dot-due' : ''}`}
      onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}
      onMouseEnter={brightenOn}
      onMouseLeave={brightenOff}
      title={state === 'empty' ? 'No service scheduled' : undefined}
      style={{
        flex:1, height:22, borderRadius:6, background:s.bg, border:`1.5px solid ${s.border}`,
        display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:700,
        color:s.glyphColor, cursor: onClick ? 'pointer' : 'default',
        outline: isSelected ? '2px solid rgba(255,255,255,0.6)' : undefined, outlineOffset: isSelected ? 1 : undefined,
      }}
    >{s.glyph}</div>
  );
}

interface ComplianceCardsViewProps {
  tasks: ScTask[];
  year: number;
  nowMonth: number;
  onYearChange: React.Dispatch<React.SetStateAction<number>>;
  selectedMonths: Record<number, number | null>;
  onCellClick: (task: ScTask, month: number) => void;
  onOpenPanel: (taskId: number) => void;
  onAddTask: () => void;
  onEditTask: (task: ScTask) => void;
  onDeleteTask: (task: ScTask) => void;
  onExportPDF: () => void;
  onAiSummary: () => void;
  isEnterprise: boolean;
}

const CARDS_NEXTDUE_STYLE: Record<'ok'|'soon'|'warn'|'late', { bg:string; color:string }> = {
  ok:   { bg:'rgba(16,185,129,0.10)', color:'#34d399' },
  soon: { bg:'rgba(59,130,246,0.13)', color:'#60a5fa' },
  warn: { bg:'rgba(245,158,11,0.13)', color:'#fbbf24' },
  late: { bg:'rgba(239,68,68,0.14)',  color:'#f87171' },
};

function ComplianceCardsView({ tasks, year, nowMonth, onYearChange, selectedMonths, onCellClick, onOpenPanel, onAddTask, onEditTask, onDeleteTask, onExportPDF, onAiSummary, isEnterprise }: ComplianceCardsViewProps) {
  const [cardsFilter, setCardsFilter] = useState<'all' | 'attn' | 'complete'>('all');
  const mo = (m: number) => (MONTH_FULL_LABELS[m - 1] ?? '').slice(0, 3).toUpperCase();

  const cards = useMemo(() => {
    return tasks.map(task => {
      const firstDueDate = task.first_due_date ? new Date(task.first_due_date) : null;
      const occMonths = firstDueDate ? getOccurrenceMonths(firstDueDate, task.frequency, year) : [];
      const completedSet = new Set<number>(task.completions);
      const nextDue = getNextDueMonth(occMonths, completedSet, nowMonth);
      const monthStates: CellState[] = Array.from({ length:12 }, (_, i) =>
        getCellStateLocal(i + 1, occMonths, completedSet, nowMonth, nextDue));
      const completedCount = occMonths.filter(m => completedSet.has(m)).length;
      const totalCount = occMonths.length;
      const hasMissed = monthStates.includes('missed');
      const hasDueNow = monthStates.includes('due');
      const attn = hasMissed || hasDueNow;

      let pillVariant: 'ok' | 'soon' | 'warn' | 'late';
      let pillLabel: string;
      let ringColor: string;
      if (hasMissed) {
        const firstMissedMonth = monthStates.findIndex(s => s === 'missed') + 1;
        pillVariant = 'late'; pillLabel = `✕ ${mo(firstMissedMonth)} missed`; ringColor = '#f87171';
      } else if (hasDueNow) {
        pillVariant = 'soon'; pillLabel = `◎ Due ${mo(nowMonth)}`; ringColor = '#60a5fa';
      } else {
        ringColor = '#34d399';
        const upcoming = occMonths.filter(m => m > nowMonth && !completedSet.has(m));
        if (upcoming.length > 0) {
          pillVariant = 'warn'; pillLabel = `◔ Next ${mo(upcoming[0])}`;
        } else if (firstDueDate) {
          // Nothing left this year — look ahead into future years for the next occurrence,
          // same search pattern as computeNextDueDisplay but anchored at the browsed `year`
          // (not always CURR_YEAR) so this stays correct while paging through past/future years.
          let found: { yr: number; month: number } | null = null;
          for (let yr = year + 1; yr <= year + 6; yr++) {
            const occ = getOccurrenceMonths(firstDueDate, task.frequency, yr);
            if (occ.length > 0) { found = { yr, month: occ[0] }; break; }
          }
          pillVariant = 'ok';
          pillLabel = found ? `✓ Next ${mo(found.month)} '${String(found.yr).slice(2)}` : '✓ Not scheduled';
        } else {
          pillVariant = 'ok'; pillLabel = '✓ Not scheduled';
        }
      }

      return { task, monthStates, completedCount, totalCount, attn, pillVariant, pillLabel, ringColor };
    });
  }, [tasks, year, nowMonth]);

  const filteredCards = cards.filter(c => cardsFilter === 'all' || (cardsFilter === 'attn' ? c.attn : !c.attn));

  return (
    <div className="pm-scale-16">
      {/* Toolbar */}
      <div style={{
        background:'rgba(255,255,255,0.045)', backdropFilter:'blur(16px)', WebkitBackdropFilter:'blur(16px)', border:'1px solid var(--border-subtle)',
        borderRadius:'var(--radius-lg)', padding:'14px 18px', display:'flex', alignItems:'center', gap:8,
        flexWrap:'wrap', marginBottom:14,
      }}>
        <button className="btn combo5-hero" onClick={onAddTask} onMouseLeave={spinIconOut}>
          <Plus size={14} className="btn-icon"/> Add Task
        </button>
        <div style={{ flex:1 }} />
        {/* Segmented filter — mockup's .seg, not the page's .pill-tab-container (that's the
            Smart View/Timeline/Cards switcher itself, a visually distinct control). */}
        <div style={{ display:'inline-flex', border:'1px solid var(--border-default)', borderRadius:8, overflow:'hidden' }}>
          {([{ id:'all', label:'All tasks' }, { id:'attn', label:'Needs attention' }, { id:'complete', label:'Complete' }] as const).map(seg => (
            <button
              key={seg.id}
              onClick={() => setCardsFilter(seg.id)}
              style={{
                background: cardsFilter === seg.id ? 'rgba(59,130,246,0.14)' : 'transparent',
                border:'none', color: cardsFilter === seg.id ? '#93c5fd' : 'var(--text-muted)',
                padding:'7px 13px', fontSize:'0.78em', fontWeight: cardsFilter === seg.id ? 600 : 400, cursor:'pointer',
              }}
            >{seg.label}</button>
          ))}
        </div>
        {/* Year switcher — same shared .year-nav class as Smart View/Timeline, not rebuilt */}
        <div className="year-nav">
          <button onClick={() => onYearChange(y => y - 1)} title="Previous year">‹</button>
          <span className="yr">{year}</span>
          <button onClick={() => onYearChange(y => y + 1)} title="Next year">›</button>
        </div>
        <button className="btn btn-ghost combo1" onClick={onExportPDF} onMouseLeave={spinIconOut}>
          <Download size={14} className="btn-icon"/> Export PDF
        </button>
        {isEnterprise && (
          <button className="btn combo4-ai" onClick={onAiSummary} onMouseLeave={spinIconOut}>
            <Settings2 size={14} className="btn-icon"/> AI Summary
          </button>
        )}
      </div>

      {filteredCards.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:'var(--text-muted)' }}>
          No tasks in this filter.
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(330px, 1fr))', gap:14 }}>
          {filteredCards.map(({ task, monthStates, completedCount, totalCount, attn, pillVariant, pillLabel, ringColor }) => {
            const freqStyle = FREQ_COLORS[task.frequency] ?? { bg:'rgba(107,114,128,0.15)', color:'#6b7280' };
            const nd = CARDS_NEXTDUE_STYLE[pillVariant];
            return (
              <div key={task.id} style={{
                background:'rgba(255,255,255,0.045)', backdropFilter:'blur(16px)', WebkitBackdropFilter:'blur(16px)',
                border: attn ? '1px solid rgba(59,130,246,0.35)' : '1px solid var(--border-subtle)',
                borderRadius:'var(--radius-lg)', padding:18, display:'flex', flexDirection:'column', gap:13,
                position:'relative', overflow:'hidden', transition:'border-color 0.18s, transform 0.18s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = attn ? 'rgba(59,130,246,0.35)' : 'var(--border-subtle)'; e.currentTarget.style.transform = ''; }}
              >
                {attn && (
                  <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:'linear-gradient(90deg, var(--accent-primary), transparent)' }} />
                )}
                {/* Header — click opens the sliding detail panel, same as Smart View's row click */}
                <div style={{ display:'flex', alignItems:'flex-start', gap:12, cursor:'pointer' }} onClick={() => onOpenPanel(task.id)}>
                  <div style={{ width:42, height:42, borderRadius:11, background:'rgba(255,255,255,0.05)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:21, flexShrink:0 }}>
                    {task.icon}
                  </div>
                  <div style={{ minWidth:0, flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:'0.94em', color:'var(--text-primary)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{task.name}</div>
                    <div style={{ fontSize:'0.72em', color:'var(--text-muted)', marginTop:1, display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                      {task.vendor_name && <span>{task.vendor_name}</span>}
                      <span style={{ padding:'2px 8px', borderRadius:99, fontSize:'0.62em', fontWeight:600, whiteSpace:'nowrap', background:freqStyle.bg, color:freqStyle.color }}>{task.frequency}</span>
                    </div>
                  </div>
                  <ProgressRing completed={completedCount} total={totalCount} color={ringColor} />
                </div>

                {/* 12-dot month strip */}
                <div>
                  <div style={{ display:'flex', gap:5, justifyContent:'space-between' }}>
                    {monthStates.map((state, i) => (
                      <CardMonthDot
                        key={i}
                        state={state}
                        isSelected={selectedMonths[task.id] === i + 1}
                        onClick={state === 'empty' ? undefined : () => onCellClick(task, i + 1)}
                      />
                    ))}
                  </div>
                  <div style={{ display:'flex', gap:5, marginTop:4 }}>
                    {MO_LABELS.map((m, i) => (
                      <span key={i} style={{ flex:1, textAlign:'center', fontSize:'0.55em', color:'var(--text-muted)', fontFamily:"'JetBrains Mono', monospace" }}>{m}</span>
                    ))}
                  </div>
                </div>

                {/* Footer */}
                <div style={{ display:'flex', alignItems:'center', gap:8, borderTop:'1px solid var(--border-subtle)', paddingTop:12 }}>
                  <span style={{
                    display:'inline-flex', alignItems:'center', gap:5, fontSize:'0.68em', fontWeight:600,
                    padding:'3px 9px', borderRadius:99, whiteSpace:'nowrap', fontFamily:"'JetBrains Mono', monospace",
                    background:nd.bg, color:nd.color,
                  }}>{pillLabel}</span>
                  <div style={{ marginLeft:'auto', display:'flex', gap:3 }}>
                    <button onClick={() => onEditTask(task)} className="combo-jello-spin" data-tint="neutral" onMouseLeave={spinIconOut} style={schActionBtn} title="Edit">
                      <Pencil size={13} className="btn-icon"/>
                    </button>
                    <button onClick={() => onDeleteTask(task)} className="combo3-danger" onMouseLeave={spinIconOut}
                      style={{ borderRadius:4, padding:'4px 5px', cursor:'pointer', display:'flex', alignItems:'center' }} title="Delete">
                      <Trash2 size={13} className="btn-icon"/>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Compliance PM: Task Detail page ────────────────────────────────────────
// Ported from mockups/compliance-task-detail-redesign.html Option C (id="view-c", the "Full
// Takeover Page"). Replaces the old sliding detail panel as the shared view/edit surface for
// a task — opens from Smart View's row/edit-icon click, Cards' card-header click, and
// Timeline's agenda item click, all via the same panelTaskId state (see the invocation site in
// ScheduleTab's return). No new API calls: every handler here is a prop passed down from
// ScheduleTab, reusing the exact same functions the old panel called (handleMarkComplete,
// doUploadReport, handleAnalyzeReport, openAnalysisModal, etc.) — this page only rearranges
// their presentation into the mockup's 3-column layout, it doesn't reimplement any of them.

const DETAIL_CHIP_STYLES: Record<'g'|'b'|'a'|'r', { bg:string; color:string }> = {
  g: { bg:'rgba(16,185,129,0.12)', color:'#34d399' },
  b: { bg:'rgba(59,130,246,0.13)', color:'#60a5fa' },
  a: { bg:'rgba(245,158,11,0.13)', color:'#fbbf24' },
  r: { bg:'rgba(239,68,68,0.13)',  color:'#f87171' },
};

function DetailChip({ variant, children }: { variant: 'g'|'b'|'a'|'r'; children: React.ReactNode }) {
  const s = DETAIL_CHIP_STYLES[variant];
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:5, fontSize:'0.68em', fontWeight:600,
      padding:'3px 10px', borderRadius:99, fontFamily:"'JetBrains Mono', monospace", whiteSpace:'nowrap',
      background:s.bg, color:s.color,
    }}>{children}</span>
  );
}

// Shared AI insights card shell — mockups/compliance-task-detail-redesign.html's .aicard,
// reused by both the main analysis summary and the QAPI-suggestion nudge below it (the mockup
// itself reuses this one class across its Option B and C views for the same reason). Colors
// match this app's own combo4-ai AI-branding palette (buttonAnimations.css: border/background
// rgba(139,92,246,...), text #a78bfa) rather than the mockup's slightly different literal
// purple, so it reads as the same "AI feature" as every other AI button on this page.
function AiInsightCard({ title, tag, children }: { title: string; tag?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{
      border:'1px solid rgba(139,92,246,0.3)',
      background:'linear-gradient(135deg, rgba(139,92,246,0.10), rgba(59,130,246,0.06))',
      borderRadius:12, padding:'14px 16px', display:'flex', flexDirection:'column', gap:8,
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.78em', fontWeight:700, color:'#a78bfa' }}>
        <span>✦ {title}</span>
        {tag && <span style={{ marginLeft:'auto' }}>{tag}</span>}
      </div>
      {children}
    </div>
  );
}

function DetailSect({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.68em', fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:'var(--text-muted)' }}>
      <span>{children}</span>
      <span style={{ flex:1, height:1, background:'var(--border-subtle)' }} />
    </div>
  );
}

function DetailCard({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      background:'rgba(255,255,255,0.045)', backdropFilter:'blur(16px)', WebkitBackdropFilter:'blur(16px)', border:'1px solid var(--border-subtle)',
      borderRadius:'var(--radius-lg)', padding:'17px 18px', display:'flex', flexDirection:'column', gap:13,
    }}>{children}</div>
  );
}

// AI insights — computed entirely from the real analysis_json already stored on the task's
// most recent report (from the existing POST /scheduled-tasks/:id/report/:reportId/analyze
// endpoint). Never fabricates prose: the summary lines below are assembled from the actual
// structured equipment/inspector/recommendations fields the AI already extracted, not invented
// narrative text like the mockup's placeholder copy.
function TaskAiInsights({ task, latestReport, isEnterprise, isPro, getAnalysisCount, proMonthlyLimit, onAnalyzeReport, onOpenAnalysisModal }: {
  task: ScTask;
  latestReport: ScTask['reports'][number] | null;
  isEnterprise: boolean; isPro: boolean;
  getAnalysisCount: () => number; proMonthlyLimit: number;
  onAnalyzeReport: (reportId: number, reportName: string, force: boolean) => void;
  onOpenAnalysisModal: (analysis: any, reportName: string | null) => void;
}) {
  void task;
  const canRunAi = isEnterprise || isPro;
  const atLimit = isPro && !isEnterprise && getAnalysisCount() >= proMonthlyLimit;

  if (!latestReport) {
    return (
      <AiInsightCard title="AI insights">
        <p style={{ fontSize:'0.78em', color:'var(--text-secondary)', lineHeight:1.55, margin:0 }}>
          No reports uploaded yet — attach a service report to run AI analysis on it.
        </p>
      </AiInsightCard>
    );
  }

  if (!latestReport.analysis_json) {
    return (
      <AiInsightCard title="AI insights" tag={<span style={{ fontSize:'0.68em', color:'var(--text-muted)', fontWeight:500 }}>{formatTaskDate(latestReport.uploaded_at)} report</span>}>
        <p style={{ fontSize:'0.78em', color:'var(--text-secondary)', lineHeight:1.55, margin:0 }}>
          The most recent report hasn't been analyzed yet.
        </p>
        {canRunAi ? (
          <button className="btn combo4-ai" onMouseLeave={spinIconOut} disabled={atLimit}
            style={{ alignSelf:'flex-start', opacity: atLimit ? 0.5 : 1, cursor: atLimit ? 'not-allowed' : 'pointer' }}
            onClick={() => { if (!atLimit) onAnalyzeReport(latestReport.id, latestReport.label || latestReport.report_name || 'Report', false); }}>
            <span className="btn-icon">✦</span> {atLimit ? `Monthly limit reached (${proMonthlyLimit}/mo)` : 'Analyze latest report'}
          </button>
        ) : (
          <span style={{ fontSize:'0.72em', color:'var(--text-muted)' }}>🔒 Pro or Enterprise feature</span>
        )}
      </AiInsightCard>
    );
  }

  const analysis = latestReport.analysis_json;
  const equip: any[] = Array.isArray(analysis.equipment) ? analysis.equipment : [];
  const defs: any[] = Array.isArray(analysis.deficiencies) ? analysis.deficiencies : [];
  const recs: string[] = Array.isArray(analysis.recommendations) ? analysis.recommendations : [];
  // Case-insensitive: the analyze prompt asks the AI to normalize condition to lowercase
  // "pass"/"fail", but cached analysis_json from older analyses (before that instruction was
  // added) can still hold raw values like "PASS" — matching case-insensitively keeps this
  // summary accurate for reports analyzed before or after that prompt change.
  const passCount = equip.filter(e => String(e.condition ?? '').toLowerCase() === 'pass').length;
  const failCount = equip.filter(e => String(e.condition ?? '').toLowerCase() === 'fail').length;

  return (
    <>
      <AiInsightCard title={`Latest report — ${formatTaskDate(latestReport.uploaded_at)}`}>
        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
          {equip.length > 0 && (
            <p style={{ fontSize:'0.78em', color:'var(--text-secondary)', lineHeight:1.55, margin:0 }}>
              {passCount}/{equip.length} device{equip.length === 1 ? '' : 's'} tested and passing{failCount > 0 ? `, ${failCount} failed` : ''}.
            </p>
          )}
          {analysis.inspector?.name && (
            <p style={{ fontSize:'0.78em', color:'var(--text-secondary)', lineHeight:1.55, margin:0 }}>
              Inspected by {analysis.inspector.name}{analysis.inspector.date ? ` on ${analysis.inspector.date}` : ''}.
            </p>
          )}
          {recs.slice(0, 2).map((r, i) => (
            <p key={i} style={{ fontSize:'0.78em', color:'var(--text-secondary)', lineHeight:1.55, margin:0 }}>{r}</p>
          ))}
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:2, alignItems:'center' }}>
          {defs.length > 0
            ? <DetailChip variant="a">⚠ {defs.length} deficienc{defs.length === 1 ? 'y' : 'ies'}</DetailChip>
            : <DetailChip variant="g">✓ No deficiencies</DetailChip>}
          <button onClick={() => onOpenAnalysisModal(analysis, latestReport.label || latestReport.report_name)}
            style={{ fontSize:'0.72em', color:'#a78bfa', background:'none', border:'none', cursor:'pointer', fontWeight:600, padding:0 }}>
            View full analysis →
          </button>
        </div>
      </AiInsightCard>
      {defs.length > 0 && (
        <AiInsightCard title="QAPI suggestion">
          <p style={{ fontSize:'0.74em', color:'var(--text-secondary)', lineHeight:1.5, margin:0 }}>
            {defs.length} deficienc{defs.length === 1 ? 'y' : 'ies'} found on the latest report — review and create QAPI tasks from the full analysis.
          </p>
          <button className="btn combo4-ai" onMouseLeave={spinIconOut} style={{ alignSelf:'flex-start', fontSize:'0.74em', padding:'6px 12px' }}
            onClick={() => onOpenAnalysisModal(analysis, latestReport.label || latestReport.report_name)}>
            <span className="btn-icon">＋</span> Review deficiencies
          </button>
        </AiInsightCard>
      )}
    </>
  );
}

// Documents list row — same reports functionality the old sliding panel had (AI analyze/
// re-analyze/view-cached-analysis, remove-with-confirm, editable service date), just restyled
// to fit a card instead of a flat panel list. Every handler is a prop; no new logic here.
function DetailReportRow({ report, isEnterprise, isPro, getAnalysisCount, proMonthlyLimit, onAnalyzeReport, onOpenAnalysisModal, confirmRemove, onRequestRemove, onCancelRemove, onDelete, serviceEditing, serviceEditValue, onStartServiceEdit, onCancelServiceEdit, onServiceEditValueChange, onSaveServiceDate }: {
  report: ScTask['reports'][number];
  isEnterprise: boolean; isPro: boolean;
  getAnalysisCount: () => number; proMonthlyLimit: number;
  onAnalyzeReport: (force: boolean) => void;
  onOpenAnalysisModal: () => void;
  confirmRemove: boolean; onRequestRemove: () => void; onCancelRemove: () => void; onDelete: () => void;
  serviceEditing: boolean; serviceEditValue: string; onStartServiceEdit: () => void; onCancelServiceEdit: () => void;
  onServiceEditValueChange: (v: string) => void; onSaveServiceDate: () => void;
}) {
  const r = report;
  const isDocx = /\.docx?($|\?)/i.test(r.report_name ?? '') || /\.docx?($|\?)/i.test(r.report_url ?? '');
  const isReportPdf = !isDocx && (
    /\.pdf($|\?)/i.test(r.report_name ?? '') ||
    /\.pdf($|\?)/i.test(r.report_url ?? '') ||
    (r.report_url ?? '').includes('proqure/reports') ||
    (r.report_url ?? '').includes('proqure/contracts') ||
    (r.report_url ?? '').includes('proqure/vendor-docs'));
  const label = r.label || r.report_name || `Report — ${new Date(r.uploaded_at).toLocaleDateString('en-US', { month:'short', year:'numeric', timeZone:'UTC' })}`;

  return (
    // 3 stacked rows, always in this order regardless of which buttons a given report has:
    // (1) title/filename + date, (2) action buttons, (3) service date + Edit date. Previously
    // rows 1 and 2 were crammed into a single flex-wrap row alongside the date; now consistently
    // split so every entry has the same shape whether it has zero, one, or all three action
    // buttons available.
    <div style={{ display:'flex', flexDirection:'column', gap:8, padding:'10px 0', borderBottom:'1px solid var(--border-subtle)' }}>
      {/* Row 1 — title/filename (+ date, when it fits on the same line) */}
      <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.78em', color:'var(--text-secondary)' }}>
        <span style={{ flexShrink:0 }}>📎</span>
        {isReportPdf ? (
          <PdfViewerButton pdfUrl={getProxyUrl(r.report_url!)} label={label} />
        ) : (
          <a href={r.report_url} target="_blank" rel="noreferrer" style={{ flex:1, color:'var(--text-primary)', textDecoration:'none', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{label}</a>
        )}
        <span style={{ flexShrink:0, color:'var(--text-muted)' }}>
          {new Date(r.uploaded_at).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric', timeZone:'UTC' })}
        </span>
      </div>

      {/* Row 2 — action buttons (Analyze/Re-analyze, View Analysis, Remove — whichever apply).
          Sizing matches the page's standard buttons (Mark completed / Upload report use .btn's
          own 8px 16px / 0.875rem-equivalent), scaled down for a row-level action rather than
          the tiny 1-3px-padding / 0.65-0.7em these used to be. */}
      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', paddingLeft:20 }}>
        {isEnterprise ? (
          <button type="button" onClick={() => onAnalyzeReport(!!r.analysis_json)}
            style={{ background:'none', border:'1px solid #7c3aed', borderRadius:6, cursor:'pointer', color:'#7c3aed', padding:'5px 10px', flexShrink:0, fontSize:'0.75em', fontWeight:500, display:'flex', alignItems:'center', gap:6 }}
            title={r.analysis_json ? 'Re-analyze (force fresh)' : 'AI Analysis'}>
            {r.analysis_json ? '🔄 Re-analyze' : '🤖 Analyze'}
          </button>
        ) : isPro ? (() => {
          const count = getAnalysisCount();
          const atLimit = count >= proMonthlyLimit;
          return (
            <span style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
              <button type="button" disabled={atLimit} onClick={() => { if (!atLimit) onAnalyzeReport(!!r.analysis_json); }}
                style={{ background:'none', border:`1px solid ${atLimit ? '#334155' : '#7c3aed'}`, borderRadius:6, cursor: atLimit ? 'not-allowed' : 'pointer', color: atLimit ? '#475569' : '#7c3aed', padding:'5px 10px', flexShrink:0, fontSize:'0.75em', fontWeight:500, display:'flex', alignItems:'center', gap:6 }}
                title={atLimit ? `Monthly limit reached (${proMonthlyLimit}/month)` : 'AI Analysis (Pro)'}>
                {r.analysis_json ? '🔄 Re-analyze' : '🤖 Analyze'}
              </button>
              <span style={{ color:'#475569', fontSize:'0.68em', flexShrink:0 }}>({count}/{proMonthlyLimit})</span>
            </span>
          );
        })() : (
          <span title="Pro or Enterprise feature" style={{ flexShrink:0, cursor:'default', color:'#475569', fontSize:'0.75em' }}>🔒</span>
        )}
        {r.analysis_json && (isEnterprise || isPro) && (
          <button type="button" onClick={onOpenAnalysisModal}
            style={{ background:'none', border:'1px solid #10b981', borderRadius:6, cursor:'pointer', color:'#10b981', padding:'5px 10px', flexShrink:0, fontSize:'0.75em', fontWeight:500, display:'flex', alignItems:'center', gap:6 }}
            title="View cached analysis">
            📋 View Analysis
          </button>
        )}
        {confirmRemove ? (
          <span style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
            <span style={{ fontSize:'0.75em', color:'var(--text-secondary)' }}>Remove?</span>
            <button type="button" onClick={onDelete} style={{ background:'#ef4444', border:'none', borderRadius:6, cursor:'pointer', color:'#fff', padding:'5px 12px', fontSize:'0.78em', fontWeight:600 }}>Confirm</button>
            <button type="button" onClick={onCancelRemove} style={{ background:'none', border:'1px solid var(--border-default)', borderRadius:6, cursor:'pointer', color:'var(--text-secondary)', padding:'5px 12px', fontSize:'0.78em' }}>Cancel</button>
          </span>
        ) : (
          <button type="button" onClick={onRequestRemove}
            style={{ background:'none', border:'1px solid rgba(239,68,68,0.3)', borderRadius:6, padding:'5px 12px', cursor:'pointer', color:'#ef4444', display:'flex', alignItems:'center', flexShrink:0, fontSize:'0.78em', fontWeight:500, gap:6 }}
            title="Remove attachment">
            <X size={13}/> Remove
          </button>
        )}
      </div>

      {/* Row 3 — service date + Edit date */}
      {serviceEditing ? (
        <div style={{ display:'flex', alignItems:'center', gap:8, paddingLeft:20 }}>
          <div style={{ width: 170 }}>
            <DatePicker value={serviceEditValue} onChange={v => onServiceEditValueChange(v)} placeholder="Select a date" style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--bg-elevated)', fontSize: '0.78em' }} />
          </div>
          <button type="button" onClick={onSaveServiceDate} style={{ background:'#3b82f6', border:'none', borderRadius:6, cursor:'pointer', color:'#fff', padding:'5px 12px', fontSize:'0.78em', fontWeight:500 }}>Save</button>
          <button type="button" onClick={onCancelServiceEdit} style={{ background:'none', border:'1px solid var(--border-default)', borderRadius:6, cursor:'pointer', color:'var(--text-secondary)', padding:'5px 12px', fontSize:'0.78em' }}>Cancel</button>
        </div>
      ) : (
        <div style={{ display:'flex', alignItems:'center', gap:8, paddingLeft:20 }}>
          <span style={{ fontSize:'0.75em', color: r.service_date ? 'var(--text-secondary)' : 'var(--text-muted)' }}>
            {r.service_date
              ? `Service: ${new Date(r.service_date.slice(0,10)+'T12:00:00Z').toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' })}`
              : <span style={{ fontStyle:'italic' }}>Set service date</span>}
          </span>
          <button type="button" onClick={onStartServiceEdit}
            style={{ background:'none', border:'1px solid var(--border-default)', borderRadius:6, cursor:'pointer', color:'var(--text-secondary)', padding:'5px 10px', fontSize:'0.75em', fontWeight:500, display:'flex', alignItems:'center', gap:6 }}
            title="Edit service date">
            ✏ Edit date
          </button>
        </div>
      )}
    </div>
  );
}

function TimelineItem({ dotColor, dotShadow, last, children }: { dotColor:string; dotShadow:string; last?:boolean; children:React.ReactNode }) {
  return (
    <div style={{ position:'relative', paddingBottom: last ? 0 : 16 }}>
      <span style={{ position:'absolute', left:-20, top:4, width:10, height:10, borderRadius:99, background:dotColor, boxShadow:`0 0 0 3px ${dotShadow}` }} />
      {children}
    </div>
  );
}

// Real chronological history from task.reports (unfiltered by year — the server's reports
// query has no year clause, so this spans every year) plus first_due_date-derived next-due and
// the task's own created_at. No fabricated technician names or per-report "logged by" — those
// fields don't exist in scheduled_task_reports, so they're left out rather than invented.
function TaskTimeline({ task, sortedReports, nextDueDisplay }: { task: ScTask; sortedReports: ScTask['reports']; nextDueDisplay: { label:string; color:string } }) {
  const hasUpcoming = nextDueDisplay.label !== '—' && nextDueDisplay.label !== 'Not scheduled';
  if (!hasUpcoming && sortedReports.length === 0 && !task.created_at) {
    return <div style={{ fontSize:'0.78em', color:'var(--text-muted)' }}>No history yet.</div>;
  }
  return (
    <div style={{ position:'relative', paddingLeft:22 }}>
      <div style={{ position:'absolute', left:7, top:6, bottom:6, width:2, background:'var(--border-subtle)' }} />
      {hasUpcoming && (
        <TimelineItem dotColor="var(--accent-primary)" dotShadow="rgba(59,130,246,0.2)">
          <div style={{ fontSize:'0.82em', fontWeight:600, color:'var(--text-primary)' }}>
            Next inspection <span style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:'0.7em', color:'var(--text-muted)', marginLeft:6 }}>{nextDueDisplay.label}</span>
          </div>
          <div style={{ fontSize:'0.72em', color:'var(--text-secondary)', marginTop:2 }}>Auto-scheduled from {task.frequency.toLowerCase()} frequency</div>
        </TimelineItem>
      )}
      {sortedReports.map(r => (
        <TimelineItem key={r.id} dotColor="var(--accent-success)" dotShadow="rgba(16,185,129,0.18)">
          <div style={{ fontSize:'0.82em', fontWeight:600, color:'var(--text-primary)' }}>
            Completed <span style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:'0.7em', color:'var(--text-muted)', marginLeft:6 }}>
              {new Date((r.service_date ?? r.uploaded_at).slice(0,10)+'T12:00:00Z').toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' })}
            </span>
          </div>
          <div style={{ fontSize:'0.72em', color:'var(--text-secondary)', marginTop:2 }}>{r.label || r.report_name || 'Service report'}</div>
        </TimelineItem>
      ))}
      {task.created_at && (
        <TimelineItem dotColor="var(--text-muted)" dotShadow="transparent" last>
          <div style={{ fontSize:'0.82em', fontWeight:600, color:'var(--text-primary)' }}>
            Task created <span style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:'0.7em', color:'var(--text-muted)', marginLeft:6 }}>
              {new Date(task.created_at).toLocaleDateString('en-US', { month:'short', year:'numeric', timeZone:'UTC' })}
            </span>
          </div>
        </TimelineItem>
      )}
    </div>
  );
}

interface TaskDetailPageProps {
  task: ScTask;
  vendor: VendorFull | null;
  facilityName: string;
  year: number;
  nowMonth: number;
  selectedMonth: number | null;
  onCellClick: (month: number) => void;
  onClose: () => void;
  onMarkComplete: () => void;
  onAttachReportClick: () => void;
  scheduleOpen: boolean;
  scheduleDate: string;
  onScheduleDateChange: (v: string) => void;
  onOpenSchedule: () => void;
  onCloseSchedule: () => void;
  onConfirmSchedule: () => void;
  onEditTask: () => void;
  onDeleteTask: () => void;
  isEnterprise: boolean;
  isPro: boolean;
  getAnalysisCount: () => number;
  proMonthlyLimit: number;
  onAnalyzeReport: (reportId: number, reportName: string, force: boolean) => void;
  onOpenAnalysisModal: (analysis: any, reportName: string | null) => void;
  onDeleteReport: (reportId: number) => void;
  confirmRemoveReportId: number | null;
  onRequestRemoveReport: (reportId: number) => void;
  onCancelRemoveReport: () => void;
  serviceEditId: number | null;
  serviceEditValue: string;
  onServiceEditIdChange: (id: number | null) => void;
  onServiceEditValueChange: (v: string) => void;
  onSaveServiceDate: (reportId: number) => void;
  facilityId: number;
  onOpenVendor: (vendorId: number) => void;
}

function TaskDetailPage(props: TaskDetailPageProps) {
  const {
    task, vendor, facilityName, year, nowMonth, selectedMonth, onCellClick, onClose, onMarkComplete,
    onAttachReportClick,
    scheduleOpen, scheduleDate, onScheduleDateChange, onOpenSchedule, onCloseSchedule, onConfirmSchedule,
    onEditTask, onDeleteTask, isEnterprise, isPro, getAnalysisCount, proMonthlyLimit,
    onAnalyzeReport, onOpenAnalysisModal, onDeleteReport, confirmRemoveReportId, onRequestRemoveReport, onCancelRemoveReport,
    serviceEditId, serviceEditValue, onServiceEditIdChange, onServiceEditValueChange, onSaveServiceDate, facilityId, onOpenVendor,
  } = props;

  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const firstDueDate = task.first_due_date ? new Date(task.first_due_date) : null;
  const occMonths = firstDueDate ? getOccurrenceMonths(firstDueDate, task.frequency, year) : [];
  const completedSet = new Set<number>(task.completions);
  const nextDue = getNextDueMonth(occMonths, completedSet, nowMonth);
  const monthStates: CellState[] = Array.from({ length:12 }, (_, i) => getCellStateLocal(i + 1, occMonths, completedSet, nowMonth, nextDue));
  const completedCount = occMonths.filter(m => completedSet.has(m)).length;
  const totalCount = occMonths.length;
  const missedCount = monthStates.filter(s => s === 'missed').length;
  const hasDueNow = monthStates.includes('due');
  const nextDueDisplay = computeNextDueDisplay(task);

  const isSelScheduled = selectedMonth !== null && occMonths.includes(selectedMonth);
  const isSelCompleted = selectedMonth !== null && task.completions.includes(selectedMonth);
  const markDisabled = !selectedMonth || !isSelScheduled;

  const freqStyle = FREQ_COLORS[task.frequency] ?? { bg:'rgba(107,114,128,0.15)', color:'#6b7280' };
  const sortedReports = [...task.reports].sort((a, b) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime());
  const latestReport = sortedReports[0] ?? null;

  const menuItemStyle: React.CSSProperties = {
    display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:6, border:'none',
    background:'transparent', color:'var(--text-secondary)', fontSize:'0.8em', cursor:'pointer', textAlign:'left',
  };

  return ReactDOM.createPortal(
    <div className="pm-scale-16" style={{
      position:'fixed', inset:0, zIndex:500, overflowY:'auto', background:'var(--bg-base)',
      animation:'pageTakeoverRiseIn 0.3s cubic-bezier(0.25,1,0.35,1)',
    }}>
      <div style={{ maxWidth:1240, margin:'0 auto', padding:'24px 30px 90px' }}>
        {/* Same bordered back-button component as ItemDetail.tsx's "← Inventory" —
            className="btn btn-ghost btn-sm" + lucide ArrowLeft, not a bare text link. Matches
            ItemDetail.tsx's usage exactly (no fontSize override), consistent with this page's
            other hero buttons (Mark completed/Upload report/Ask AI), which also don't override
            .btn's default size. */}
        <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ gap:6 }}>
          <ArrowLeft size={14} /> Back to Compliance PM
        </button>

        {/* Hero */}
        <div style={{ display:'flex', alignItems:'center', gap:16, margin:'14px 0 20px', flexWrap:'wrap' }}>
          <div style={{ width:58, height:58, borderRadius:15, background:'rgba(255,255,255,0.06)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:29, flexShrink:0 }}>
            {task.icon}
          </div>
          <div>
            <div style={{ fontSize:'1.5em', fontWeight:700, color:'var(--text-primary)' }}>{task.name}</div>
            <div style={{ fontSize:'0.74em', color:'var(--text-muted)', marginTop:4, display:'flex', alignItems:'center', gap:7, flexWrap:'wrap' }}>
              <span style={{ padding:'2px 8px', borderRadius:99, fontSize:'0.84em', fontWeight:600, whiteSpace:'nowrap', background:freqStyle.bg, color:freqStyle.color }}>{task.frequency}</span>
              {facilityName && <><span>·</span><span>{facilityName}</span></>}
              {task.vendor_name && <><span>·</span><span>{task.vendor_name}</span></>}
              <span>·</span>
              {missedCount > 0 ? <DetailChip variant="r">{missedCount} missed</DetailChip>
                : hasDueNow ? <DetailChip variant="b">Due now</DetailChip>
                : <DetailChip variant="g">✓ On track</DetailChip>}
            </div>
          </div>
          <div style={{ marginLeft:'auto', display:'flex', gap:9, alignItems:'center', flexWrap:'wrap' }}>
            {/* combo2 (BUTTON_SYSTEM.md's modal-primary-action pulse ring) only while the task
                actually needs action (due or missed) — not a permanent decoration, and not
                shown once the task is on-track. */}
            <button className={`btn btn-primary${(missedCount > 0 || hasDueNow) ? ' combo2' : ''}`} onMouseLeave={spinIconOut}
              disabled={markDisabled}
              title={!selectedMonth ? 'Click a month dot below first' : !isSelScheduled ? 'Not a scheduled month' : isSelCompleted ? 'Click to unmark' : 'Mark as completed'}
              style={{ opacity: markDisabled ? 0.45 : 1, cursor: markDisabled ? 'not-allowed' : 'pointer' }}
              onClick={onMarkComplete}>
              <Check size={14} className="btn-icon"/> {isSelCompleted ? 'Unmark completed' : 'Mark completed'}
            </button>
            <button className="btn btn-ghost combo1" onMouseLeave={spinIconOut} onClick={onAttachReportClick}>
              <FileText size={14} className="btn-icon"/> Upload report
            </button>
            {(isEnterprise || isPro) && latestReport && (
              <button className="btn combo4-ai" onMouseLeave={spinIconOut}
                onClick={() => onAnalyzeReport(latestReport.id, latestReport.label || latestReport.report_name || 'Report', !!latestReport.analysis_json)}>
                <span className="btn-icon">✦</span> Ask AI
              </button>
            )}
            <div style={{ position:'relative' }}>
              <button className="combo-jello-spin" data-tint="neutral" onMouseLeave={spinIconOut} onClick={() => setMenuOpen(o => !o)}
                style={{ width:36, height:36, borderRadius:8, border:'1px solid var(--border-default)', background:'transparent', color:'var(--text-muted)', cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center' }}>
                <span className="btn-icon" style={{ fontSize:16 }}>⋯</span>
              </button>
              {menuOpen && (
                <>
                  <div onClick={() => setMenuOpen(false)} style={{ position:'fixed', inset:0, zIndex:8 }} />
                  <div style={{
                    position:'absolute', top:'calc(100% + 6px)', right:0, zIndex:9, minWidth:190,
                    background:'var(--bg-elevated)', border:'1px solid var(--border-default)', borderRadius:10,
                    boxShadow:'var(--shadow-lg)', padding:6, display:'flex', flexDirection:'column', gap:2,
                  }}>
                    <button onClick={() => { setMenuOpen(false); onOpenSchedule(); }} className="combo-jello-spin" data-tint="neutral" onMouseLeave={spinIconOut} style={menuItemStyle}>
                      <Calendar size={13} className="btn-icon"/> Schedule next service
                    </button>
                    <button onClick={() => { setMenuOpen(false); onEditTask(); }} className="combo-jello-spin" data-tint="neutral" onMouseLeave={spinIconOut} style={menuItemStyle}>
                      <Pencil size={13} className="btn-icon"/> Edit task
                    </button>
                    <button onClick={() => { setMenuOpen(false); onDeleteTask(); }} className="combo3-danger" onMouseLeave={spinIconOut} style={menuItemStyle}>
                      <Trash2 size={13} className="btn-icon"/> Delete task
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {scheduleOpen && (
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16, padding:'10px 14px', background:'rgba(255,255,255,0.045)', border:'1px solid var(--border-default)', borderRadius:10, flexWrap:'wrap' }}>
            <span style={{ fontSize:'0.8em', fontWeight:600, color:'var(--text-primary)' }}>Schedule next service:</span>
            <div style={{ width: 170 }}>
              <DatePicker value={scheduleDate} onChange={v => onScheduleDateChange(v)} placeholder="Select a date" style={{ fontSize: '0.8em' }} />
            </div>
            <button className="btn btn-primary" style={{ fontSize:'0.8em' }} disabled={!scheduleDate} onClick={onConfirmSchedule}>Add to calendar</button>
            <button className="btn btn-ghost" style={{ fontSize:'0.8em' }} onClick={onCloseSchedule}>Cancel</button>
          </div>
        )}

        <div className="task-detail-grid" style={{ display:'grid', gap:14, alignItems:'start' }}>
          {/* LEFT column */}
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <DetailCard>
              <DetailSect>Status</DetailSect>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:9 }}>
                <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid var(--border-subtle)', borderRadius:10, padding:'10px 12px' }}>
                  <div style={{ fontSize:'0.62em', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-muted)' }}>Last</div>
                  <div style={{ fontSize:'1em', fontWeight:700, fontFamily:"'JetBrains Mono', monospace", marginTop:3, color:'#34d399' }}>{formatTaskDate(task.last_completed_date)}</div>
                </div>
                <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid var(--border-subtle)', borderRadius:10, padding:'10px 12px' }}>
                  <div style={{ fontSize:'0.62em', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-muted)' }}>Next</div>
                  <div style={{ fontSize:'1em', fontWeight:700, fontFamily:"'JetBrains Mono', monospace", marginTop:3, color:nextDueDisplay.color }}>{nextDueDisplay.label}</div>
                </div>
                <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid var(--border-subtle)', borderRadius:10, padding:'10px 12px' }}>
                  <div style={{ fontSize:'0.62em', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-muted)' }}>YTD</div>
                  <div style={{ fontSize:'1em', fontWeight:700, fontFamily:"'JetBrains Mono', monospace", marginTop:3, color:'var(--text-primary)' }}>{totalCount > 0 ? `${completedCount}/${totalCount}` : '—'}</div>
                </div>
              </div>
              <div>
                <div style={{ display:'flex', gap:5 }}>
                  {monthStates.map((state, i) => (
                    <CardMonthDot key={i} state={state} isSelected={selectedMonth === i + 1} onClick={state === 'empty' ? undefined : () => onCellClick(i + 1)} />
                  ))}
                </div>
                <div style={{ display:'flex', gap:5, marginTop:4 }}>
                  {MO_LABELS.map((m, i) => <span key={i} style={{ flex:1, textAlign:'center', fontSize:'0.55em', color:'var(--text-muted)', fontFamily:"'JetBrains Mono', monospace" }}>{m}</span>)}
                </div>
                <div style={{ fontSize:'0.72em', color: selectedMonth ? '#60a5fa' : 'var(--text-muted)', marginTop:8 }}>
                  {selectedMonth ? `Acting on: ${MONTH_FULL_LABELS[selectedMonth - 1]} ${year}` : '↑ Click a month dot to select, then Mark completed'}
                </div>
              </div>
            </DetailCard>

            <DetailCard>
              <DetailSect>Vendor</DetailSect>
              {task.vendor_name ? (
                // Clicking anywhere on the card (or the icon specifically) opens the Vendor
                // Detail takeover in-place, instead of navigating to /vendors and losing the
                // task context (item 4).
                <div
                  style={{ display:'flex', alignItems:'center', gap:12, cursor: vendor ? 'pointer' : 'default' }}
                  onClick={() => { if (vendor) onOpenVendor(vendor.id); }}
                >
                  <div style={{ width:38, height:38, borderRadius:10, background:'rgba(239,68,68,0.15)', color:'#f87171', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:'0.82em', flexShrink:0 }}>
                    {task.vendor_name.slice(0, 2).toUpperCase()}
                  </div>
                  <div style={{ minWidth:0, flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:'0.84em', color:'var(--text-primary)' }}>{task.vendor_name}</div>
                    <div style={{ fontSize:'0.7em', color:'var(--text-muted)', marginTop:1 }}>
                      {[vendor?.contact_name, vendor?.contact_phone ?? vendor?.vendor_phone].filter(Boolean).join(' · ') || 'No contact on file'}
                      {vendor?.contract_expiry_date && <><br/>Contract thru {formatTaskDate(vendor.contract_expiry_date)}</>}
                    </div>
                    {task.vendor_2 && <div style={{ fontSize:'0.7em', color:'var(--text-muted)', marginTop:2 }}>Also: {task.vendor_2}</div>}
                  </div>
                  {vendor && (
                    // Mockup's Vendor card has three icon buttons — call, email, external link
                    // (item 1). Same tel:/mailto: pattern used elsewhere in the app (Vendors.tsx,
                    // VendorDetailTakeover above); stopPropagation so they don't also trigger the
                    // card's own onClick (which opens the Vendor Detail takeover).
                    <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                      {(vendor.contact_phone ?? vendor.vendor_phone) && (
                        <a href={`tel:${vendor.contact_phone ?? vendor.vendor_phone}`} className="combo-jello-spin" data-tint="green" onMouseLeave={spinIconOut}
                          onClick={e => e.stopPropagation()}
                          style={{ width:28, height:28, borderRadius:7, border:'1px solid var(--border-default)', background:'transparent', color:'var(--text-muted)', cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', textDecoration:'none' }}
                          title="Call vendor">
                          <Phone size={13} className="btn-icon"/>
                        </a>
                      )}
                      {(vendor.contact_email ?? vendor.vendor_email) && (
                        <a href={`mailto:${vendor.contact_email ?? vendor.vendor_email}`} className="combo-jello-spin" data-tint="purple" onMouseLeave={spinIconOut}
                          onClick={e => e.stopPropagation()}
                          style={{ width:28, height:28, borderRadius:7, border:'1px solid var(--border-default)', background:'transparent', color:'var(--text-muted)', cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', textDecoration:'none' }}
                          title="Email vendor">
                          <Mail size={13} className="btn-icon"/>
                        </a>
                      )}
                      <button className="combo-jello-spin" data-tint="blue" onMouseLeave={spinIconOut}
                        onClick={e => { e.stopPropagation(); onOpenVendor(vendor.id); }}
                        style={{ width:28, height:28, borderRadius:7, border:'1px solid var(--border-default)', background:'transparent', color:'var(--text-muted)', cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}
                        title="Open vendor">
                        <ExternalLink size={13} className="btn-icon"/>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize:'0.78em', color:'var(--text-muted)' }}>No vendor assigned.</div>
              )}
            </DetailCard>

            <DetailCard>
              <DetailSect>Notes</DetailSect>
              <ExpandedNotes taskId={task.id} facilityId={facilityId} initialNotes={task.notes} />
            </DetailCard>
          </div>

          {/* MIDDLE column */}
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <DetailCard>
              <DetailSect>AI insights</DetailSect>
              <TaskAiInsights
                task={task}
                latestReport={latestReport}
                isEnterprise={isEnterprise}
                isPro={isPro}
                getAnalysisCount={getAnalysisCount}
                proMonthlyLimit={proMonthlyLimit}
                onAnalyzeReport={onAnalyzeReport}
                onOpenAnalysisModal={onOpenAnalysisModal}
              />
            </DetailCard>

            <DetailCard>
              <DetailSect>Documents ({task.reports.length})</DetailSect>
              {task.reports.length === 0 ? (
                <div style={{ fontSize:'0.78em', color:'var(--text-muted)' }}>No reports uploaded yet.</div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  {sortedReports.map(r => (
                    <DetailReportRow
                      key={r.id}
                      report={r}
                      isEnterprise={isEnterprise}
                      isPro={isPro}
                      getAnalysisCount={getAnalysisCount}
                      proMonthlyLimit={proMonthlyLimit}
                      onAnalyzeReport={(force) => onAnalyzeReport(r.id, r.label || r.report_name || 'Report', force)}
                      onOpenAnalysisModal={() => onOpenAnalysisModal(r.analysis_json, r.label || r.report_name || 'Report')}
                      confirmRemove={confirmRemoveReportId === r.id}
                      onRequestRemove={() => onRequestRemoveReport(r.id)}
                      onCancelRemove={onCancelRemoveReport}
                      onDelete={() => onDeleteReport(r.id)}
                      serviceEditing={serviceEditId === r.id}
                      serviceEditValue={serviceEditValue}
                      onStartServiceEdit={() => { onServiceEditIdChange(r.id); onServiceEditValueChange(r.service_date ? r.service_date.slice(0, 10) : new Date().toISOString().slice(0, 10)); }}
                      onCancelServiceEdit={() => onServiceEditIdChange(null)}
                      onServiceEditValueChange={onServiceEditValueChange}
                      onSaveServiceDate={() => onSaveServiceDate(r.id)}
                    />
                  ))}
                </div>
              )}
            </DetailCard>
          </div>

          {/* RIGHT column */}
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <DetailCard>
              <DetailSect>Timeline</DetailSect>
              <TaskTimeline task={task} sortedReports={sortedReports} nextDueDisplay={nextDueDisplay} />
            </DetailCard>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Compliance PM: Vendor Detail takeover (item 4) ─────────────────────────
// Investigated first: Vendors.tsx already has a full VendorDetailPanel (slide-in, theme-
// reactive per the CLAUDE_RULES.md 2026-07-11 exception) with tabbed Contracts/Spend/
// Documents/Ratings sections, each fetching its own data via vendorId. Reusing that whole
// component here isn't practical — it's ~800 lines wired to Vendors.tsx's own edit/logo-
// upload/AI-extract flows, and its tab sections aren't exported for reuse. What IS reused is
// its DATA-FETCHING SHAPE: like VendorDetailPanel, this never fetches on its own — it reads
// from the same already-loaded vendor list (ScheduleTab's vendorsFull, sourced from the same
// GET /vendors the edit-modal dropdown already uses) rather than firing a new request, exactly
// how VendorDetailPanel receives its `vendor` prop pre-fetched from Vendors.tsx's own list
// query. Presented as a full-page takeover matching TaskDetailPage's own pattern (same
// pm-scale-16 baseline, same DetailCard/DetailSect), layered on top of it via a higher z-index
// instead of unmounting it, so "Back" returns to the task exactly where the user left it.
function VendorDetailTakeover({ vendor, isEnterprise, onClose }: { vendor: VendorFull; isEnterprise: boolean; onClose: () => void }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const phone = vendor.contact_phone ?? vendor.vendor_phone;
  const email = vendor.contact_email ?? vendor.vendor_email;

  return ReactDOM.createPortal(
    <div className="pm-scale-16" style={{
      position:'fixed', inset:0, zIndex:600, overflowY:'auto', background:'var(--bg-base)',
      animation:'pageTakeoverRiseIn 0.3s cubic-bezier(0.25,1,0.35,1)',
    }}>
      <div style={{ maxWidth:720, margin:'0 auto', padding:'24px 30px 90px' }}>
        <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ gap:6 }}>
          <ArrowLeft size={14}/> Back to Task
        </button>

        {/* Hero */}
        <div style={{ display:'flex', alignItems:'center', gap:16, margin:'14px 0 20px', flexWrap:'wrap' }}>
          <div style={{ width:58, height:58, borderRadius:15, background:'rgba(239,68,68,0.15)', color:'#f87171', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:'1.3em', flexShrink:0 }}>
            {vendor.name.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
              <div style={{ fontSize:'1.5em', fontWeight:700, color:'var(--text-primary)' }}>{vendor.name}</div>
              {vendor.is_main_vendor && (
                <span style={{ background:'rgba(245,158,11,0.15)', color:'#f59e0b', fontSize:'0.65em', padding:'2px 9px', borderRadius:99, fontWeight:600 }}>Main Vendor</span>
              )}
              {vendor.is_preferred && (
                <span style={{ background:'rgba(96,165,250,0.15)', color:'#60a5fa', fontSize:'0.65em', padding:'2px 9px', borderRadius:99, fontWeight:600, display:'inline-flex', alignItems:'center', gap:3 }}>
                  <Star size={10} fill="#60a5fa" color="#60a5fa"/> Preferred
                </span>
              )}
            </div>
            {(vendor.avg_rating != null && vendor.rating_count) ? (
              <div style={{ fontSize:'0.74em', color:'var(--text-muted)', marginTop:4, display:'flex', alignItems:'center', gap:5 }}>
                <Star size={12} fill="#fbbf24" color="#fbbf24"/> {vendor.avg_rating.toFixed(1)} ({vendor.rating_count} rating{vendor.rating_count === 1 ? '' : 's'})
              </div>
            ) : null}
          </div>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {isEnterprise && vendor.risk_flags && vendor.risk_flags.length > 0 && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
              {vendor.risk_flags.map(flag => (
                <span key={flag} style={{
                  background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.4)',
                  color:'#fcd34d', fontSize:'0.72em', padding:'3px 9px', borderRadius:99,
                  display:'inline-flex', alignItems:'center', gap:4,
                }}>
                  <AlertTriangle size={11}/> {flag}
                </span>
              ))}
            </div>
          )}

          <DetailCard>
            <DetailSect>Contact</DetailSect>
            <div style={{ display:'flex', flexDirection:'column', gap:9 }}>
              {phone && (
                <a href={`tel:${phone}`} style={{ display:'flex', alignItems:'center', gap:9, fontSize:'0.82em', color:'var(--text-primary)', textDecoration:'none' }}>
                  <Phone size={14} color="var(--text-muted)"/> {phone}
                </a>
              )}
              {email && (
                <a href={`mailto:${email}`} style={{ display:'flex', alignItems:'center', gap:9, fontSize:'0.82em', color:'var(--text-primary)', textDecoration:'none' }}>
                  <Mail size={14} color="var(--text-muted)"/> {email}
                </a>
              )}
              {vendor.website && (
                <a href={/^https?:\/\//i.test(vendor.website) ? vendor.website : `https://${vendor.website}`} target="_blank" rel="noreferrer"
                  style={{ display:'flex', alignItems:'center', gap:9, fontSize:'0.82em', color:'var(--text-primary)', textDecoration:'none' }}>
                  <Globe size={14} color="var(--text-muted)"/> {vendor.website}
                </a>
              )}
              {vendor.hours_of_operation && (
                <div style={{ display:'flex', alignItems:'center', gap:9, fontSize:'0.82em', color:'var(--text-primary)' }}>
                  <Clock size={14} color="var(--text-muted)"/> {vendor.hours_of_operation}
                  {vendor.is_24_hour_service && <DetailChip variant="g">24-hour</DetailChip>}
                </div>
              )}
              {vendor.account_number && (
                <div style={{ display:'flex', alignItems:'center', gap:9, fontSize:'0.82em', color:'var(--text-primary)' }}>
                  <Hash size={14} color="var(--text-muted)"/> Acct: {vendor.account_number}
                </div>
              )}
              {!phone && !email && !vendor.website && !vendor.hours_of_operation && !vendor.account_number && (
                <div style={{ fontSize:'0.78em', color:'var(--text-muted)' }}>No contact info on file.</div>
              )}
            </div>
          </DetailCard>

          <VendorContactsCard vendorId={vendor.id} />

          {vendor.address && (
            <DetailCard>
              <DetailSect>Address</DetailSect>
              <div style={{ display:'flex', alignItems:'flex-start', gap:9, fontSize:'0.82em', color:'var(--text-primary)' }}>
                <MapPin size={14} color="var(--text-muted)" style={{ marginTop:2, flexShrink:0 }}/> {vendor.address}
              </div>
            </DetailCard>
          )}

          {(vendor.contract_start_date || vendor.contract_expiry_date || vendor.contract_notes) && (
            <DetailCard>
              <DetailSect>Contract</DetailSect>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:9 }}>
                <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid var(--border-subtle)', borderRadius:10, padding:'10px 12px' }}>
                  <div style={{ fontSize:'0.62em', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-muted)' }}>Start</div>
                  <div style={{ fontSize:'0.85em', fontWeight:600, marginTop:3, color:'var(--text-primary)' }}>{formatTaskDate(vendor.contract_start_date)}</div>
                </div>
                <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid var(--border-subtle)', borderRadius:10, padding:'10px 12px' }}>
                  <div style={{ fontSize:'0.62em', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-muted)' }}>Expiry</div>
                  <div style={{ fontSize:'0.85em', fontWeight:600, marginTop:3, color:'var(--text-primary)' }}>{formatTaskDate(vendor.contract_expiry_date)}</div>
                </div>
              </div>
              {vendor.contract_notes && (
                <div style={{ fontSize:'0.78em', color:'var(--text-secondary)', lineHeight:1.5, marginTop:4 }}>{vendor.contract_notes}</div>
              )}
            </DetailCard>
          )}

          {vendor.service_categories && vendor.service_categories.length > 0 && (
            <DetailCard>
              <DetailSect>Service Categories</DetailSect>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {vendor.service_categories.map(cat => (
                  <span key={cat} style={{ padding:'3px 10px', borderRadius:99, fontSize:'0.72em', fontWeight:600, background:'rgba(139,92,246,0.13)', color:'#a78bfa' }}>{cat}</span>
                ))}
              </div>
            </DetailCard>
          )}

          {vendor.notes && (
            <DetailCard>
              <DetailSect>Notes</DetailSect>
              <div style={{ fontSize:'0.78em', color:'var(--text-secondary)', lineHeight:1.5 }}>{vendor.notes}</div>
            </DetailCard>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// Item 3 — the takeover previously only showed the vendor's single legacy contact_name/phone
// field. Vendors can have multiple contacts (primary/secondary/emergency/after-hours), stored
// in vendor_contacts and fetched the same way VendorContactsSection (Vendors.tsx) does — GET
// /vendors/:id/contacts — kept here as its own component so this fetch only fires once the
// takeover is actually open. Read-only: this page doesn't own contact editing, that stays on
// the Vendors page.
function VendorContactsCard({ vendorId }: { vendorId: number }) {
  const { data: contactsRes } = useQuery({
    queryKey: ['vendor-contacts', vendorId],
    queryFn: () => api.get<VendorContact[]>(`/vendors/${vendorId}/contacts`),
  });
  const contacts: VendorContact[] = (contactsRes as any)?.data ?? [];
  if (contacts.length === 0) return null;

  const typeColors: Record<string, string> = {
    primary: '#3b82f6', secondary: '#10b981', emergency: '#ef4444', after_hours: '#f59e0b',
  };
  const typeLabels: Record<string, string> = {
    primary: 'Primary', secondary: 'Secondary', emergency: 'Emergency', after_hours: 'After Hours',
  };

  return (
    <DetailCard>
      <DetailSect>Contacts ({contacts.length})</DetailSect>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {contacts.map(c => {
          const color = typeColors[c.contact_type] ?? '#6366f1';
          return (
            <div key={c.id} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid var(--border-subtle)', borderRadius:10, padding:'10px 12px', display:'flex', flexDirection:'column', gap:4 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontWeight:600, fontSize:'0.82em', color:'var(--text-primary)', flex:1 }}>{c.name}</span>
                <span style={{ padding:'1px 8px', borderRadius:99, fontSize:'0.65em', fontWeight:700, background:`${color}22`, color, border:`1px solid ${color}44` }}>
                  {typeLabels[c.contact_type] ?? c.contact_type}
                </span>
              </div>
              {c.title && <div style={{ fontSize:'0.76em', color:'var(--text-muted)' }}>{c.title}</div>}
              {c.phone && (
                <a href={`tel:${c.phone}`} style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.78em', color:'var(--text-secondary)', textDecoration:'none' }}>
                  <Phone size={12} color="var(--text-muted)"/> {c.phone}
                </a>
              )}
              {c.email && (
                <a href={`mailto:${c.email}`} style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.78em', color:'var(--text-secondary)', textDecoration:'none' }}>
                  <Mail size={12} color="var(--text-muted)"/> {c.email}
                </a>
              )}
              {c.notes && <div style={{ fontSize:'0.72em', color:'var(--text-muted)', fontStyle:'italic' }}>{c.notes}</div>}
            </div>
          );
        })}
      </div>
    </DetailCard>
  );
}

// ─── Task Schedule Add/Edit Modal ─────────────────────────────────────────────
const NAMED_FREQS = ['Weekly','Monthly','Every 2 months','Quarterly','Semi-Annual','Yearly','Every 2 years','Every 3 years','Every 4 years','Every 5 years'];
const FREQ_LABEL: Record<string, string> = {
  'Weekly':         'Weekly',
  'Monthly':        'Monthly',
  'Every 2 months': 'Every 2 months',
  'Quarterly':      'Quarterly (every 3 months)',
  'Semi-Annual':    'Semi-Annual (every 6 months)',
  'Yearly':         'Yearly',
  'Every 2 years':  'Every 2 years',
  'Every 3 years':  'Every 3 years',
  'Every 4 years':  'Every 4 years',
  'Every 5 years':  'Every 5 years',
};
const FREQ_TO_INTERVAL_MODAL: Record<string, number> = {
  'Weekly': 0, 'Monthly': 1, 'Every 2 months': 2, 'Quarterly': 3,
  'Semi-Annual': 6, 'Yearly': 12, 'Every 2 years': 24, 'Every 3 years': 36, 'Every 4 years': 48, 'Every 5 years': 60,
};

// Rebuilt per mockups/add-task-modal-redesign.html Option A (two-column command center).
// Fixed header/footer live OUTSIDE the scrollable area — <form> is the flex column, the body
// div is the only scrolling child (flex:1, overflowY:auto), header/footer are flexShrink:0
// siblings around it, same skeleton as Vendors.tsx's Add/Edit modal (line ~1156). Every field,
// the custom-frequency sub-form, and handleSubmit/handleFreqChange are unchanged from before —
// this is a layout/visual rebuild only. Notes wasn't in the mockup (which only sketches
// name/label/icon/color + frequency/date/vendors) so it's kept in the Identity column, since it
// describes the task itself rather than its schedule.
function TaskScheduleModal({ task, vendors, facilityName, onClose, onSaved }: {
  task?: ScTask;
  vendors: { id: number; name: string }[];
  facilityName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: task?.name ?? '',
    icon: task?.icon ?? '🔧',
    color: task?.color ?? '#3b82f6',
    frequency: task?.frequency ?? 'Monthly',
    interval_months: task?.interval_months ?? 1,
    customN: task?.interval_months ?? 1,
    customUnit: 'months' as 'months'|'years',
    vendor_id: task?.vendor_id ? String(task.vendor_id) : '',
    vendor_name: task?.vendor_name ?? '',
    vendor_2: task?.vendor_2 ?? '',
    label: task?.label ?? '',
    notes: task?.notes ?? '',
    first_due_month: task?.first_due_date ? new Date(task.first_due_date).getUTCMonth() + 1 : undefined as number | undefined,
    first_due_year: task?.first_due_date ? new Date(task.first_due_date).getUTCFullYear() : undefined as number | undefined,
    first_due_date: task?.first_due_date ? (() => { const d = new Date(task.first_due_date); return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}-15`; })() : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  useEffect(()=>{ document.body.style.overflow='hidden'; return()=>{ document.body.style.overflow=''; }; },[]);

  function handleFreqChange(freq: string) {
    if (freq === 'custom') {
      setForm(p=>({ ...p, frequency:`Every ${p.customN} ${p.customUnit}`, interval_months: p.customUnit==='years' ? p.customN*12 : p.customN }));
      return;
    }
    const interval = FREQ_TO_INTERVAL_MODAL[freq] ?? 1;
    setForm(p=>({ ...p, frequency:freq, interval_months: interval }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    if (!form.first_due_date) { setError('First Due Date is required'); return; }
    setSaving(true); setError('');
    try {
      const payload: Record<string, unknown> = {
        name: form.name.trim(), icon: form.icon, color: form.color,
        frequency: form.frequency,
        vendor_id: form.vendor_id ? Number(form.vendor_id) : null,
        vendor_name: form.vendor_name || null,
        vendor_2: form.vendor_2 || null,
        label: form.label || null,
        notes: form.notes || null,
        first_due_date: form.first_due_date,
      };
      if (task?.id) await api.put(`/scheduled-tasks/${task.id}`, payload);
      else await api.post('/scheduled-tasks', payload);
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Save failed');
    } finally { setSaving(false); }
  }

  const isCustom = !NAMED_FREQS.includes(form.frequency);

  // Footer hint ("Next occurrence will be …") — reuses the same computeNextDueDisplay the
  // Task Detail page's Status card already uses, fed a minimal stand-in ScTask (only
  // first_due_date/frequency/completions are read by that function) instead of duplicating its
  // occurrence-search math here.
  const nextOccurrence = form.first_due_date
    ? computeNextDueDisplay({ first_due_date: form.first_due_date, frequency: form.frequency, completions: task?.completions ?? [] } as ScTask)
    : null;
  const previewMonthLabel = form.first_due_month
    ? ['January','February','March','April','May','June','July','August','September','October','November','December'][form.first_due_month - 1]
    : null;

  return ReactDOM.createPortal(
    <div
      className="modal-overlay"
      onMouseDown={e => { mouseDownTargetRef.current = e.target; }}
      onMouseUp={e => { if (e.target === e.currentTarget && mouseDownTargetRef.current === e.currentTarget) onClose(); }}
    >
      <div
        className="modal"
        style={{ maxWidth:900, maxHeight:'90vh', overflow:'hidden', display:'flex', flexDirection:'column', padding:0 }}
        onClick={e => e.stopPropagation()}
      >
        {/* Fixed Header */}
        <div style={{ flexShrink:0, padding:'20px 24px', borderBottom:'1px solid var(--border-subtle)', display:'flex', alignItems:'center', gap:13 }}>
          <div style={{ width:42, height:42, borderRadius:12, background:'rgba(59,130,246,0.13)', color:'#60a5fa', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <ListChecks size={20} className="modal-icon-intro"/>
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:'1.15rem', fontWeight:700, color:'var(--text-primary)' }}>{task ? 'Edit compliance task' : 'Add compliance task'}</div>
            <div style={{ fontSize:'0.74rem', color:'var(--text-muted)', marginTop:1 }}>Compliance PM{facilityName ? ` · ${facilityName}` : ''}</div>
          </div>
          <button type="button" className="modal-close" onClick={onClose}><X size={18} className="btn-icon"/></button>
        </div>

        <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', flex:1, overflow:'hidden', minHeight:0 }}>
          {/* Scrollable two-column body — the only child that scrolls */}
          <div className="task-modal-grid" style={{ overflowY:'auto', flex:1, display:'grid' }}>
            {/* LEFT — Identity */}
            <div style={{ padding:'22px 24px', display:'flex', flexDirection:'column', gap:18 }}>
              <div className="task-modal-colhead">Identity</div>

              <div>
                <label className="form-label">Task Name <span style={{ color:'#f87171' }}>*</span></label>
                <input className="form-input" required value={form.name}
                  onChange={e=>setForm(p=>({ ...p, name:e.target.value }))} placeholder="e.g. HVAC Filter Replacement"/>
              </div>

              <div>
                <label className="form-label">Label <span style={{ color:'var(--text-muted)', fontWeight:400 }}>(optional)</span></label>
                <input className="form-input" value={form.label}
                  onChange={e=>setForm(p=>({ ...p, label:e.target.value }))} placeholder="e.g. Annual Fire Inspection"/>
              </div>

              {/* Emoji picker */}
              <div>
                <label className="form-label">Icon</label>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {TASK_EMOJIS.map(emoji=>(
                    <button key={emoji} type="button" onClick={()=>setForm(p=>({ ...p, icon:emoji }))} style={{
                      width:34, height:34, borderRadius:8,
                      border: form.icon===emoji ? '1px solid var(--accent-primary)' : '1px solid var(--border-default)',
                      background: form.icon===emoji ? 'rgba(59,130,246,0.18)' : 'var(--bg-elevated)',
                      boxShadow: form.icon===emoji ? '0 0 0 1px var(--accent-primary)' : 'none',
                      cursor:'pointer', fontSize:'1rem', display:'flex', alignItems:'center', justifyContent:'center',
                      transition:'all 0.13s',
                    }}>{emoji}</button>
                  ))}
                </div>
              </div>

              {/* Color picker */}
              <div>
                <label className="form-label">Color</label>
                <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                  {TASK_COLORS.map(color=>(
                    <button key={color} type="button" onClick={()=>setForm(p=>({ ...p, color }))} title={color} style={{
                      width:28, height:28, borderRadius:'50%',
                      border: form.color===color ? `2px solid ${color}` : '2px solid transparent',
                      boxShadow: form.color===color ? `0 0 0 2px var(--bg-elevated), 0 0 0 3.5px ${color}` : 'none',
                      background:color, cursor:'pointer', transition:'transform 0.13s',
                    }}/>
                  ))}
                </div>
              </div>

              {/* Notes — not part of the mockup's Identity/Schedule split, kept here since it
                  describes the task rather than its recurrence */}
              <div>
                <label className="form-label">Notes</label>
                <textarea className="form-input" rows={3} value={form.notes}
                  onChange={e=>setForm(p=>({ ...p, notes:e.target.value }))}/>
              </div>
            </div>

            {/* RIGHT — Schedule (distinct background per mockup's .mA-col.right) */}
            <div style={{ padding:'22px 24px', display:'flex', flexDirection:'column', gap:18, borderLeft:'1px solid var(--border-subtle)', background:'var(--bg-hover)' }}>
              <div className="task-modal-colhead">Schedule</div>

              {/* Live preview chip */}
              <div style={{ display:'flex', alignItems:'center', gap:12, padding:'13px 15px', borderRadius:12, border:'1px solid rgba(59,130,246,0.25)', background:'rgba(59,130,246,0.08)' }}>
                <div style={{ width:40, height:40, borderRadius:11, background:'rgba(59,130,246,0.16)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.25rem', flexShrink:0 }}>
                  {form.icon}
                </div>
                <div style={{ minWidth:0, flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:'0.9rem', color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {form.name || 'Untitled task'}
                  </div>
                  <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:2 }}>
                    {FREQ_LABEL[form.frequency] ?? form.frequency}{previewMonthLabel ? ` · starts ${previewMonthLabel} ${form.first_due_year}` : ''}
                  </div>
                </div>
                <span style={{ flexShrink:0, fontSize:'0.62rem', fontWeight:700, padding:'3px 9px', borderRadius:99, background:'rgba(236,72,153,0.15)', color:'#f472b6', textTransform:'uppercase' }}>
                  {(FREQ_LABEL[form.frequency] ?? form.frequency).slice(0, 14)}
                </span>
              </div>

              {/* Frequency — chip-select, reuses the app's category-pill mechanics */}
              <div>
                <label className="form-label">Frequency <span style={{ color:'#f87171' }}>*</span></label>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {NAMED_FREQS.map(f => {
                    const on = !isCustom && form.frequency === f;
                    return (
                      <button key={f} type="button" className={`category-pill${on ? ' active' : ''}`}
                        onClick={()=>handleFreqChange(f)}
                        style={{
                          padding:'7px 14px', borderRadius:9, fontSize:'0.8rem', fontWeight:500, cursor:'pointer',
                          border: on ? '1px solid rgba(59,130,246,0.5)' : '1px solid var(--border-default)',
                          background: on ? 'rgba(59,130,246,0.13)' : 'var(--bg-elevated)',
                          color: on ? '#93c5fd' : 'var(--text-secondary)',
                        }}>{FREQ_LABEL[f]}</button>
                    );
                  })}
                  <button type="button" className={`category-pill${isCustom ? ' active' : ''}`}
                    onClick={()=>handleFreqChange('custom')}
                    style={{
                      padding:'7px 14px', borderRadius:9, fontSize:'0.8rem', fontWeight:500, cursor:'pointer',
                      border: isCustom ? '1px solid rgba(59,130,246,0.5)' : '1px solid var(--border-default)',
                      background: isCustom ? 'rgba(59,130,246,0.13)' : 'var(--bg-elevated)',
                      color: isCustom ? '#93c5fd' : 'var(--text-secondary)',
                    }}>Custom…</button>
                </div>
                {isCustom && (
                  <div style={{ display:'flex', gap:8, marginTop:10, alignItems:'center' }}>
                    <span style={{ fontSize:'0.875rem', color:'var(--text-muted)' }}>Every</span>
                    <input type="number" min={1} max={120} className="form-input" style={{ width:70 }}
                      value={form.customN}
                      onChange={e=>{
                        const n = Math.max(1, parseInt(e.target.value)||1);
                        const interval = form.customUnit==='years' ? n*12 : n;
                        setForm(p=>({ ...p, customN:n, frequency:`Every ${n} ${p.customUnit}`, interval_months:interval }));
                      }}/>
                    <select className="form-input" style={{ width:100 }} value={form.customUnit}
                      onChange={e=>{
                        const unit = e.target.value as 'months'|'years';
                        const interval = unit==='years' ? form.customN*12 : form.customN;
                        setForm(p=>({ ...p, customUnit:unit, frequency:`Every ${p.customN} ${unit}`, interval_months:interval }));
                      }}>
                      <option value="months">months</option>
                      <option value="years">years</option>
                    </select>
                  </div>
                )}
              </div>

              {/* First Due Date — required, sets the schedule anchor */}
              <div>
                <label className="form-label">First Due Date <span style={{ color:'#f87171' }}>*</span></label>
                <div style={{ display:'flex', gap:8 }}>
                  <select
                    className="form-input"
                    required
                    value={form.first_due_month ?? ''}
                    onChange={e => {
                      const m = Number(e.target.value);
                      setForm(p => ({ ...p, first_due_month: m,
                        first_due_date: p.first_due_year ? `${p.first_due_year}-${String(m).padStart(2,'0')}-15` : '' }));
                    }}
                    style={{ flex:1 }}
                  >
                    <option value="">Month *</option>
                    {['January','February','March','April','May','June','July','August','September','October','November','December']
                      .map((name,i) => <option key={i+1} value={i+1}>{name}</option>)}
                  </select>
                  <select
                    className="form-input"
                    required
                    value={form.first_due_year ?? ''}
                    onChange={e => {
                      const y = Number(e.target.value);
                      setForm(p => ({ ...p, first_due_year: y,
                        first_due_date: p.first_due_month ? `${y}-${String(p.first_due_month).padStart(2,'0')}-15` : '' }));
                    }}
                    style={{ flex:1 }}
                  >
                    <option value="">Year *</option>
                    {Array.from({ length:15 }, (_,i) => new Date().getFullYear() - 10 + i)
                      .map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:4 }}>
                  The month this task is first due. All future occurrences are anchored to this date.
                </div>
              </div>

              {/* Vendor 1 */}
              <div>
                <label className="form-label">Vendor</label>
                <select className="form-input" value={form.vendor_id}
                  onChange={e=>{
                    const v = vendors.find(v=>String(v.id)===e.target.value);
                    setForm(p=>({ ...p, vendor_id:e.target.value, vendor_name:v?.name ?? '' }));
                  }}>
                  <option value="">— None —</option>
                  {vendors.map(v=><option key={v.id} value={String(v.id)}>{v.name}</option>)}
                </select>
              </div>

              {/* Vendor 2 (free text) */}
              <div>
                <label className="form-label">Vendor 2 <span style={{ color:'var(--text-muted)', fontWeight:400 }}>(optional)</span></label>
                <input className="form-input" value={form.vendor_2}
                  onChange={e=>setForm(p=>({ ...p, vendor_2:e.target.value }))} placeholder="Secondary or backup vendor"/>
              </div>

              {error && <div style={{ color:'var(--accent-danger)', fontSize:'0.875rem' }}>{error}</div>}
            </div>
          </div>

          {/* Fixed Footer */}
          <div style={{ flexShrink:0, padding:'16px 24px', borderTop:'1px solid var(--border-subtle)', display:'flex', alignItems:'center', gap:10, background:'rgba(0,0,0,0.18)' }}>
            {nextOccurrence && (
              <span style={{ fontSize:'0.7rem', color:'var(--text-muted)' }}>
                Next occurrence will be <b style={{ color:'var(--text-secondary)' }}>{nextOccurrence.label}</b>
              </span>
            )}
            <div style={{ flex:1 }}/>
            <button type="button" className="btn btn-ghost combo-cancel-jello" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary combo2" disabled={saving||!form.name.trim()||!form.first_due_date}>
              {saving ? 'Saving…' : task ? 'Save Changes' : '＋ Create task'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// ─── Work Orders Types + Helpers ─────────────────────────────────────────────

interface WorkOrder {
  id: number; wo_number: string; title: string; description: string | null;
  category: string; priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'on_hold' | 'done' | 'cancelled';
  assigned_to: number | null; assigned_to_name: string | null;
  assignee_name: string | null; assignee_username: string | null;
  location: string | null; due_date: string | null; completed_date: string | null;
  estimated_hours: number | null; actual_hours: number; total_hours: number;
  note_count: number; photo_count: number; life_safety_section: number | null;
  is_recurring: boolean; source: string; notes: string | null;
  creator_name: string | null; created_at: string; updated_at: string;
  ticket_item_id?: number | null; ticket_item_label?: string | null;
  logbook_template_id?: number | null;
  custom_form_schema?: FormField[] | null;
  custom_form_values?: Record<string, string | boolean> | null;
}
interface WoTimeLog {
  id: number; wo_id: number; user_id: number | null; user_name: string | null;
  hours: number; notes: string | null; logged_at: string;
}
interface WoNote {
  id: number; wo_id: number; user_id: number | null; user_name: string | null;
  content: string; created_at: string;
}
interface StaffSummary {
  id: number; name: string; username: string;
  active_count: number; done_this_week: number; hours_this_week: number;
}
interface ScheduledWorkOrder {
  id: number; facility_id: number; title: string; description: string | null;
  category: string; priority: string;
  area_label: string | null; location_label: string | null; ticket_item_label: string | null;
  assigned_to: number | null; assigned_to_name: string | null;
  frequency_unit: 'days' | 'weeks' | 'months' | 'years'; frequency_interval: number;
  start_date: string; end_date: string | null; next_run_date: string;
  prior_days_notice: number; skip_weekends: boolean;
  estimated_hours: number | null; is_active: boolean;
  last_generated_at: string | null; created_at: string;
  custom_form_schema?: FormField[] | null;
  // v145 — item C: filter/part link + how many it consumes each time this schedule's occurrence
  // is closed. Every generated work order inherits both from here (scheduled-work-orders.ts's
  // /generate), which is what makes "close a filter-change task" able to auto-log usage.
  linked_hvac_filter_id: number | null;
  consumption_qty: number | null;
}

// ─── Logbook Form tie-in (Logbooks Phase 2) — explicit selector, not an automatic tie based on
// category/source. Templates with a non-null form_schema only; Disaster Manual is never in
// logbook_templates (its own system, disaster-manual.ts), so it's excluded automatically.
// LogbookFormField/LogbookFormSchema/LogbookFormFieldInputs/LogbookFormRenderer all live in
// components/LogbookFormRenderer.tsx now — shared with the Settings > Development JSON injector
// and LogbookTemplates.tsx, not duplicated per caller. ───
interface LogbookTemplateOption {
  id: number; name: string; department_id: number | null; form_schema: LogbookFormSchema | null;
}

// ─── WO detail panel — Checklist tab data (Logbooks Phase 2 WO tie-in, restyled) ───────────────
// Fetches the attached template via GET /logbook-templates/:id — that endpoint already enforces
// the exact department gate this feature needs ("a user shouldn't be able to fill/view a form
// tied to a template outside their department"), so a 403 here is handled as an access-denied
// state rather than duplicating the department-comparison client-side. Same reasoning for
// save/edit: POST/PUT /logbook-filled-entries already gate identically — errors surface inline
// rather than being pre-computed client-side.
//
// Split into a data hook + presentational panel (rather than the earlier single LogFormTab
// component) because the takeover redesign needs the same live checklist progress (checked
// count / total) in both the Checklist tab body AND the properties sidebar's Progress section —
// calling the hook once at the TaskManagerTab level lets both consume the same state.
function useWoChecklistData(workOrderId: number | undefined, templateId: number | null | undefined, active: boolean) {
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, string | boolean>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [autoCreateFollowup, setAutoCreateFollowup] = useState(false);
  const initializedRef = useRef(false);
  // Tracks the previous render's "has any violation" state so the auto-check effect below only
  // fires on the 0->nonzero transition, not on every keystroke while values stay out of range --
  // that's what lets a user manually uncheck the box and have it stay unchecked while they keep
  // typing, per Chris's "leave it user-toggleable" requirement.
  const hadViolationsRef = useRef(false);

  // Reset local state whenever the selected WO (or its attached template) changes — this hook is
  // called unconditionally from TaskManagerTab, which stays mounted across WO selections, so
  // there's no component remount to reset state for us the way the old per-tab component had.
  useEffect(() => {
    setValues({});
    setSaveError('');
    setAutoCreateFollowup(false);
    initializedRef.current = false;
    hadViolationsRef.current = false;
  }, [workOrderId, templateId]);

  const enabledQ = active && !!templateId;

  const { data: templateRes, isLoading: templateLoading, isError: templateError } = useQuery({
    queryKey: ['logbook-template', templateId],
    queryFn: () => api.get<{ id: number; name: string; department_id: number | null; form_schema: LogbookFormSchema | null }>(`/logbook-templates/${templateId}`),
    enabled: enabledQ,
    retry: false,
  });
  const template = templateRes?.data;

  const { data: entryRes, refetch: refetchEntry } = useQuery({
    queryKey: ['logbook-filled-entries', 'by-wo', workOrderId],
    queryFn: () => api.get<FilledEntry[]>('/logbook-filled-entries', { work_order_id: workOrderId }),
    enabled: enabledQ && !!template,
  });
  const existingEntry = entryRes?.data?.[0] ?? null;

  useEffect(() => {
    if (existingEntry && !initializedRef.current) {
      setValues(existingEntry.filled_data ?? {});
      initializedRef.current = true;
    }
  }, [existingEntry]);

  // Real-time range check against the currently-loaded template's schema -- recomputed on every
  // values change, but the auto-check effect below only reacts to the count crossing 0->nonzero.
  const violations = findLogbookRangeViolations(template?.form_schema ?? null, values);
  useEffect(() => {
    const hasViolations = violations.length > 0;
    if (hasViolations && !hadViolationsRef.current) setAutoCreateFollowup(true);
    hadViolationsRef.current = hasViolations;
  }, [violations.length]);

  async function handleSave() {
    if (!template || !templateId || !workOrderId) return;
    setSaving(true); setSaveError('');
    try {
      if (existingEntry) {
        await api.put(`/logbook-filled-entries/${existingEntry.id}`, { filled_data: values, auto_create_followup: autoCreateFollowup });
      } else {
        await api.post('/logbook-filled-entries', { template_id: templateId, work_order_id: workOrderId, filled_data: values, auto_create_followup: autoCreateFollowup });
      }
      await refetchEntry();
      qc.invalidateQueries({ queryKey: ['logbook-filled-entries'] });
      qc.invalidateQueries({ queryKey: ['work-orders'] });
      setAutoCreateFollowup(false);
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || 'Failed to save');
    }
    setSaving(false);
  }

  return { template, templateLoading, templateError, existingEntry, values, setValues, saving, saveError, handleSave, autoCreateFollowup, setAutoCreateFollowup, violations };
}

type WoChecklistData = ReturnType<typeof useWoChecklistData>;

// Presentational Checklist tab body — takes the already-fetched hook data as props so the
// progress figures rendered here always match the sidebar's Progress section exactly.
function WoChecklistPanel({ data, workOrderId, woNumber, woTitle }: {
  data: WoChecklistData; workOrderId: number; woNumber: string; woTitle: string;
}) {
  const { template, templateLoading, templateError, existingEntry, values, setValues, saving, saveError, handleSave, autoCreateFollowup, setAutoCreateFollowup, violations } = data;
  const logoUrl = useFacilityLogoUrl();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [printingEntry, setPrintingEntry] = useState(false);
  const [printError, setPrintError] = useState('');
  // Guards against a dangling dialog if the user somehow switches WOs while it's open (the hook's
  // own values/violations reset on workOrderId change, but this modal-visibility flag is local to
  // this presentational component and wouldn't otherwise know to close).
  useEffect(() => { setConfirmOpen(false); }, [workOrderId]);

  if (templateLoading) {
    return <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>;
  }
  if (templateError || !template) {
    return <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>You don't have access to this department's form.</div>;
  }
  const schemaHasFields = template.form_schema && (
    template.form_schema.form_mode === 'single_form'
      ? (template.form_schema.fixed_fields?.length ?? 0) > 0
      : (template.form_schema.rows?.length ?? 0) > 0 && (template.form_schema.row_fields?.length ?? 0) > 0
  );
  if (!schemaHasFields) {
    return <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>This template has no fillable fields.</div>;
  }
  const schema = template.form_schema!;

  const printEntry: FilledEntry | null = existingEntry ? { ...existingEntry, wo_number: woNumber, work_order_title: woTitle } : null;
  const allFieldDefs = schema.form_mode === 'single_form' ? (schema.fixed_fields ?? []) : (schema.row_fields ?? []);
  const hasRangeLimitedFields = allFieldDefs.some(f => f.type === 'number' && (f.min !== undefined || f.max !== undefined));

  // Print now routes through a real generated PDF (printFilledEntryPdf, shared with Logbooks.tsx)
  // instead of window.print() against this live DOM -- this tab sits inside an ancestor panel
  // with fixed height + overflow, which was silently clipping the old live-DOM print output (the
  // print dialog still reported "1 of 1" since nothing beyond the clip was ever laid out to
  // paginate). Rendering into an isolated off-screen iframe sidesteps that entirely.
  const handlePrint = async () => {
    if (!printEntry || printingEntry) return;
    setPrintingEntry(true);
    setPrintError('');
    try {
      await printFilledEntryPdf(printEntry, logoUrl);
    } catch {
      setPrintError('PDF generation failed');
    }
    setPrintingEntry(false);
  };

  return (
    <div className="card" style={{ padding: '20px 22px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>{template.name}</div>
        {/* Directly editable from the moment the tab opens — whether the entry is new or
            already has values — no separate Edit click required (Chris's explicit ask). */}
        <LogbookFormRenderer
          schema={schema}
          values={values}
          onChange={(key, val) => setValues(v => ({ ...v, [key]: val }))}
          checklistStyle
        />
        {saveError && <div style={{ color: 'var(--accent-danger)', fontSize: 12 }}>{saveError}</div>}
        {printError && <div style={{ color: 'var(--accent-danger)', fontSize: 12 }}>{printError}</div>}
        {hasRangeLimitedFields && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={autoCreateFollowup} onChange={e => setAutoCreateFollowup(e.target.checked)} />
            Auto-create a follow-up ticket if any values are out of range
          </label>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button
            disabled={saving}
            onClick={() => {
              // Confirmation only inserts itself when there's actually something to warn about --
              // checkbox unchecked, or no active violations, and Save proceeds exactly as before.
              if (autoCreateFollowup && violations.length > 0) setConfirmOpen(true);
              else handleSave();
            }}
            style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, background: 'var(--accent-primary)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {existingEntry && (
            <button disabled={printingEntry} onClick={handlePrint} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 14px', fontSize: 12, fontWeight: 500, background: 'none', border: '1px solid var(--border-default)', color: 'var(--text-secondary)', borderRadius: 6, cursor: 'pointer', opacity: printingEntry ? 0.6 : 1 }}>
              <Printer size={12} /> {printingEntry ? 'Preparing…' : 'Print filled version'}
            </button>
          )}
        </div>
        {existingEntry && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            Filled {new Date(existingEntry.filled_at).toLocaleString()}
            {existingEntry.updated_at ? ` · edited ${new Date(existingEntry.updated_at).toLocaleString()}` : ''}
          </div>
        )}
      </div>
      {confirmOpen && (
        <RangeViolationConfirmModal
          violations={violations}
          onConfirm={() => { setConfirmOpen(false); handleSave(); }}
          onClose={() => setConfirmOpen(false)}
        />
      )}
    </div>
  );
}

const woMetaLabelStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)',
};
const woMetaValStyle: React.CSSProperties = {
  fontSize: '0.92rem', fontWeight: 600, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-primary)',
};
const WO_PRIORITY_COLOR: Record<string, string> = {
  urgent: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#10b981',
};
const WO_STATUS_LABEL: Record<string, string> = {
  open: 'Open', in_progress: 'In Progress', on_hold: 'On Hold', done: 'Done', cancelled: 'Cancelled',
};
const WO_STATUS_COLOR: Record<string, string> = {
  open: '#6b7280', in_progress: '#3b82f6', on_hold: '#f59e0b', done: '#10b981', cancelled: '#4b5563',
};
const USER_COLORS = [
  '#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6',
  '#ec4899','#06b6d4','#84cc16','#f97316','#14b8a6',
  '#6366f1','#e11d48','#059669','#d97706','#7c3aed',
  '#db2777','#0284c7','#65a30d','#ea580c','#0d9488',
];
function getUserColor(userId: number | null): string {
  if (userId === null) return 'var(--accent-primary)';
  return USER_COLORS[userId % USER_COLORS.length];
}

interface FacilityUser {
  id: number;
  first_name: string | null;
  last_name: string | null;
  username: string;
  role?: string;
  department_id?: number | null;
  department_name?: string | null;
  department_color?: string | null;
}

const deptBadge = (color: string, _name: string) => ({
  fontSize: 10,
  padding: '1px 6px',
  borderRadius: 20,
  background: color + '22',
  border: `1px solid ${color}66`,
  color: color,
  whiteSpace: 'nowrap' as const,
  display: 'inline-block',
});

function woFmtDate(d: string | null): string {
  if (!d) return '—';
  const [y, m, day] = d.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
function isWoOverdue(wo: WorkOrder): boolean {
  return !!(wo.due_date && wo.status !== 'done' && wo.status !== 'cancelled' &&
    new Date(wo.due_date) < new Date());
}

function ScheduledWoListView({
  items, onEdit, onDelete, facilityUsers = [],
}: {
  items: ScheduledWorkOrder[];
  onEdit: (swo: ScheduledWorkOrder) => void;
  onDelete: (id: number) => void;
  facilityUsers?: FacilityUser[];
}) {
  const today = new Date(); today.setHours(0,0,0,0);
  function fmtNext(dateStr: string) {
    const [y,m,d] = dateStr.slice(0,10).split('-').map(Number);
    const dt = new Date(y,m-1,d);
    const isOvd = dt < today;
    const isSoon = !isOvd && (dt.getTime()-today.getTime()) <= 7*86400000;
    return {
      label: dt.toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}),
      color: isOvd ? '#ef4444' : isSoon ? '#f59e0b' : 'var(--text-secondary)',
      bold: isOvd || isSoon,
    };
  }
  const freqStyle = (u: string) => ({
    days:   {bg:'rgba(59,130,246,0.15)',  color:'#3b82f6'},
    weeks:  {bg:'rgba(139,92,246,0.15)',  color:'#8b5cf6'},
    months: {bg:'rgba(245,158,11,0.15)',  color:'#f59e0b'},
    years:  {bg:'rgba(16,185,129,0.15)',  color:'#10b981'},
  }[u] ?? {bg:'rgba(107,114,128,0.15)',color:'#9ca3af'});

  if (items.length === 0) {
    return (
      <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'48px 20px',textAlign:'center'}}>
        <div style={{fontSize:36,opacity:0.25,marginBottom:14}}>🔁</div>
        <div style={{fontSize:14,fontWeight:500,color:'var(--text-secondary)',marginBottom:6}}>No scheduled work orders yet.</div>
        <div style={{fontSize:12,color:'var(--text-muted)'}}>Click + New Schedule to create one.</div>
      </div>
    );
  }
  const COLS = '22px 1fr 140px 110px 150px 90px 76px';
  return (
    <div style={{background:'rgba(255,255,255,0.045)', backdropFilter:'blur(16px)', WebkitBackdropFilter:'blur(16px)', borderRadius:10,border:'1px solid var(--border-subtle)',overflow:'hidden'}}>
      <div style={{display:'grid',gridTemplateColumns:COLS,gap:0,background:'var(--bg-elevated)',borderBottom:'1px solid var(--border-subtle)',padding:'8px 12px'}}>
        {['','Title','Frequency','Next Due','Assigned','Status','Actions'].map((h,i)=>(
          <div key={i} style={{fontSize:'0.7rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.05em',color:'var(--text-muted)',padding:'0 4px'}}>{h}</div>
        ))}
      </div>
      {items.map(swo=>{
        const nd = fmtNext(swo.next_run_date);
        const fc = freqStyle(swo.frequency_unit);
        const initials = swo.assigned_to_name
          ? swo.assigned_to_name.split(' ').map((n:string)=>n[0]??'').slice(0,2).join('').toUpperCase() : '';
        return (
          <div key={swo.id} onClick={()=>onEdit(swo)}
            style={{display:'grid',gridTemplateColumns:COLS,gap:0,padding:'10px 12px',borderBottom:'1px solid var(--border-subtle)',cursor:'pointer',transition:'background 0.1s',alignItems:'center'}}
            onMouseEnter={e=>(e.currentTarget as HTMLElement).style.background='var(--bg-hover)'}
            onMouseLeave={e=>(e.currentTarget as HTMLElement).style.background='transparent'}
          >
            <div style={{display:'flex',justifyContent:'center'}}>
              <div style={{width:8,height:8,borderRadius:'50%',background:swo.is_active?'#10b981':'#6b7280'}}/>
            </div>
            <div style={{padding:'0 8px',minWidth:0}}>
              <div style={{fontWeight:700,fontSize:13,color:'var(--text-primary)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{swo.title}</div>
              {swo.location_label&&<div style={{fontSize:11,color:'var(--text-muted)',marginTop:1}}>{swo.location_label}</div>}
            </div>
            <div style={{padding:'0 4px'}}>
              <span style={{padding:'2px 8px',borderRadius:99,fontSize:11,fontWeight:600,background:fc.bg,color:fc.color,whiteSpace:'nowrap'}}>
                Every {swo.frequency_interval} {swo.frequency_unit}
              </span>
            </div>
            <div style={{padding:'0 4px',fontSize:12,color:nd.color,fontWeight:nd.bold?600:400,whiteSpace:'nowrap'}}>{nd.label}</div>
            <div style={{padding:'0 4px',display:'flex',alignItems:'center',gap:5,flexWrap:'wrap'}}>
              {swo.assigned_to?(
                <>
                  <span style={{width:20,height:20,borderRadius:'50%',background:getUserColor(swo.assigned_to),color:'#fff',fontSize:9,fontWeight:700,display:'inline-flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{initials}</span>
                  <span style={{fontSize:12,color:'var(--text-secondary)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{swo.assigned_to_name}</span>
                  {(() => { const fu = facilityUsers.find(u => u.id === swo.assigned_to); return fu?.department_name && fu?.department_color ? <span style={deptBadge(fu.department_color, fu.department_name)}>{fu.department_name}</span> : null; })()}
                </>
              ):<span style={{fontSize:12,color:'var(--text-muted)',fontStyle:'italic'}}>—</span>}
            </div>
            <div style={{padding:'0 4px'}}>
              <span style={{padding:'2px 8px',borderRadius:99,fontSize:11,fontWeight:600,background:swo.is_active?'rgba(16,185,129,0.15)':'rgba(107,114,128,0.15)',color:swo.is_active?'#10b981':'#9ca3af',whiteSpace:'nowrap'}}>
                {swo.is_active?'Active':'Inactive'}
              </span>
            </div>
            <div onClick={e=>e.stopPropagation()} style={{padding:'0 4px',display:'flex',gap:4}}>
              <button onClick={()=>onEdit(swo)} title="Edit"
                style={{background:'none',border:'1px solid rgba(255,255,255,0.15)',borderRadius:6,padding:'3px 7px',cursor:'pointer',color:'var(--text-muted)',display:'flex',alignItems:'center'}}>
                <Pencil size={12}/>
              </button>
              <button onClick={()=>onDelete(swo.id)} title="Delete"
                style={{background:'none',border:'1px solid rgba(239,68,68,0.3)',borderRadius:6,padding:'3px 7px',cursor:'pointer',color:'#ef4444',display:'flex',alignItems:'center'}}>
                <Trash2 size={12}/>
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Round avatar assignment selector — shared by WoCard's inline reassign and the Activity Log
// tab's "Logged by" control. Circle shows '?' (neutral) when unassigned/no value, or the matched
// user's initials on their own getUserColor tint when set. The circle itself NEVER changes shape
// or gets replaced -- clicking it opens a floating portal dropdown anchored directly below it
// (same portal-positioning approach as DarkSelect), independent of the fixed-size circle so the
// card/row layout never shifts. Previously this swapped the circle out for an inline DarkSelect,
// which (a) changed the control's shape/size on click and (b) inherited DarkSelect's own
// `placeholder` row PLUS our explicit `{value:'',label:unassignedLabel}` option -- two rows with
// the same text -- and, when the picker's value didn't match any option (e.g. defaulted to the
// current user's id when that user isn't in `facilityUsers`), DarkSelect's trigger fell back to
// rendering the raw numeric `value` string (e.g. "1") instead of a label. Building the dropdown
// list ourselves (Unassigned + facilityUsers, no placeholder-row duplication, never falls back to
// a raw id) removes all three of those bugs at once.
function UserAvatarPicker({ userId, facilityUsers, onChange, disabled, size = 20, unassignedLabel = 'Unassigned' }: {
  userId: number | null;
  facilityUsers: FacilityUser[];
  onChange: (userId: number | null) => void;
  disabled?: boolean;
  size?: number;
  unassignedLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
  const u = userId != null ? facilityUsers.find(f => f.id === userId) : null;
  const initials = u ? `${u.first_name?.[0] ?? ''}${u.last_name?.[0] ?? ''}`.toUpperCase() : '';
  const label = u ? (`${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.username) : unassignedLabel;

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target as Node))
      ) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  function toggleOpen(e: React.MouseEvent) {
    e.stopPropagation();
    if (disabled) return;
    if (!open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpen(o => !o);
  }

  function select(val: number | null) {
    onChange(val);
    setOpen(false);
  }

  const rowStyle = (active: boolean): React.CSSProperties => ({
    padding: '9px 14px', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap',
    display: 'flex', alignItems: 'center', gap: 8,
    color: active ? 'var(--accent-primary)' : 'var(--text-primary)',
    background: active ? 'var(--bg-hover)' : 'transparent',
  });
  // Small round identifier for each dropdown row -- same getUserColor tint + initials the trigger
  // circle above already uses, so a user's color is consistent between the closed trigger and the
  // open list. Unassigned gets the same neutral dashed-gray '?' circle the trigger shows when no
  // user is selected, not a color (there's no user to derive one from).
  const rowAvatarStyle = (bg: string, color: string, dashed?: boolean): React.CSSProperties => ({
    width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 8, fontWeight: 700, background: bg, color,
    border: dashed ? '1px dashed var(--border-strong)' : 'none',
  });

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        className="combo-jello-spin"
        data-tint="neutral"
        title={label}
        disabled={disabled}
        onClick={toggleOpen}
        style={{
          width: size, height: size, borderRadius: '50%', flexShrink: 0, padding: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: size * 0.4, fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
          background: u ? getUserColor(userId) : 'rgba(255,255,255,0.08)',
          color: u ? '#fff' : 'var(--text-muted)',
          border: u ? 'none' : '1px dashed var(--border-strong)',
        }}
      >
        {u ? initials : '?'}
      </button>
      {open && dropdownPos && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          onClick={e => e.stopPropagation()}
          style={{
            position: 'fixed', top: dropdownPos.top, left: dropdownPos.left,
            minWidth: 150, width: 'max-content', maxWidth: 240,
            zIndex: 10001, background: 'var(--bg-elevated)', border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 6, color: 'var(--text-primary)', maxHeight: 200, overflowY: 'auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          <div
            onClick={() => select(null)}
            style={rowStyle(userId == null)}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
            onMouseLeave={e => (e.currentTarget.style.background = userId == null ? 'var(--bg-hover)' : 'transparent')}
          >
            <span style={rowAvatarStyle('rgba(255,255,255,0.08)', 'var(--text-muted)', true)}>?</span>
            {unassignedLabel}
          </div>
          {facilityUsers.map(f => {
            const flabel = `${f.first_name ?? ''} ${f.last_name ?? ''}`.trim() || f.username;
            const finitials = `${f.first_name?.[0] ?? ''}${f.last_name?.[0] ?? ''}`.toUpperCase();
            const active = userId === f.id;
            return (
              <div
                key={f.id}
                onClick={() => select(f.id)}
                style={rowStyle(active)}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
                onMouseLeave={e => (e.currentTarget.style.background = active ? 'var(--bg-hover)' : 'transparent')}
              >
                <span style={rowAvatarStyle(getUserColor(f.id), '#fff')}>{finitials}</span>
                {flabel}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}

function WoCard({ wo, onClick, facilityUsers = [], onQuickClose, onAssign }: {
  wo: WorkOrder; onClick: () => void; facilityUsers?: FacilityUser[];
  onQuickClose?: (wo: WorkOrder) => void;
  onAssign?: (wo: WorkOrder, userId: number | null) => void;
}) {
  const overdue = isWoOverdue(wo);
  const canQuickClose = !!onQuickClose && wo.status !== 'done' && wo.status !== 'cancelled';
  // Same lock as the full detail view's assignment control and the server-side PATCH check
  // (work-orders.ts) -- once Done/Cancelled, assignment is immutable everywhere, not just here.
  const canReassign = !!onAssign && wo.status !== 'done' && wo.status !== 'cancelled';
  return (
    <div onClick={onClick}
      style={{
        background: overdue ? 'rgba(239,68,68,0.06)' : 'var(--bg-elevated)',
        border: '1px solid var(--border-subtle)',
        // Priority-colored left accent — same WO_PRIORITY_COLOR map the priority dot below
        // already uses, so the border is never invisible-transparent (previously it was
        // status-based and only ever showed a color for in_progress/on_hold, leaving every
        // open/done card — the majority in any board — with no visible left border at all).
        borderLeft: `3px solid ${WO_PRIORITY_COLOR[wo.priority] ?? '#6b7280'}`,
        borderRadius: 8, padding: '10px 12px', cursor: 'pointer',
        opacity: wo.status === 'done' ? 0.55 : 1, marginBottom: 8, transition: 'background 120ms',
      }}
      onMouseEnter={e => { if (wo.status !== 'done') (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = overdue ? 'rgba(239,68,68,0.06)' : 'var(--bg-elevated)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: 3 }}>
        <div style={{ fontSize: 11, color: 'var(--accent-primary)', fontWeight: 600 }}>{wo.wo_number}</div>
        {/* Admin/org_admin-only quick-close — moves an open/in-progress/on-hold card straight to
            Done without opening the full detail takeover. No existing action icons on this card
            to collide with (verified before adding this). */}
        {canQuickClose && (
          <button
            className="combo-jello-spin"
            data-tint="green"
            title="Mark Done"
            onClick={e => { e.stopPropagation(); onQuickClose!(wo); }}
            onMouseLeave={spinIconOut}
            style={{ background: 'none', border: '1px solid rgba(16,185,129,0.35)', borderRadius: 5, padding: '2px 4px', cursor: 'pointer', color: '#34d399', display: 'flex', alignItems: 'center', flexShrink: 0 }}
          >
            <Check size={12} className="btn-icon" />
          </button>
        )}
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 5, lineHeight: 1.35 }}>{wo.title}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: WO_PRIORITY_COLOR[wo.priority] ?? '#6b7280', flexShrink: 0 }}/>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'capitalize' }}>{wo.priority}</span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· {wo.category}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {/* Round avatar assignment control — replaces the old pencil-icon + name/"Unassigned"
            text pair with one circle (see UserAvatarPicker above). Always rendered (even once
            locked) so a completed card still shows who did the work, just non-interactive. */}
        <UserAvatarPicker
          userId={wo.assigned_to}
          facilityUsers={facilityUsers}
          onChange={(userId) => onAssign?.(wo, userId)}
          disabled={!canReassign}
        />
        {wo.due_date && (
          <span style={{ marginLeft: 'auto', fontSize: 11, color: overdue ? '#ef4444' : 'var(--text-muted)', flexShrink: 0 }}>
            {woFmtDate(wo.due_date)}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Shared page-takeover shell ───────────────────────────────────────────────
// One mechanism, reused by the WO detail view, the Add Work Order takeover, and the Add/Edit
// Scheduled ticket takeover — full viewport, Sidebar/TopBar unmounted (via useTakeoverFocusMode),
// no dimmed backdrop, gradient background matching mockups/workorder-takeover-redesign.html's
// .takeover treatment. Never build a second takeover implementation — extend this one.
const TAKEOVER_SHELL_STYLE: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'var(--bg-base)',
  backgroundImage: 'radial-gradient(1100px 600px at -5% -8%, rgba(45,120,130,0.16), transparent 60%), radial-gradient(1000px 600px at 108% 6%, rgba(120,90,220,0.10), transparent 55%)',
  zIndex: 9991, display: 'flex', flexDirection: 'column',
};
function TakeoverAccentBar() {
  return <div style={{ height: 3, background: 'linear-gradient(90deg, var(--accent-primary), var(--accent-purple))', flexShrink: 0 }} />;
}
// Renders nothing — its only job is to capture/restore focusMode across the takeover's
// mount/unmount lifecycle (see useTakeoverFocusMode). Mount this once per takeover instance.
function TakeoverFocusEffect() {
  useTakeoverFocusMode();
  return null;
}

// ─── Completed Work Orders — dedicated takeover (replaces the old "View all" behavior, which
// just set the Kanban's own status filter to "done", leaving Open/In Progress/On Hold columns
// empty on the same board). Independent query scoped to status=done regardless of whatever the
// Kanban's own filter dropdowns currently show, plus real filtering (search, date range,
// category, assigned staff) — a dedicated view, not a 3-empty-column Kanban board. ───────────
function CompletedWorkOrdersTakeover({ facilityUsers, onClose, onOpenDetail, isTopmost }: {
  facilityUsers: FacilityUser[];
  onClose: () => void;
  onOpenDetail: (wo: WorkOrder) => void;
  // This takeover stays mounted (not closed) while a WO detail opened from within it is open on
  // top -- so its own Escape handler must stay quiet in that case, or Escape would close both
  // layers at once instead of just the topmost one (the WO detail takeover has its own Escape
  // handler that's already correctly scoped to its own woDetailOpen state).
  isTopmost: boolean;
}) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['work-orders', 'completed-takeover'],
    queryFn: () => api.get<WorkOrder[]>('/work-orders', { status: 'done' }),
  });
  const completed: WorkOrder[] = data?.data ?? [];

  const categoryOptions = Array.from(new Set(completed.map(wo => wo.category))).sort();
  const assignedOptions = facilityUsers.filter(u => completed.some(wo => wo.assigned_to === u.id));

  const filtered = completed.filter(wo => {
    if (category && wo.category !== category) return false;
    if (assignedTo && String(wo.assigned_to ?? '') !== assignedTo) return false;
    // completed_date comes back as a full ISO timestamp (e.g. 2026-07-26T04:00:00.000Z), not a
    // plain YYYY-MM-DD string like the date inputs -- compare date-only slices so a WO completed
    // ON the "to" boundary day isn't wrongly excluded (its timestamp would otherwise lexically
    // sort after the plain date string).
    const completedDateOnly = wo.completed_date ? wo.completed_date.slice(0, 10) : null;
    if (dateFrom && (!completedDateOnly || completedDateOnly < dateFrom)) return false;
    if (dateTo && (!completedDateOnly || completedDateOnly > dateTo)) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!wo.title.toLowerCase().includes(q) && !wo.wo_number.toLowerCase().includes(q)) return false;
    }
    return true;
  }).sort((a, b) => (b.completed_date ?? '').localeCompare(a.completed_date ?? ''));

  useEffect(() => {
    if (!isTopmost) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, isTopmost]);

  return ReactDOM.createPortal(
    <>
      <TakeoverFocusEffect />
      <div style={TAKEOVER_SHELL_STYLE}>
        <TakeoverAccentBar />
        <div style={{ padding: '20px 28px 18px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-primary)' }}>Completed Work Orders</h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{filtered.length} of {completed.length}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={20} /></button>
        </div>

        <div style={{ padding: '16px 28px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 200px', minWidth: 160 }}>
            <label className="form-label">Search</label>
            <div className="search-bar search-input-wrapper">
              <Search size={14} className="btn-icon" color="var(--text-muted)" />
              <input type="text" placeholder="WO # or title…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>
          <div style={{ flex: '0 1 150px' }}>
            <label className="form-label">Completed from</label>
            <DatePicker value={dateFrom} onChange={v => setDateFrom(v)} placeholder="From" />
          </div>
          <div style={{ flex: '0 1 150px' }}>
            <label className="form-label">Completed to</label>
            <DatePicker value={dateTo} onChange={v => setDateTo(v)} placeholder="To" />
          </div>
          <div style={{ flex: '0 1 180px' }}>
            <label className="form-label">Category</label>
            <DarkSelect
              value={category}
              onChange={setCategory}
              placeholder="All categories"
              options={[{ value: '', label: 'All categories' }, ...categoryOptions.map(c => ({ value: c, label: c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) }))]}
            />
          </div>
          <div style={{ flex: '0 1 200px' }}>
            <label className="form-label">Assigned to</label>
            <DarkSelect
              value={assignedTo}
              onChange={setAssignedTo}
              placeholder="Anyone"
              options={[{ value: '', label: 'Anyone' }, ...assignedOptions.map(u => ({ value: String(u.id), label: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.username }))]}
            />
          </div>
          {(search || category || assignedTo || dateFrom || dateTo) && (
            <button className="btn btn-ghost btn-sm combo1" onClick={() => { setSearch(''); setCategory(''); setAssignedTo(''); setDateFrom(''); setDateTo(''); }}>
              Clear filters
            </button>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '22px 28px' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>
              {completed.length === 0 ? 'No completed work orders yet.' : 'No completed work orders match your filters.'}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
              {filtered.map(wo => <WoCard key={wo.id} wo={wo} onClick={() => onOpenDetail(wo)} facilityUsers={facilityUsers} />)}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}

// ─── Tab 5: Task Manager — Work Orders ───────────────────────────────────────
function TaskManagerTab() {
  const { user } = useAuth();
  const { facilityId } = useFacility();
  const qc = useQueryClient();
  // WO detail is always a full-page takeover now — no slide-in state, no manual toggle.
  // TakeoverFocusEffect (mounted inside the takeover portal below) handles entering/restoring
  // focusMode across its own mount/unmount lifecycle.
  const [woView, setWoView] = useState<'kanban' | 'table' | 'staff'>(() =>
    (localStorage.getItem('wo_view') as 'kanban' | 'table' | 'staff') ?? 'kanban'
  );
  const [selectedWo, setSelectedWo] = useState<WorkOrder | null>(null);
  const [woDetailOpen, setWoDetailOpen] = useState(false);
  const [woDetailTab, setWoDetailTab] = useState<'details' | 'checklist' | 'timelog' | 'notes'>('details');
  const [deleteConfirmWoId, setDeleteConfirmWoId] = useState<number | null>(null);
  const [equipmentLinking, setEquipmentLinking] = useState(false);
  const [woFilterStatus, setWoFilterStatus] = useState('');
  const [completedTakeoverOpen, setCompletedTakeoverOpen] = useState(false);
  const [woFilterPriority, setWoFilterPriority] = useState('');
  const [woCreateOpen, setWoCreateOpen] = useState(false);
  const [woForm, setWoForm] = useState({
    title: '', description: '', category: 'general',
    priority: 'medium', assigned_to: '', location: '',
    locationCustom: '',
    due_date: '', estimated_hours: '', notes: '',
    ticket_item_id: '', ticket_item_label: '',
    logbook_template_id: '',
  });
  const [woFormError, setWoFormError] = useState('');
  const [woFormSaving, setWoFormSaving] = useState(false);
  // Logbook Form tie-in (Phase 2) — filled_data keyed by the selected template's field keys.
  const [woFormValues, setWoFormValues] = useState<Record<string, string | boolean>>({});
  const [woAutoCreateFollowup, setWoAutoCreateFollowup] = useState(false);
  // Ad-hoc WO-local checklist/fields (independent of any linked logbook template) — schema only
  // at create/edit time (same as how a template's own form_schema is authored separately from
  // filling it in); values are filled in later from the Checklist tab.
  const [woCustomFields, setWoCustomFields] = useState<FormField[]>([]);
  const [customChecklistValues, setCustomChecklistValues] = useState<Record<string, string | boolean>>({});
  const [customSchemaEditorOpen, setCustomSchemaEditorOpen] = useState(false);
  const [customSchemaDraft, setCustomSchemaDraft] = useState<FormField[]>([]);
  const [customValuesSaving, setCustomValuesSaving] = useState(false);
  const [customSchemaSaving, setCustomSchemaSaving] = useState(false);
  const [tlHours, setTlHours] = useState('');
  const [tlNotes, setTlNotes] = useState('');
  // Only managers/admins get to override this (server enforces the same gate) -- regular users
  // can still log activity against any WO same as before, always attributed to themselves.
  const [tlLoggedBy, setTlLoggedBy] = useState('');
  const [tlSaving, setTlSaving] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [woAreaId, setWoAreaId] = useState<string>('');
  const [woEditMode, setWoEditMode] = useState(false);
  const [woEditForm, setWoEditForm] = useState<Partial<WorkOrder>>({});
  const [woEditAreaId, setWoEditAreaId] = useState('');
  const [woSubView, setWoSubView] = useState<'board'|'schedules'>('board');
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleEditTarget, setScheduleEditTarget] = useState<ScheduledWorkOrder | null>(null);
  const [deleteConfirmScheduleId, setDeleteConfirmScheduleId] = useState<number | null>(null);
  const { data: workOrders = [], refetch: refetchWOs } = useQuery({
    queryKey: ['work-orders', facilityId, woFilterStatus, woFilterPriority],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (woFilterStatus) params.append('status', woFilterStatus);
      if (woFilterPriority) params.append('priority', woFilterPriority);
      const res = await api.get(`/work-orders?${params.toString()}`);
      return (res as any).data as WorkOrder[];
    },
    enabled: !!facilityId,
  });

  const { data: staffSummary = [] } = useQuery({
    queryKey: ['wo-staff-summary', facilityId],
    queryFn: async () => {
      const res = await api.get('/work-orders/staff-summary');
      return (res as any).data as StaffSummary[];
    },
    enabled: !!facilityId && woView === 'staff',
  });

  const { data: facilityUsers = [] } = useQuery({
    queryKey: ['facility-users', facilityId],
    queryFn: async () => {
      const res = await api.get('/settings/users');
      return (res as any).data?.users?.filter((u: any) =>
        ['manager', 'user'].includes(u.role) &&
        u.is_active !== false &&
        u.facility_id === facilityId
      ) ?? [];
    },
    enabled: !!facilityId,
  });

  const { data: areaOptions = [] } = useQuery({
    queryKey: ['dropdown-areas', facilityId],
    queryFn: async () => {
      const res = await api.get(`/dropdowns?dropdown_type=area&facility_id=${facilityId}`);
      return (res as any).options as { id: number; label: string; value: string }[];
    },
    enabled: !!facilityId,
  });

  const { data: locationOptions = [] } = useQuery({
    queryKey: ['dropdown-locations', facilityId, woAreaId],
    queryFn: async () => {
      const parentParam = woAreaId ? `&parent_id=${woAreaId}` : '&parent_id=null';
      const res = await api.get(`/dropdowns?dropdown_type=location&facility_id=${facilityId}${parentParam}`);
      return (res as any).options as { id: number; label: string; value: string }[];
    },
    enabled: !!facilityId,
  });

  const { data: editLocationOptions = [] } = useQuery({
    queryKey: ['dropdown-locations-edit', facilityId, woEditAreaId],
    queryFn: async () => {
      if (!woEditAreaId) return [];
      const res = await api.get(
        `/dropdowns?dropdown_type=location&facility_id=${facilityId}&parent_id=${woEditAreaId}`
      );
      return (res as any).options ?? [];
    },
    enabled: !!woEditAreaId,
  });

  const { data: scheduledWOs = [], refetch: refetchSchedules } = useQuery({
    queryKey: ['scheduled-work-orders', facilityId],
    queryFn: async () => {
      const res = await api.get('/scheduled-work-orders');
      return (res as any).data as ScheduledWorkOrder[];
    },
    enabled: !!facilityId && facilityId > 0,
  });

  const { data: ticketItems = [] } = useQuery({
    queryKey: ['ticket-items', facilityId],
    queryFn: async () => {
      const res = await api.get(`/dropdowns?dropdown_type=ticket_item&org_id=${user?.orgId}`);
      return (res as any).options ?? [];
    },
    enabled: !!user?.orgId,
  });

  // Logbook Form tie-in (Phase 2) — server already applies the same department-gated
  // visibility as Phase 1's Logbook Templates workspace (logbook-templates.ts GET /,
  // ?formsOnly=true). Disaster Manual is excluded automatically — it's never in this table.
  const { data: logbookFormOptions = [] } = useQuery({
    queryKey: ['logbook-templates', 'forms-only'],
    queryFn: async () => {
      const res = await api.get<LogbookTemplateOption[]>('/logbook-templates', { formsOnly: 'true' });
      return (res as any).data as LogbookTemplateOption[];
    },
    enabled: !!facilityId,
  });
  const selectedLogbookTemplate = (logbookFormOptions as LogbookTemplateOption[]).find(
    (t) => String(t.id) === woForm.logbook_template_id
  );
  // Same real-time range check as the Checklist tab's useWoChecklistData, mirrored here since the
  // Add WO form's template preview keeps its own separate values/state (woFormValues) rather than
  // sharing that hook. Auto-checks "auto-create a follow-up ticket" on the 0->nonzero transition
  // only (see hadViolationsRef's comment in useWoChecklistData for why), and naturally re-zeroes
  // itself when the template selection changes since that already resets woFormValues to {}.
  const woFormViolations = findLogbookRangeViolations(selectedLogbookTemplate?.form_schema ?? null, woFormValues);
  const [woConfirmOpen, setWoConfirmOpen] = useState(false);
  const woHadViolationsRef = useRef(false);
  useEffect(() => {
    const hasViolations = woFormViolations.length > 0;
    if (hasViolations && !woHadViolationsRef.current) setWoAutoCreateFollowup(true);
    woHadViolationsRef.current = hasViolations;
  }, [woFormViolations.length]);

  const { data: woDetail, refetch: refetchDetail } = useQuery({
    queryKey: ['work-order-detail', selectedWo?.id],
    queryFn: async () => {
      const res = await api.get(`/work-orders/${selectedWo!.id}`);
      return (res as any).data;
    },
    enabled: !!selectedWo?.id && woDetailOpen,
  });

  const timeLogs: WoTimeLog[] = woDetail?.time_logs ?? [];
  const wkNotes: WoNote[] = woDetail?.notes ?? [];
  const woEquipmentLinks: BomEquipmentOption[] = woDetail?.equipment_links ?? [];

  // Checklist tab data + live progress — hook lives here (not inside the tab body) so the
  // sidebar's Progress section and the "Linked log template" card can read the exact same
  // template/values state as the Checklist tab, with no separate fetch or duplicated ratio math.
  const woChecklist = useWoChecklistData(selectedWo?.id, selectedWo?.logbook_template_id ?? null, woDetailOpen);
  // Linked-template checklist is only a thing once its template + non-empty form_schema are
  // confirmed loaded — a distinct concept from the ad-hoc checklist below (mirrors the mockup's
  // separate "Task Checklist" vs "Linked log template" cards).
  const templateSchema = woChecklist.template?.form_schema;
  const templateSchemaHasFields = !!templateSchema && (
    templateSchema.form_mode === 'single_form'
      ? (templateSchema.fixed_fields?.length ?? 0) > 0
      : (templateSchema.rows?.length ?? 0) > 0 && (templateSchema.row_fields?.length ?? 0) > 0
  );
  const templateChecklistVisible = selectedWo?.logbook_template_id != null && templateSchemaHasFields;
  const templateProgress = countLogbookCheckboxProgress(templateSchema, woChecklist.values);
  const templateDoneCount = templateProgress.done;
  const templateCheckboxFieldsTotal = templateProgress.total;

  // Ad-hoc WO-local checklist — always available regardless of any linked template.
  const hasCustomSchema = (selectedWo?.custom_form_schema?.length ?? 0) > 0;
  const customCheckboxFields = (selectedWo?.custom_form_schema ?? []).filter(f => f.type === 'checkbox');
  const customDoneCount = customCheckboxFields.filter(f => !!customChecklistValues[f.key]).length;

  // Combined progress — the sidebar shows one meter across both sources.
  const checklistDoneCount = templateDoneCount + customDoneCount;
  const checklistTotalCount = templateCheckboxFieldsTotal + customCheckboxFields.length;

  useEffect(() => {
    setWoEditMode(false);
    setWoEditForm({});
    setCustomChecklistValues(selectedWo?.custom_form_values ?? {});
    setCustomSchemaDraft(selectedWo?.custom_form_schema ?? []);
    setCustomSchemaEditorOpen(false);
  }, [selectedWo?.id]);

  const isManager = ['admin', 'org_admin', 'manager'].includes(user?.role ?? '');
  // Narrower than isManager (excludes plain 'manager') — same admin/org_admin-only gate used
  // throughout this session's work (Settings.tsx isAdmin, vendors.ts tier gates). Reused here for
  // the quick-close button, not a new permission tier.
  const isWoAdmin = user?.role === 'admin' || user?.role === 'org_admin';

  // Same GET /inventory/bom-equipment `entries` list Learning Hub's equipment picker reads —
  // real bom_equipment rows only (see EquipmentLinkPicker.tsx).
  const { data: equipOptionsRaw } = useQuery({
    queryKey: ['bom-equipment-options'],
    queryFn: async () => {
      const res = await api.get('/inventory/bom-equipment');
      return ((res as any).entries ?? []) as BomEquipmentOption[];
    },
    enabled: isManager && woDetailOpen,
  });
  const woLinkedEquipmentIds = new Set(woEquipmentLinks.map((l) => l.id));
  const woEquipOptions = (equipOptionsRaw ?? []).filter((o) => !woLinkedEquipmentIds.has(o.id));

  async function handleLinkWoEquipment(equipmentId: number) {
    if (!selectedWo) return;
    setEquipmentLinking(true);
    try {
      await api.post(`/work-orders/${selectedWo.id}/equipment-links`, { equipment_id: equipmentId });
      await refetchDetail();
    } finally {
      setEquipmentLinking(false);
    }
  }
  async function handleUnlinkWoEquipment(equipmentId: number) {
    if (!selectedWo) return;
    await api.delete(`/work-orders/${selectedWo.id}/equipment-links/${equipmentId}`);
    await refetchDetail();
  }

  async function handleQuickCloseWo(wo: WorkOrder) {
    try {
      await api.patch(`/work-orders/${wo.id}`, { status: 'done' });
      qc.invalidateQueries({ queryKey: ['work-orders'] });
    } catch (err) {
      console.error('[wo-quick-close]', err);
    }
  }
  // WoCard itself already refuses to show its inline reassign control once a WO is done/cancelled
  // (canReassign check), but the server enforces the actual lock (work-orders.ts PATCH) so a
  // rejected request here is a real, expected outcome, not just a defensive catch.
  async function handleAssignWo(wo: WorkOrder, userId: number | null) {
    try {
      await api.patch(`/work-orders/${wo.id}`, { assigned_to: userId });
      qc.invalidateQueries({ queryKey: ['work-orders'] });
    } catch (err) {
      console.error('[wo-inline-assign]', err);
    }
  }
  const assignedFacilityUser = selectedWo ? (facilityUsers as FacilityUser[]).find(u => u.id === selectedWo.assigned_to) : undefined;
  const assignedInitials = assignedFacilityUser
    ? `${assignedFacilityUser.first_name?.[0] ?? ''}${assignedFacilityUser.last_name?.[0] ?? ''}`.toUpperCase()
    : '';

  async function handleStatusChange(woId: number, newStatus: string) {
    setStatusSaving(true);
    try {
      await api.patch(`/work-orders/${woId}`, { status: newStatus });
      await refetchWOs();
      if (woDetailOpen) await refetchDetail();
      if (selectedWo?.id === woId)
        setSelectedWo(prev => prev ? { ...prev, status: newStatus as WorkOrder['status'] } : null);
    } catch { /* silent */ }
    finally { setStatusSaving(false); }
  }

  // Takes no event -- called either directly from the form's onSubmit wrapper (no violations to
  // confirm) or from the confirmation dialog's "Save & Create Ticket" button (already confirmed).
  async function handleCreateWo() {
    if (!woForm.title.trim()) { setWoFormError('Title is required'); return; }
    setWoFormError(''); setWoFormSaving(true);
    try {
      const created = await api.post<WorkOrder>('/work-orders', {
        title: woForm.title,
        description: woForm.description || null,
        category: woForm.category,
        priority: woForm.priority,
        assigned_to: woForm.assigned_to ? Number(woForm.assigned_to) : null,
        location: woForm.location === '__other__' ? (woForm.locationCustom || null) : (woForm.location || null),
        due_date: woForm.due_date || null,
        estimated_hours: woForm.estimated_hours ? Number(woForm.estimated_hours) : null,
        notes: woForm.notes || null,
        ticket_item_id: woForm.ticket_item_id ? Number(woForm.ticket_item_id) : null,
        ticket_item_label: woForm.ticket_item_label || null,
        logbook_template_id: woForm.logbook_template_id ? Number(woForm.logbook_template_id) : null,
        custom_form_schema: woCustomFields.filter(f => f.label.trim()).length > 0
          ? woCustomFields.filter(f => f.label.trim()) : null,
      });
      // Filled entry is its own independent record (Logbooks Phase 3) — filled_by/filled_at
      // come from this actual submitting user/time, not any work_orders completion side-channel
      // (work_orders has no completed_by column at all, per this session's research).
      //
      // Only create it if the user actually touched a field in the create modal's preview —
      // creating an entry unconditionally (even with empty filled_data) meant every new WO with
      // a template attached already "had an entry" the instant it was created, so the Checklist
      // tab opened in disabled/view-mode (existingEntry truthy, editing false) on the very first
      // visit instead of the fillable state it's supposed to start in.
      if (selectedLogbookTemplate && woForm.logbook_template_id && Object.keys(woFormValues).length > 0) {
        try {
          await api.post('/logbook-filled-entries', {
            template_id: Number(woForm.logbook_template_id),
            work_order_id: created.data?.id ?? null,
            filled_data: woFormValues,
            auto_create_followup: woAutoCreateFollowup,
          });
        } catch { /* WO itself already saved — don't block on the filled-entry write */ }
      }
      await refetchWOs();
      setWoCreateOpen(false);
      setWoAreaId('');
      setWoAutoCreateFollowup(false);
      setWoConfirmOpen(false);
      setWoForm({ title: '', description: '', category: 'general', priority: 'medium', assigned_to: '', location: '', locationCustom: '', due_date: '', estimated_hours: '', notes: '', ticket_item_id: '', ticket_item_label: '', logbook_template_id: '' });
      setWoFormValues({});
      setWoCustomFields([]);
    } catch (err: any) {
      setWoFormError(err?.response?.data?.error || 'Failed to create work order');
    } finally { setWoFormSaving(false); }
  }

  async function handleLogTime() {
    if (!selectedWo || !tlHours || Number(tlHours) <= 0) return;
    setTlSaving(true);
    try {
      await api.post(`/work-orders/${selectedWo.id}/time-logs`, {
        hours: Number(tlHours), notes: tlNotes || null,
        user_id: tlLoggedBy ? Number(tlLoggedBy) : undefined,
      });
      setTlHours(''); setTlNotes(''); setTlLoggedBy('');
      await refetchDetail(); await refetchWOs();
    } catch { /* silent */ }
    finally { setTlSaving(false); }
  }

  async function handleAddNote() {
    if (!selectedWo || !noteContent.trim()) return;
    setNoteSaving(true);
    try {
      await api.post(`/work-orders/${selectedWo.id}/notes`, { content: noteContent });
      setNoteContent('');
      await refetchDetail();
    } catch { /* silent */ }
    finally { setNoteSaving(false); }
  }

  // Ad-hoc WO-local checklist — always available regardless of any linked template. Values save
  // immediately (no separate archive/edit-gating like the linked-template flow — this is WO-local
  // and always directly editable), schema edits are an explicit Save/Cancel step.
  async function saveCustomValues(nextValues: Record<string, string | boolean>) {
    if (!selectedWo) return;
    setCustomChecklistValues(nextValues);
    setCustomValuesSaving(true);
    try {
      await api.patch(`/work-orders/${selectedWo.id}`, { custom_form_values: nextValues });
      await refetchWOs();
      const updated = workOrders.find(w => w.id === selectedWo.id);
      if (updated) setSelectedWo({ ...updated, custom_form_values: nextValues });
    } catch { /* silent */ }
    finally { setCustomValuesSaving(false); }
  }

  async function saveCustomSchema() {
    if (!selectedWo) return;
    setCustomSchemaSaving(true);
    try {
      const cleaned = customSchemaDraft.filter(f => f.label.trim());
      await api.patch(`/work-orders/${selectedWo.id}`, { custom_form_schema: cleaned.length > 0 ? cleaned : null });
      await refetchWOs();
      const updated = workOrders.find(w => w.id === selectedWo.id);
      if (updated) setSelectedWo({ ...updated, custom_form_schema: cleaned.length > 0 ? cleaned : null });
      setCustomSchemaEditorOpen(false);
    } catch { /* silent */ }
    finally { setCustomSchemaSaving(false); }
  }

  function openDetail(wo: WorkOrder) {
    setSelectedWo(wo); setWoDetailOpen(true); setWoDetailTab('details');
  }

  function closeDetail() {
    setWoDetailOpen(false);
  }

  // Escape closes the WO detail takeover — same unconditional convention already used by
  // PdfViewer.tsx's full-screen readers (no special-casing for focused text inputs/textareas;
  // matching that existing pattern rather than inventing a new one).
  useEffect(() => {
    if (!woDetailOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closeDetail(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [woDetailOpen]);

  // Same Escape-to-close convention for the Add Work Order takeover.
  useEffect(() => {
    if (!woCreateOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') { setWoCreateOpen(false); setWoFormError(''); } };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [woCreateOpen]);

  const KANBAN_COLS = [
    { key: 'open',        label: 'Open',        color: 'var(--text-secondary)' },
    { key: 'in_progress', label: 'In Progress',  color: 'var(--accent-primary)' },
    { key: 'on_hold',     label: 'On Hold',      color: 'var(--accent-warning)' },
    { key: 'done',        label: 'Done',         color: 'var(--accent-success)' },
  ];

  void qc;

  return (
    <div>
      {/* ── Toolbar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', marginBottom: 12, flexWrap: 'wrap', borderBottom: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ display: 'flex', border: '1px solid var(--border-default)', borderRadius: 8, overflow: 'hidden' }}>
            {(['kanban', 'table', 'staff'] as const).map(v => (
              <button key={v} onClick={() => { setWoView(v); setWoSubView('board'); localStorage.setItem('wo_view', v); }}
                style={{ padding: '5px 12px', fontSize: 12, fontWeight: 500, border: 'none', cursor: 'pointer',
                  background: woSubView === 'board' && woView === v ? 'var(--accent-primary)' : 'transparent',
                  color: woSubView === 'board' && woView === v ? '#fff' : 'var(--text-secondary)',
                  borderRight: v !== 'staff' ? '1px solid var(--border-default)' : 'none' }}>
                {v === 'kanban' ? '⊞ Kanban' : v === 'table' ? '☰ Table' : '👤 By Staff'}
              </button>
            ))}
          </div>
          <div style={{ width: 1, height: 24, background: 'var(--border-default)', flexShrink: 0 }}/>
          <button onClick={() => setWoSubView(woSubView === 'schedules' ? 'board' : 'schedules')}
            style={{ padding: '5px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer', borderRadius: 8,
              background: woSubView === 'schedules' ? 'rgba(139,92,246,0.12)' : 'transparent',
              color: woSubView === 'schedules' ? '#8b5cf6' : 'var(--text-secondary)',
              border: woSubView === 'schedules' ? '1px solid rgba(139,92,246,0.3)' : '1px solid var(--border-default)' }}>
            🔁 Schedules
          </button>
        </div>
        {woSubView === 'board' && (<>
          <select value={woFilterStatus} onChange={e => setWoFilterStatus(e.target.value)}
            style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', color: 'var(--text-secondary)' }}>
            <option value="">All status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="on_hold">On Hold</option>
            <option value="done">Done</option>
          </select>
          <select value={woFilterPriority} onChange={e => setWoFilterPriority(e.target.value)}
            style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', color: 'var(--text-secondary)' }}>
            <option value="">All priority</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </>)}
        {isManager && woSubView === 'board' && (
          <button onClick={() => setWoCreateOpen(true)}
            style={{ marginLeft: 'auto', padding: '5px 14px', fontSize: 12, fontWeight: 500, background: 'var(--accent-primary)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
            + New WO
          </button>
        )}
        {isManager && woSubView === 'schedules' && (
          <button onClick={() => { setScheduleEditTarget(null); setScheduleModalOpen(true); }}
            style={{ marginLeft: 'auto', padding: '5px 14px', fontSize: 12, fontWeight: 500, background: 'rgba(139,92,246,0.85)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
            + New Schedule
          </button>
        )}
      </div>

      {/* ── Schedules ── */}
      {woSubView === 'schedules' && (
        <ScheduledWoListView
          items={scheduledWOs as ScheduledWorkOrder[]}
          onEdit={swo => { setScheduleEditTarget(swo); setScheduleModalOpen(true); }}
          onDelete={id => setDeleteConfirmScheduleId(id)}
          facilityUsers={facilityUsers as FacilityUser[]}
        />
      )}

      {/* ── Kanban ── */}
      {woSubView === 'board' && woView === 'kanban' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, alignItems: 'start' }}>
          {KANBAN_COLS.map(col => {
            const colWOs = workOrders.filter(wo => wo.status === col.key);
            const isDone = col.key === 'done';
            const shown = isDone ? colWOs.slice(0, 5) : colWOs;
            return (
              <div key={col.key} style={{ background: 'rgba(255,255,255,0.045)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', borderRadius: 10, padding: '10px 10px 12px', border: '1px solid var(--border-subtle)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid var(--border-subtle)' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: col.color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{col.label}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, background: 'var(--bg-elevated)', color: 'var(--text-muted)', borderRadius: 999, padding: '1px 7px', border: '1px solid var(--border-subtle)' }}>{colWOs.length}</span>
                </div>
                {shown.map(wo => <WoCard key={wo.id} wo={wo} onClick={() => openDetail(wo)} facilityUsers={facilityUsers as FacilityUser[]} onQuickClose={isWoAdmin ? handleQuickCloseWo : undefined} onAssign={isManager ? handleAssignWo : undefined} />)}
                {isDone && colWOs.length > 5 && (
                  <button onClick={() => setCompletedTakeoverOpen(true)}
                    style={{ width: '100%', padding: 6, fontSize: 12, color: 'var(--accent-primary)', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'center' }}>
                    View all {colWOs.length} →
                  </button>
                )}
                {colWOs.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '20px 0', fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No items</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Table ── */}
      {woSubView === 'board' && woView === 'table' && (
        <div style={{ background: 'rgba(255,255,255,0.045)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', borderRadius: 10, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border-subtle)' }}>
                {['WO#', 'Title', 'Status', 'Priority', 'Category', 'Assigned', 'Due', 'Hrs'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
                {isWoAdmin && <th style={{ padding: '8px 12px' }} />}
              </tr>
            </thead>
            <tbody>
              {workOrders.length === 0 && (
                <tr><td colSpan={isWoAdmin ? 9 : 8} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>No work orders yet</td></tr>
              )}
              {workOrders.map(wo => {
                const overdue = isWoOverdue(wo);
                const dueIn7 = wo.due_date && !overdue && (new Date(wo.due_date).getTime() - Date.now()) < 7 * 86400000;
                return (
                  <tr key={wo.id} onClick={() => openDetail(wo)}
                    style={{ background: overdue ? 'rgba(239,68,68,0.06)' : 'transparent', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = overdue ? 'rgba(239,68,68,0.1)' : 'var(--bg-hover)'}
                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = overdue ? 'rgba(239,68,68,0.06)' : 'transparent'}
                  >
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: 'var(--accent-primary)', fontSize: 12, whiteSpace: 'nowrap' }}>{wo.wo_number}</td>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: 'var(--text-primary)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wo.title}</td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: (WO_STATUS_COLOR[wo.status] ?? '#6b7280') + '22', color: WO_STATUS_COLOR[wo.status] ?? '#6b7280' }}>
                        {WO_STATUS_LABEL[wo.status] ?? wo.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: WO_PRIORITY_COLOR[wo.priority] ?? '#6b7280' }}/>
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{wo.priority}</span>
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-secondary)', fontSize: 12 }}>{wo.category}</td>
                    <td style={{ padding: '10px 12px', fontSize: 12, maxWidth: 160 }}>
                      {wo.assigned_to ? (() => {
                        const fu = (facilityUsers as FacilityUser[]).find(u => u.id === wo.assigned_to);
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                            <span style={{ width: 20, height: 20, borderRadius: '50%', background: getUserColor(wo.assigned_to), color: '#fff', fontSize: 9, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {(wo.assignee_name || wo.assigned_to_name || '').split(' ').map((n: string) => n[0] ?? '').slice(0, 2).join('').toUpperCase()}
                            </span>
                            <span style={{ color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wo.assignee_name || wo.assigned_to_name}</span>
                            {fu?.department_name && fu?.department_color && (
                              <span style={deptBadge(fu.department_color, fu.department_name)}>{fu.department_name}</span>
                            )}
                          </div>
                        );
                      })() : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: 12, color: overdue ? '#ef4444' : dueIn7 ? '#f59e0b' : 'var(--text-secondary)' }}>
                      {woFmtDate(wo.due_date)}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-secondary)', fontSize: 12 }}>
                      {Number(wo.total_hours || wo.actual_hours || 0).toFixed(1)}h
                    </td>
                    {isWoAdmin && (
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        {wo.status !== 'done' && wo.status !== 'cancelled' && (
                          <button
                            className="combo-jello-spin"
                            data-tint="green"
                            title="Mark Done"
                            onClick={e => { e.stopPropagation(); handleQuickCloseWo(wo); }}
                            onMouseLeave={spinIconOut}
                            style={{ background: 'none', border: '1px solid rgba(16,185,129,0.35)', borderRadius: 5, padding: '3px 5px', cursor: 'pointer', color: '#34d399', display: 'flex', alignItems: 'center' }}
                          >
                            <Check size={13} className="btn-icon" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── By Staff ── */}
      {woSubView === 'board' && woView === 'staff' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {(staffSummary as StaffSummary[]).map(staff => {
            const staffWOs = workOrders.filter(wo => wo.assigned_to === staff.id);
            const initials = staff.name.split(' ').map((n: string) => n[0] ?? '').slice(0, 2).join('').toUpperCase();
            return (
              <div key={staff.id}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ width: 32, height: 32, borderRadius: '50%', background: getUserColor(staff.id), color: '#fff', fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{initials}</span>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{staff.name}</span>
                    {(() => { const fu = (facilityUsers as FacilityUser[]).find(u => u.id === staff.id); return fu?.department_name ? <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{fu.department_name}</div> : null; })()}
                  </div>
                  <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: 'rgba(59,130,246,0.15)', color: 'var(--accent-primary)' }}>{staff.active_count} active</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{Number(staff.hours_this_week).toFixed(1)}h this week</span>
                </div>
                {staffWOs.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', paddingLeft: 42 }}>No work orders assigned</div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, paddingLeft: 42 }}>
                    {staffWOs.map(wo => <WoCard key={wo.id} wo={wo} onClick={() => openDetail(wo)} facilityUsers={facilityUsers as FacilityUser[]} onQuickClose={isWoAdmin ? handleQuickCloseWo : undefined} onAssign={isManager ? handleAssignWo : undefined} />)}
                  </div>
                )}
              </div>
            );
          })}
          {(() => {
            const unassigned = workOrders.filter(wo => !wo.assigned_to && wo.status !== 'done' && wo.status !== 'cancelled');
            if (unassigned.length === 0) return null;
            return (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(239,68,68,0.15)', color: '#ef4444', fontSize: 18, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>?</span>
                  <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>Unassigned</span>
                  <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>{unassigned.length}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, paddingLeft: 42 }}>
                  {unassigned.map(wo => <WoCard key={wo.id} wo={wo} onClick={() => openDetail(wo)} facilityUsers={facilityUsers as FacilityUser[]} onQuickClose={isWoAdmin ? handleQuickCloseWo : undefined} onAssign={isManager ? handleAssignWo : undefined} />)}
                </div>
              </div>
            );
          })()}
          {(staffSummary as StaffSummary[]).length === 0 && workOrders.filter(wo => !wo.assigned_to).length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 13 }}>
              <Users size={36} style={{ opacity: 0.25, marginBottom: 8 }}/><br/>No staff data
            </div>
          )}
        </div>
      )}

      {/* ── Detail Panel — always a full-page takeover, no slide-in state ──
          No backdrop: Sidebar/TopBar are unmounted via useTakeoverFocusMode (TakeoverFocusEffect
          below), so there's no "page behind" left to dim. No box-shadow/border/narrow-width/
          toggle button either — those were slide-in-panel chrome for an intermediate state that
          no longer exists; the only way in is the takeover itself, and the only way out is the X
          button or Escape. */}
      {woDetailOpen && selectedWo && ReactDOM.createPortal(
        <>
          <TakeoverFocusEffect />
          {/* Two-column takeover layout (Variant A — Properties Sidebar) — main content left,
              sticky properties panel right, stacking below ~1000px. Media query needs a real
              <style> block since inline styles can't express it. */}
          <style>{`
            .wo-tk-body { flex: 1; display: grid; grid-template-columns: 1fr 340px; min-height: 0; }
            .wo-tk-main { padding: 22px 28px; overflow-y: auto; min-width: 0; }
            .wo-tk-side { border-left: 1px solid var(--border-subtle); background: rgba(0,0,0,0.13); padding: 22px 24px; overflow-y: auto; display: flex; flex-direction: column; gap: 20px; }
            @media (max-width: 1000px) {
              .wo-tk-body { grid-template-columns: 1fr; overflow-y: auto; }
              .wo-tk-main, .wo-tk-side { overflow-y: visible; }
              .wo-tk-side { border-left: none; border-top: 1px solid var(--border-subtle); }
            }
          `}</style>
          {/* WO detail is the deepest drill-down level in this app (you always open it FROM some
              listing/board, never the reverse) — it can be opened from the base Task Manager page
              OR from within CompletedWorkOrdersTakeover (still mounted underneath, not closed —
              see that takeover's onOpenDetail below). A bumped z-index (base takeovers are 9991)
              guarantees it always paints on top regardless of which context it was opened from,
              rather than relying on DOM/portal append order. */}
          <div style={{ ...TAKEOVER_SHELL_STYLE, zIndex: 9992 }}>
            <TakeoverAccentBar />
            {/* Header — transparent so the takeover's own gradient background shows through
                (mockup's .a-head has no background of its own; the previous solid #0d1526 navy
                strip didn't match). */}
            <div style={{ padding: '20px 28px 18px', borderBottom: '1px solid var(--border-subtle)', background: 'transparent', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--accent-primary)', fontWeight: 600, letterSpacing: '0.03em' }}>{selectedWo.wo_number}</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-primary)', lineHeight: 1.25, margin: '4px 0 10px' }}>{selectedWo.title}</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 11px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.02em', background: (WO_STATUS_COLOR[selectedWo.status] ?? '#6b7280') + '22', color: WO_STATUS_COLOR[selectedWo.status] ?? '#6b7280' }}>
                      ● {WO_STATUS_LABEL[selectedWo.status] ?? selectedWo.status}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 11px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.02em', background: (WO_PRIORITY_COLOR[selectedWo.priority] ?? '#6b7280') + '22', color: WO_PRIORITY_COLOR[selectedWo.priority] ?? '#6b7280' }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: WO_PRIORITY_COLOR[selectedWo.priority] ?? '#6b7280' }}/>
                      <span style={{ textTransform: 'capitalize' }}>{selectedWo.priority} priority</span>
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 11px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.02em', background: 'rgba(107,114,128,0.15)', color: '#9ca3af', textTransform: 'capitalize' }}>
                      {selectedWo.category.replace('_', ' ')}
                    </span>
                    {selectedWo.is_recurring && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 11px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.02em', background: 'rgba(139,92,246,0.14)', color: '#a78bfa' }}>
                        <Repeat size={11}/> Recurring
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {isManager && !woEditMode && selectedWo.status === 'open' && (
                    <button className="btn btn-primary combo2" disabled={statusSaving} onMouseLeave={spinIconOut}
                      onClick={() => handleStatusChange(selectedWo.id, 'in_progress')}>
                      <Play size={14} className="btn-icon"/> Start Work
                    </button>
                  )}
                  {isManager && !woEditMode && selectedWo.status === 'in_progress' && (
                    <>
                      <button className="btn btn-ghost combo1" disabled={statusSaving} style={{ color: 'var(--accent-warning)', borderColor: 'rgba(245,158,11,0.3)' }}
                        onClick={() => handleStatusChange(selectedWo.id, 'on_hold')}>
                        Put On Hold
                      </button>
                      <button className="btn btn-primary combo2" disabled={statusSaving} onMouseLeave={spinIconOut} style={{ background: 'var(--accent-success)', borderColor: 'var(--accent-success)' }}
                        onClick={() => handleStatusChange(selectedWo.id, 'done')}>
                        <Check size={14} className="btn-icon"/> Complete Work
                      </button>
                    </>
                  )}
                  {isManager && !woEditMode && selectedWo.status === 'on_hold' && (
                    <>
                      <button className="btn btn-ghost combo1" disabled={statusSaving}
                        onClick={() => handleStatusChange(selectedWo.id, 'in_progress')}>
                        Resume
                      </button>
                      <button className="btn btn-primary combo2" disabled={statusSaving} onMouseLeave={spinIconOut} style={{ background: 'var(--accent-success)', borderColor: 'var(--accent-success)' }}
                        onClick={() => handleStatusChange(selectedWo.id, 'done')}>
                        <Check size={14} className="btn-icon"/> Complete Work
                      </button>
                    </>
                  )}
                  {isManager && !woEditMode && selectedWo.status === 'done' && (
                    <button className="btn btn-ghost combo1" disabled={statusSaving}
                      onClick={() => handleStatusChange(selectedWo.id, 'open')}>
                      ↩ Reopen
                    </button>
                  )}
                  {/* Edit / Delete — mockup gives both a hover jello-bounce (its raw .jello demo
                      class); the app's real equivalent is combo-jello-spin with the data-tint
                      this exact action already maps to elsewhere (BUTTON_SYSTEM.md's data-tint
                      reference table: Edit → neutral, Delete/Remove → red). combo-jello-spin only
                      supplies the bounce/spin mechanics + hover tint — resting border/color still
                      comes from inline style, same as every other combo-jello-spin button. */}
                  {isManager && !woEditMode && (
                    <button className="btn btn-ghost combo-jello-spin" data-tint="neutral" onMouseLeave={spinIconOut}
                      onClick={() => {
                        setWoEditMode(true);
                        setWoEditForm({
                          title: selectedWo?.title ?? '',
                          description: selectedWo?.description ?? '',
                          category: selectedWo?.category ?? 'general',
                          priority: selectedWo?.priority ?? 'medium',
                          location: selectedWo?.location ?? '',
                          due_date: selectedWo?.due_date?.slice(0,10) ?? '',
                          estimated_hours: selectedWo?.estimated_hours ?? undefined,
                          notes: selectedWo?.notes ?? '',
                          ticket_item_id: selectedWo?.ticket_item_id ?? null,
                          ticket_item_label: selectedWo?.ticket_item_label ?? '',
                          custom_form_schema: selectedWo?.custom_form_schema ?? [],
                        });
                      }}>
                      <Pencil size={14} className="btn-icon"/> Edit
                    </button>
                  )}
                  {isManager && woEditMode && (
                    <button className="btn btn-ghost combo-cancel-jello" onClick={() => setWoEditMode(false)}>
                      Cancel
                    </button>
                  )}
                  {isManager && !woEditMode && (
                    <button className="combo-jello-spin" data-tint="red" onMouseLeave={spinIconOut} title="Delete work order"
                      style={{ width: 36, height: 36, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171' }}
                      onClick={() => setDeleteConfirmWoId(selectedWo.id)}>
                      <Trash2 size={16} className="btn-icon"/>
                    </button>
                  )}
                  <button onClick={closeDetail} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, flexShrink: 0 }}>
                    <X size={20}/>
                  </button>
                </div>
              </div>
            </div>

            {/* Body — main content (left) + sticky properties sidebar (right) */}
            <div className="wo-tk-body">
              <div className="wo-tk-main">
                {/* Pill tabs — shared app-standard pill-tab-container/pill-tab classes (same as
                    Reminders/Archived/Compliance PM/Task Manager), with icons + the existing
                    order-tab-icon hover-wiggle loop, not a one-off tab implementation. */}
                <div className="pill-tab-container" style={{ marginBottom: 20 }}>
                  <button className={`order-tab pill-tab${woDetailTab === 'details' ? ' active' : ''}`} onClick={() => setWoDetailTab('details')}>
                    <FileText size={14} className="btn-icon order-tab-icon"/> Details
                  </button>
                  {/* Checklist tab is always available — an ad-hoc WO-local checklist is a
                      separate, always-present capability from any linked template (mockup's
                      "Task Checklist" vs "Linked log template" distinction), not gated on one. */}
                  <button className={`order-tab pill-tab${woDetailTab === 'checklist' ? ' active' : ''}`} onClick={() => setWoDetailTab('checklist')}>
                    <ListChecks size={14} className="btn-icon order-tab-icon"/> Checklist
                    {checklistTotalCount > 0 && (
                      <span style={{ fontSize: '0.66rem', background: 'rgba(255,255,255,0.08)', borderRadius: 99, padding: '1px 7px', fontFamily: 'monospace' }}>
                        {checklistDoneCount}/{checklistTotalCount}
                      </span>
                    )}
                  </button>
                  <button className={`order-tab pill-tab${woDetailTab === 'timelog' ? ' active' : ''}`} onClick={() => setWoDetailTab('timelog')}>
                    <Clock size={14} className="btn-icon order-tab-icon"/> Activity Log
                  </button>
                  <button className={`order-tab pill-tab${woDetailTab === 'notes' ? ' active' : ''}`} onClick={() => setWoDetailTab('notes')}>
                    <StickyNote size={14} className="btn-icon order-tab-icon"/> Notes
                  </button>
                </div>

                {woDetailTab === 'details' && (
                  woEditMode ? (
                    <div className="card" style={{ padding: '20px 22px' }}>
                      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                        <div>
                          <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--text-muted)', marginBottom:3 }}>Title *</div>
                          <input
                            value={woEditForm.title ?? ''}
                            onChange={e => setWoEditForm(p => ({...p, title: e.target.value}))}
                            style={{ width:'100%', fontSize:13, padding:'6px 10px', borderRadius:6, background:'var(--bg-elevated)', border:'1px solid var(--border-default)', color:'var(--text-primary)' }}
                          />
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                          <div>
                            <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--text-muted)', marginBottom:3 }}>Category</div>
                            <select
                              value={woEditForm.category ?? ''}
                              onChange={e => setWoEditForm(p => ({...p, category: e.target.value}))}
                              style={{ width:'100%', fontSize:12, padding:'6px 8px', borderRadius:6, background:'var(--bg-elevated)', border:'1px solid var(--border-default)', color:'var(--text-primary)' }}>
                              {['general','plumbing','electrical','hvac','life_safety','vendor','doors','flooring','other'].map(c => (
                                <option key={c} value={c}>{c.replace('_',' ')}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--text-muted)', marginBottom:3 }}>Priority</div>
                            <select
                              value={woEditForm.priority ?? ''}
                              onChange={e => setWoEditForm(p => ({...p, priority: e.target.value as any}))}
                              style={{ width:'100%', fontSize:12, padding:'6px 8px', borderRadius:6, background:'var(--bg-elevated)', border:'1px solid var(--border-default)', color:'var(--text-primary)' }}>
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High</option>
                              <option value="urgent">Urgent</option>
                            </select>
                          </div>
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                          <div>
                            <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--text-muted)', marginBottom:3 }}>Area</div>
                            <select
                              value={woEditAreaId}
                              onChange={e => {
                                setWoEditAreaId(e.target.value);
                                setWoEditForm(p => ({...p, location:''}));
                              }}
                              style={{ width:'100%', fontSize:12, padding:'6px 8px', borderRadius:6, background:'var(--bg-elevated)', border:'1px solid var(--border-default)', color:'var(--text-primary)' }}>
                              <option value="">— All areas —</option>
                              {areaOptions.map((a:any) => (
                                <option key={a.id} value={a.id}>{a.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--text-muted)', marginBottom:3 }}>Location</div>
                            <select
                              value={woEditForm.location ?? ''}
                              onChange={e => setWoEditForm(p => ({...p, location: e.target.value}))}
                              style={{ width:'100%', fontSize:12, padding:'6px 8px', borderRadius:6, background:'var(--bg-elevated)', border:'1px solid var(--border-default)', color:'var(--text-primary)' }}>
                              <option value="">— Select —</option>
                              {editLocationOptions.map((l:any) => (
                                <option key={l.value} value={l.label}>{l.label}</option>
                              ))}
                              <option value="__other__">Other...</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--text-muted)', marginBottom:3 }}>Item</div>
                          <select
                            value={String(woEditForm.ticket_item_id ?? '')}
                            onChange={e => {
                              const selected = (ticketItems as any[]).find((t:any) => String(t.id) === e.target.value);
                              setWoEditForm(p => ({ ...p, ticket_item_id: e.target.value ? Number(e.target.value) : null, ticket_item_label: selected?.label ?? '' }));
                            }}
                            style={{ width:'100%', fontSize:12, padding:'6px 8px', borderRadius:6, background:'var(--bg-elevated)', border:'1px solid var(--border-default)', color:'var(--text-primary)' }}>
                            <option value="">— No item —</option>
                            {(ticketItems as any[])
                              .filter((t:any) => t.is_active !== false)
                              .sort((a:any, b:any) => a.label.localeCompare(b.label))
                              .map((t:any) => (
                                <option key={t.id} value={t.id}>{t.label}</option>
                              ))
                            }
                          </select>
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                          <div>
                            <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--text-muted)', marginBottom:3 }}>Due date</div>
                            <DatePicker
                              value={woEditForm.due_date ?? ''}
                              onChange={v => setWoEditForm(p => ({...p, due_date: v}))}
                              placeholder="Select a date"
                              style={{ fontSize: 12, padding: '6px 8px', borderRadius: 6, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)' }}
                            />
                          </div>
                          <div>
                            <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--text-muted)', marginBottom:3 }}>Est. hours</div>
                            <input type="number" min="0" step="0.25"
                              value={woEditForm.estimated_hours ?? ''}
                              onChange={e => setWoEditForm(p => ({
                                ...p, estimated_hours: parseFloat(e.target.value) || undefined
                              }))}
                              style={{ width:'100%', fontSize:12, padding:'6px 8px', borderRadius:6, background:'var(--bg-elevated)', border:'1px solid var(--border-default)', color:'var(--text-primary)' }}
                            />
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--text-muted)', marginBottom:3 }}>Description</div>
                          <RichTextEditor
                            value={woEditForm.description ?? ''}
                            onChange={v => setWoEditForm(p => ({...p, description: v}))}
                            placeholder="Description..."
                            rows={4}
                          />
                        </div>
                        <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
                          <FormSchemaBuilder
                            fields={woEditForm.custom_form_schema ?? []}
                            onChange={fields => setWoEditForm(p => ({ ...p, custom_form_schema: fields }))}
                            stableKeys={(selectedWo?.custom_form_schema ?? []).map(f => f.key)}
                            label="Task checklist / fields (optional)"
                            addLabel="Add checklist item / field"
                            emptyLabel="No checklist items yet."
                          />
                        </div>
                        <div style={{ display:'flex', justifyContent:'flex-end', gap:8, paddingTop:4 }}>
                          <button
                            className="btn btn-ghost combo-cancel-jello"
                            onClick={() => setWoEditMode(false)}>
                            Cancel
                          </button>
                          <button
                            className="btn btn-primary combo2"
                            onClick={async () => {
                              if (!selectedWo) return;
                              const payload: any = { ...woEditForm };
                              if (payload.due_date === '') payload.due_date = null;
                              payload.ticket_item_id = woEditForm.ticket_item_id ? Number(woEditForm.ticket_item_id) : null;
                              payload.ticket_item_label = (woEditForm.ticket_item_label as string | null | undefined) || null;
                              const cleanedCustomFields = (woEditForm.custom_form_schema ?? []).filter(f => f.label.trim());
                              payload.custom_form_schema = cleanedCustomFields.length > 0 ? cleanedCustomFields : null;
                              await api.patch(`/work-orders/${selectedWo.id}`, payload);
                              await refetchWOs();
                              const updated = workOrders.find(w => w.id === selectedWo.id);
                              if (updated) setSelectedWo({ ...updated, ...payload });
                              setWoEditMode(false);
                            }}>
                            Save Changes
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <div className="card" style={{ padding: '20px 22px' }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <FileText size={13}/> Description
                        </div>
                        {selectedWo.description ? (
                          <div style={{ fontSize: '0.95rem', lineHeight: 1.7, color: 'var(--text-secondary)' }} dangerouslySetInnerHTML={{ __html: mdToHtml(selectedWo.description ?? '') }} />
                        ) : (
                          <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>No description provided.</div>
                        )}
                      </div>
                      {selectedWo.notes && (
                        <div className="card" style={{ padding: '20px 22px' }}>
                          <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 14 }}>Notes</div>
                          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: mdToHtml(selectedWo.notes ?? '') }} />
                        </div>
                      )}
                    </div>
                  )
                )}

                {woDetailTab === 'checklist' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Ad-hoc "Task Checklist" — only shown when no logbook template is linked.
                        When one IS linked, this section is unrelated to the actual template form
                        and always empty, so it's just confusing clutter next to the real
                        "Linked template checklist" panel below — hidden entirely in that case. */}
                    {selectedWo.logbook_template_id == null && (
                    <div className="card" style={{ padding: '20px 22px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <ListChecks size={13}/> Task Checklist
                        </div>
                        {isManager && (
                          <button
                            className="btn btn-ghost btn-sm combo1"
                            onClick={() => {
                              if (customSchemaEditorOpen) { setCustomSchemaDraft(selectedWo.custom_form_schema ?? []); }
                              setCustomSchemaEditorOpen(o => !o);
                            }}>
                            {customSchemaEditorOpen ? 'Done' : (hasCustomSchema ? 'Edit fields' : 'Add checklist item / field')}
                          </button>
                        )}
                      </div>
                      {customSchemaEditorOpen ? (
                        <>
                          <FormSchemaBuilder
                            fields={customSchemaDraft}
                            onChange={setCustomSchemaDraft}
                            stableKeys={(selectedWo.custom_form_schema ?? []).map(f => f.key)}
                            label=""
                            addLabel="Add checklist item / field"
                            emptyLabel="No checklist items yet."
                          />
                          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                            <button className="btn btn-primary combo2" disabled={customSchemaSaving} onClick={saveCustomSchema}>
                              {customSchemaSaving ? 'Saving…' : 'Save'}
                            </button>
                            <button className="btn btn-ghost combo-cancel-jello" onClick={() => { setCustomSchemaDraft(selectedWo.custom_form_schema ?? []); setCustomSchemaEditorOpen(false); }}>
                              Cancel
                            </button>
                          </div>
                        </>
                      ) : hasCustomSchema ? (
                        <>
                          <LogbookFormFieldInputs
                            // Ad-hoc WO checklist fields never get section_break markers (its
                            // FormSchemaBuilder usage below doesn't pass allowSections) -- cast
                            // narrows FormField[]'s wider type (which now includes 'section_break'
                            // for the visual builder) back to the real LogbookFormField[] shape.
                            fields={selectedWo.custom_form_schema! as unknown as LogbookFormField[]}
                            values={customChecklistValues}
                            onChange={(key, val) => saveCustomValues({ ...customChecklistValues, [key]: val })}
                            checklistStyle
                          />
                          {customValuesSaving && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>Saving…</div>}
                        </>
                      ) : (
                        <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                          No checklist items yet.{isManager ? ' Click "Add checklist item / field" to add some.' : ''}
                        </div>
                      )}
                    </div>
                    )}

                    {/* Linked template checklist — a separate, optional concept from the ad-hoc
                        checklist above; only present if a logbook template is attached. */}
                    {templateChecklistVisible && selectedWo.logbook_template_id != null && (
                      <WoChecklistPanel data={woChecklist} workOrderId={selectedWo.id} woNumber={selectedWo.wo_number} woTitle={selectedWo.title} />
                    )}
                  </div>
                )}

                {woDetailTab === 'timelog' && (
                  <div className="card" style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {timeLogs.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>No activity logged yet</div>
                    )}
                    {timeLogs.map(tl => (
                      <div key={tl.id} style={{ display: 'flex', gap: 10, padding: '10px 12px', background: 'rgba(255,255,255,0.045)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                            <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{tl.user_name ?? 'Unknown'}</span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-primary)' }}>{Number(tl.hours).toFixed(2)}h</span>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>{woFmtDate(tl.logged_at)}</span>
                          </div>
                          {tl.notes && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{tl.notes}</div>}
                        </div>
                      </div>
                    ))}
                    <div style={{ padding: 12, background: 'rgba(255,255,255,0.045)', borderRadius: 8, border: '1px solid var(--border-subtle)', marginTop: 4 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>+ Add Activity</div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input type="number" min="0.25" step="0.25" value={tlHours} onChange={e => setTlHours(e.target.value)} placeholder="Hours" className="form-input" style={{ width: 90, fontSize: 13, flexShrink: 0 }}/>
                        <input value={tlNotes} onChange={e => setTlNotes(e.target.value)} placeholder="Notes (optional)" className="form-input" style={{ flex: 1, fontSize: 13, minWidth: 0 }}/>
                        {/* Logged by — same round avatar picker as WoCard's inline reassign,
                            same shared UserAvatarPicker component (see its definition above).
                            Closed state shows "Unassigned" until explicitly picked, never defaults
                            to the current user's id -- server still attributes the entry to the
                            current user when tlLoggedBy is left blank (see handleLogTime), this
                            only controls what the picker displays. Any user can already add an
                            activity entry (unrestricted server-side, always was); this only lets a
                            manager/admin change WHO it's attributed to, for logging on behalf of
                            someone who doesn't use the system — server enforces the same gate. */}
                        {isManager && (
                          <UserAvatarPicker
                            userId={tlLoggedBy ? Number(tlLoggedBy) : null}
                            facilityUsers={facilityUsers as FacilityUser[]}
                            onChange={(userId) => setTlLoggedBy(userId != null ? String(userId) : '')}
                            size={26}
                          />
                        )}
                        <button className="btn btn-primary combo2 btn-sm" disabled={tlSaving || !tlHours || Number(tlHours) <= 0} onClick={handleLogTime}
                          style={{ flexShrink: 0 }}>
                          {tlSaving ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                    {timeLogs.length > 0 && (
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'right' }}>
                        Total: <strong style={{ color: 'var(--text-primary)' }}>{timeLogs.reduce((s, tl) => s + Number(tl.hours), 0).toFixed(2)}h</strong>
                      </div>
                    )}
                  </div>
                )}

                {woDetailTab === 'notes' && (
                  <div className="card" style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {wkNotes.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>No notes yet</div>
                    )}
                    {wkNotes.map(n => (
                      <div key={n.id} style={{ padding: '10px 12px', background: 'rgba(255,255,255,0.045)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{n.user_name ?? 'Unknown'}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{woFmtDate(n.created_at)}</span>
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: mdToHtml(n.content) }} />
                      </div>
                    ))}
                    <div style={{ padding: 12, background: 'rgba(255,255,255,0.045)', borderRadius: 8, border: '1px solid var(--border-subtle)', marginTop: 4 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>+ Add Note</div>
                      <RichTextEditor
                        value={noteContent}
                        onChange={setNoteContent}
                        placeholder="Write a note..."
                        rows={3}
                        style={{ marginBottom: 8 }}
                      />
                      <button className="btn btn-primary combo2 btn-sm" disabled={noteSaving || !noteContent.trim()} onClick={handleAddNote}>
                        {noteSaving ? 'Saving…' : 'Save Note'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Properties sidebar */}
              <div className="wo-tk-side">
                <div>
                  <div style={woMetaLabelStyle}>Assigned to</div>
                  <div
                    style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', borderRadius: 9, border: '1px solid var(--border-default)', background: 'rgba(255,255,255,0.03)', transition: 'border-color 0.14s ease, background 0.14s ease' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                  >
                    {isManager && selectedWo.status !== 'done' ? (
                      // Same UserAvatarPicker used by WoCard's inline reassign, the Activity Log's
                      // "Logged by", and the Scheduled Work Order form's "Assigned To" -- one shared
                      // circle/color/dropdown-row implementation, so this panel can't drift from
                      // those again (this previously used DarkSelect with a bare-text options list,
                      // no circles at all, hence "AS — Abe Shoner · Administration" plain text).
                      <UserAvatarPicker
                        userId={selectedWo.assigned_to}
                        facilityUsers={facilityUsers as FacilityUser[]}
                        onChange={async (userId) => {
                          await api.patch(`/work-orders/${selectedWo!.id}`, { assigned_to: userId });
                          await refetchWOs();
                          const updated = workOrders.find(w => w.id === selectedWo!.id);
                          if (updated) setSelectedWo({ ...updated, assigned_to: userId });
                        }}
                        size={26}
                      />
                    ) : (
                      <span style={{ width: 26, height: 26, borderRadius: 99, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.68rem', fontWeight: 700, color: '#fff', flexShrink: 0, background: selectedWo.assigned_to ? getUserColor(selectedWo.assigned_to) : 'rgba(255,255,255,0.08)', border: selectedWo.assigned_to ? 'none' : '1px dashed var(--border-strong)' }}>
                        {assignedInitials || '?'}
                      </span>
                    )}
                    {!(isManager && selectedWo.status !== 'done') && (
                      <span style={{ flex: 1, fontSize: 13, color: selectedWo.assigned_to ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                        {(selectedWo.assignee_name || selectedWo.assigned_to_name) || 'Unassigned'}
                        {/* Locked-on-completion indicator — matches the "no reassignment once Done"
                            rule enforced server-side too (work-orders.ts PATCH). */}
                        {selectedWo.status === 'done' && isManager && (
                          <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>(locked — completed)</span>
                        )}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                  <div>
                    <div style={woMetaLabelStyle}>Due date</div>
                    <div style={woMetaValStyle}><Calendar size={16} style={{ color: 'var(--text-muted)' }}/> {woFmtDate(selectedWo.due_date)}</div>
                  </div>
                  <div>
                    <div style={woMetaLabelStyle}>Est. hours</div>
                    <div style={woMetaValStyle}><Clock size={16} style={{ color: 'var(--text-muted)' }}/> {selectedWo.estimated_hours != null ? `${selectedWo.estimated_hours}h` : '—'}</div>
                  </div>
                  <div>
                    <div style={woMetaLabelStyle}>Location</div>
                    <div style={woMetaValStyle}><MapPin size={16} style={{ color: 'var(--text-muted)' }}/> {selectedWo.location || '—'}</div>
                  </div>
                  <div>
                    <div style={woMetaLabelStyle}>Category</div>
                    <div style={{ ...woMetaValStyle, textTransform: 'capitalize' }}><Tag size={16} style={{ color: 'var(--text-muted)' }}/> {selectedWo.category.replace('_', ' ')}</div>
                  </div>
                </div>

                {(templateChecklistVisible || hasCustomSchema) && (
                  <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 18 }}>
                    <div style={{ ...woMetaLabelStyle, marginBottom: 8 }}>Progress</div>
                    <div style={{ height: 7, borderRadius: 99, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 99, background: 'linear-gradient(90deg, var(--accent-success), #34d399)', width: `${checklistTotalCount ? (checklistDoneCount / checklistTotalCount * 100) : 0}%`, transition: 'width 0.2s ease' }}/>
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 6 }}>
                      {checklistTotalCount === 0 ? 'No checklist items in this form' : `${checklistDoneCount} of ${checklistTotalCount} checklist items complete`}
                    </div>
                  </div>
                )}

                <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 18 }}>
                  <div style={{ ...woMetaLabelStyle, marginBottom: 10 }}>Linked Equipment</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: isManager ? 10 : 0 }}>
                    {woEquipmentLinks.length === 0 && (
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Not linked to any equipment yet.</span>
                    )}
                    {woEquipmentLinks.map((link) => (
                      <span key={link.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 999, fontSize: '0.78rem', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)', color: '#93c5fd' }}>
                        {equipmentLabel(link)}
                        {isManager && (
                          <button onClick={() => handleUnlinkWoEquipment(link.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex', padding: 0 }} title="Unlink">
                            <X size={12} />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                  {isManager && (
                    <EquipmentLinkPicker entries={woEquipOptions} onSelect={handleLinkWoEquipment} disabled={equipmentLinking} />
                  )}
                </div>

                {selectedWo.logbook_template_id != null && woChecklist.template && (
                  <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 18 }}>
                    <div style={{ ...woMetaLabelStyle, marginBottom: 10 }}>Linked log template</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: 12, borderRadius: 11, border: '1px solid rgba(16,185,129,0.28)', background: 'rgba(16,185,129,0.06)' }}>
                      <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(16,185,129,0.16)', color: '#34d399', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Link2 size={16}/>
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.83rem', color: 'var(--text-primary)' }}>{woChecklist.template.name}</div>
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 1 }}>Completing this checklist saves the filled log</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* ── Delete Work Order Confirm ── */}
      {deleteConfirmWoId !== null && (
        <DeleteConfirmModal
          message="Delete this work order? This cannot be undone."
          onConfirm={async () => {
            await api.delete(`/work-orders/${deleteConfirmWoId}`);
            await refetchWOs();
            setDeleteConfirmWoId(null);
            closeDetail();
          }}
          onClose={() => setDeleteConfirmWoId(null)}
        />
      )}

      {/* ── Schedule Modal ── */}
      {scheduleModalOpen && (
        <ScheduleModal
          editTarget={scheduleEditTarget}
          facilityId={facilityId!}
          facilityUsers={facilityUsers as any[]}
          areaOptions={areaOptions as any[]}
          ticketItems={ticketItems as any[]}
          onClose={() => { setScheduleModalOpen(false); setScheduleEditTarget(null); }}
          onSaved={() => { refetchSchedules(); setScheduleModalOpen(false); setScheduleEditTarget(null); }}
        />
      )}

      {/* ── Delete Schedule Confirm ── */}
      {deleteConfirmScheduleId !== null && (
        <DeleteConfirmModal
          message="Delete this schedule? Work orders already generated will not be affected."
          onConfirm={async () => {
            await api.delete(`/scheduled-work-orders/${deleteConfirmScheduleId}`);
            await refetchSchedules();
            setDeleteConfirmScheduleId(null);
          }}
          onClose={() => setDeleteConfirmScheduleId(null)}
        />
      )}

      {/* ── Add Work Order — page-takeover (same shared shell as WO detail), not a dialog ── */}
      {woCreateOpen && ReactDOM.createPortal(
        <>
          <TakeoverFocusEffect />
          <div style={TAKEOVER_SHELL_STYLE}>
            <TakeoverAccentBar />
            <div style={{ padding: '20px 28px 18px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-primary)' }}>New Work Order</h3>
              <button onClick={() => { setWoCreateOpen(false); setWoFormError(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={20}/></button>
            </div>
            <form
              onSubmit={e => {
                e.preventDefault();
                // Confirmation only inserts itself when there's actually something to warn about
                // -- checkbox unchecked, or no active violations, and creation proceeds exactly
                // as before.
                if (woAutoCreateFollowup && woFormViolations.length > 0) setWoConfirmOpen(true);
                else void handleCreateWo();
              }}
              style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}
            >
              <div style={{ flex: 1, overflowY: 'auto', padding: '22px 28px', display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 720, width: '100%', margin: '0 auto' }}>
              <div>
                <label className="form-label">Title *</label>
                <input className="form-input" value={woForm.title} onChange={e => setWoForm(p => ({ ...p, title: e.target.value }))} placeholder="Work order title" autoFocus/>
              </div>
              <div>
                <label className="form-label">Description</label>
                <RichTextEditor
                  value={woForm.description}
                  onChange={v => setWoForm(p => ({ ...p, description: v }))}
                  placeholder="Describe the work to be done..."
                  rows={3}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label className="form-label">Category</label>
                  <select className="form-input" value={woForm.category} onChange={e => setWoForm(p => ({ ...p, category: e.target.value }))}>
                    {['general','plumbing','electrical','hvac','life_safety','vendor','doors','flooring','other'].map(c => (
                      <option key={c} value={c}>{c.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Priority</label>
                  <select className="form-input" value={woForm.priority} onChange={e => setWoForm(p => ({ ...p, priority: e.target.value }))}>
                    {['urgent','high','medium','low'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label className="form-label">Assign To</label>
                  <select className="form-input" value={woForm.assigned_to} onChange={e => setWoForm(p => ({ ...p, assigned_to: e.target.value }))}>
                    <option value="">— Unassigned —</option>
                    {(facilityUsers as any[]).map((u: any) => {
                      const ini = `${u.first_name?.[0] ?? ''}${u.last_name?.[0] ?? ''}`.toUpperCase();
                      return <option key={u.id} value={u.id}>{ini} — {u.first_name} {u.last_name}</option>;
                    })}
                  </select>
                </div>
                <div>
                  <label className="form-label">Location</label>
                  {(areaOptions as any[]).length > 0 && (
                    <select
                      value={woAreaId}
                      onChange={e => { setWoAreaId(e.target.value); setWoForm(p => ({ ...p, location: '' })); }}
                      style={{ fontSize: 13, padding: '6px 10px', borderRadius: 6, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', color: woAreaId ? 'var(--text-primary)' : 'var(--text-muted)', width: '100%', marginBottom: 6 }}>
                      <option value="">— Select area —</option>
                      {(areaOptions as any[]).map((a: any) => (
                        <option key={a.id} value={a.id}>{a.label}</option>
                      ))}
                    </select>
                  )}
                  <select
                    value={woForm.location}
                    onChange={e => setWoForm(p => ({ ...p, location: e.target.value }))}
                    style={{ fontSize: 13, padding: '6px 10px', borderRadius: 6, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', color: woForm.location ? 'var(--text-primary)' : 'var(--text-muted)', width: '100%' }}>
                    <option value="">— Select location —</option>
                    {(locationOptions as any[]).map((l: any) => (
                      <option key={l.id ?? l.value} value={l.value}>{l.label}</option>
                    ))}
                    <option value="__other__">Other (type below)</option>
                  </select>
                  {woForm.location === '__other__' && (
                    <input
                      type="text"
                      placeholder="Describe location..."
                      value={woForm.locationCustom ?? ''}
                      onChange={e => setWoForm(p => ({ ...p, locationCustom: e.target.value }))}
                      style={{ marginTop: 6, fontSize: 13, padding: '6px 10px', borderRadius: 6, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', color: 'var(--text-primary)', width: '100%' }}
                    />
                  )}
                </div>
              </div>
              <div>
                <label style={{ fontSize:11, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>Item</label>
                <select
                  value={woForm.ticket_item_id}
                  onChange={e => {
                    const selected = (ticketItems as any[]).find((t:any) => String(t.id) === e.target.value);
                    setWoForm(p => ({ ...p, ticket_item_id: e.target.value, ticket_item_label: selected?.label ?? '' }));
                  }}
                  style={{ fontSize:13, padding:'6px 10px', borderRadius:6, background:'var(--bg-elevated)', border:'1px solid var(--border-default)', color: woForm.ticket_item_id ? 'var(--text-primary)' : 'var(--text-muted)', width:'100%', marginTop:4 }}>
                  <option value="">— Select item (optional) —</option>
                  {(ticketItems as any[])
                    .filter((t:any) => t.is_active !== false)
                    .sort((a:any, b:any) => a.label.localeCompare(b.label))
                    .map((t:any) => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))
                  }
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label className="form-label">Due Date</label>
                  <DatePicker value={woForm.due_date} onChange={v => setWoForm(p => ({ ...p, due_date: v }))} placeholder="Select a date" />
                </div>
                <div>
                  <label className="form-label">Est. Hours</label>
                  <input type="number" min="0" step="0.5" className="form-input" value={woForm.estimated_hours} onChange={e => setWoForm(p => ({ ...p, estimated_hours: e.target.value }))} placeholder="0.0"/>
                </div>
              </div>
              <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
                <label className="form-label">Logbook Form (optional)</label>
                <DarkSelect
                  value={woForm.logbook_template_id}
                  onChange={v => { setWoForm(p => ({ ...p, logbook_template_id: v })); setWoFormValues({}); }}
                  placeholder="No form attached"
                  options={[
                    { value: '', label: 'No form attached' },
                    ...(logbookFormOptions as LogbookTemplateOption[]).map(t => ({ value: String(t.id), label: t.name })),
                  ]}
                />
                {(() => {
                  const schema = selectedLogbookTemplate?.form_schema;
                  const hasFields = !!schema && (
                    schema.form_mode === 'single_form'
                      ? (schema.fixed_fields?.length ?? 0) > 0
                      : (schema.rows?.length ?? 0) > 0 && (schema.row_fields?.length ?? 0) > 0
                  );
                  if (!hasFields || !schema) return null;
                  const allFieldDefs = schema.form_mode === 'single_form' ? (schema.fixed_fields ?? []) : (schema.row_fields ?? []);
                  const hasRangeLimited = allFieldDefs.some(f => f.type === 'number' && (f.min !== undefined || f.max !== undefined));
                  return (
                    <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: '1px solid rgba(59,130,246,0.2)', background: 'rgba(59,130,246,0.05)' }}>
                      <LogbookFormRenderer
                        schema={schema}
                        values={woFormValues}
                        onChange={(key, val) => setWoFormValues(v => ({ ...v, [key]: val }))}
                      />
                      {hasRangeLimited && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer', marginTop: 10 }}>
                          <input type="checkbox" checked={woAutoCreateFollowup} onChange={e => setWoAutoCreateFollowup(e.target.checked)} />
                          Auto-create a follow-up ticket if any values are out of range
                        </label>
                      )}
                    </div>
                  );
                })()}
              </div>
              {/* Ad-hoc checklist/fields — a separate, always-available capability from the
                  logbook form tie-in above (a WO can have its own checklist whether or not a
                  template is attached). Schema only here; values are filled in from the
                  Checklist tab after creation, same as how a template's own fields are authored
                  separately from filling one in. */}
              <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
                <FormSchemaBuilder
                  fields={woCustomFields}
                  onChange={setWoCustomFields}
                  label="Task checklist / fields (optional)"
                  addLabel="Add checklist item / field"
                  emptyLabel="No checklist items yet — you can also add these later from the Checklist tab."
                />
              </div>
              <div>
                <label className="form-label">Notes</label>
                <RichTextEditor
                  value={woForm.notes}
                  onChange={v => setWoForm(p => ({ ...p, notes: v }))}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
              {woFormError && <div style={{ color: 'var(--accent-danger)', fontSize: '0.875rem' }}>{woFormError}</div>}
              </div>
              <div style={{ flexShrink: 0, padding: '14px 28px', borderTop: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="btn btn-ghost combo-cancel-jello" onClick={() => { setWoCreateOpen(false); setWoFormError(''); }}>Cancel</button>
                <button type="submit" className="btn btn-primary combo2" disabled={woFormSaving || !woForm.title.trim()}>
                  {woFormSaving ? 'Creating…' : 'Create Work Order'}
                </button>
              </div>
            </form>
            {woConfirmOpen && (
              <RangeViolationConfirmModal
                violations={woFormViolations}
                onConfirm={() => { setWoConfirmOpen(false); void handleCreateWo(); }}
                onClose={() => setWoConfirmOpen(false)}
              />
            )}
          </div>
        </>,
        document.body
      )}

      {/* ── Completed Work Orders — dedicated takeover, replaces the old status-filter-only
          "View all" behavior ── */}
      {completedTakeoverOpen && (
        <CompletedWorkOrdersTakeover
          facilityUsers={facilityUsers as FacilityUser[]}
          isTopmost={!woDetailOpen}
          onClose={() => setCompletedTakeoverOpen(false)}
          // Deliberately does NOT close this takeover — it stays mounted underneath the WO detail
          // takeover (which renders at a higher z-index, see TAKEOVER_SHELL_STYLE usage above) so
          // closing the detail view naturally reveals this one again, instead of always dropping
          // back to the base Task Manager page. This was the actual bug: onOpenDetail used to
          // close the parent before opening the child, so there was nothing left to return to.
          onOpenDetail={openDetail}
        />
      )}
    </div>
  );
}

function ScheduleModal({
  editTarget, facilityId, facilityUsers, areaOptions, ticketItems, onClose, onSaved,
}: {
  editTarget: ScheduledWorkOrder | null;
  facilityId: number;
  facilityUsers: any[];
  areaOptions: any[];
  ticketItems: any[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!editTarget;
  const [schedTab, setSchedTab] = useState<'details'|'schedule'>('details');
  const [freqMode, setFreqMode] = useState<'daily'|'weekly'|'monthly'|'yearly'|'custom'>(() => {
    if (!editTarget) return 'weekly';
    const u = editTarget.frequency_unit; const n = editTarget.frequency_interval;
    if (u==='days'&&n===1) return 'daily';
    if (u==='weeks'&&n===1) return 'weekly';
    if (u==='months'&&n===1) return 'monthly';
    if (u==='years'&&n===1) return 'yearly';
    return 'custom';
  });
  const [schedForm, setSchedForm] = useState({
    title: editTarget?.title ?? '',
    description: editTarget?.description ?? '',
    category: editTarget?.category ?? 'general',
    priority: editTarget?.priority ?? 'medium',
    area_id: '',
    area_label: editTarget?.area_label ?? '',
    location_label: editTarget?.location_label ?? '',
    ticket_item_id: '',
    ticket_item_label: editTarget?.ticket_item_label ?? '',
    assigned_to: editTarget?.assigned_to ? String(editTarget.assigned_to) : '',
    frequency_unit: (editTarget?.frequency_unit ?? 'weeks') as 'days'|'weeks'|'months'|'years',
    frequency_interval: editTarget?.frequency_interval ?? 1,
    start_date: editTarget?.start_date?.slice(0,10) ?? new Date().toISOString().slice(0,10),
    end_date: editTarget?.end_date?.slice(0,10) ?? '',
    prior_days_notice: editTarget?.prior_days_notice ?? 0,
    skip_weekends: editTarget?.skip_weekends ?? false,
    estimated_hours: editTarget?.estimated_hours ? String(editTarget.estimated_hours) : '',
    notes: '',
    linked_hvac_filter_id: editTarget?.linked_hvac_filter_id ? String(editTarget.linked_hvac_filter_id) : '',
    consumption_qty: editTarget?.consumption_qty ? String(editTarget.consumption_qty) : '1',
  });
  const [schedCustomFields, setSchedCustomFields] = useState<FormField[]>(editTarget?.custom_form_schema ?? []);
  const [schedPreview, setSchedPreview] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const { data: schedLocOpts = [] } = useQuery({
    queryKey: ['sched-loc', facilityId, schedForm.area_id],
    queryFn: async () => {
      if (!schedForm.area_id) return [];
      const res = await api.get(`/dropdowns?dropdown_type=location&facility_id=${facilityId}&parent_id=${schedForm.area_id}`);
      return (res as any).options ?? [];
    },
    enabled: !!schedForm.area_id,
  });

  // v145 — item C: flat "unit name — filter size (type)" options for the Linked HVAC Filter
  // picker, derived from the same GET /hvac the HVAC Filter Usage tab itself uses (no new
  // endpoint needed just for this dropdown).
  const { data: hvacUnitsForSched = [] } = useQuery({
    queryKey: ['facility-hvac-for-schedule'],
    queryFn: () => api.get<{ id: number; name: string; filters: { id: number; filter_size: string; filter_type: string | null }[] }[]>('/hvac'),
  });
  const hvacFilterOptions = ((hvacUnitsForSched as any)?.data ?? []).flatMap((u: { id: number; name: string; filters: { id: number; filter_size: string; filter_type: string | null }[] }) =>
    u.filters.map((f) => ({ id: f.id, label: `${u.name} — ${f.filter_size}${f.filter_type ? ` (${f.filter_type})` : ''}` }))
  );

  useEffect(() => { document.body.style.overflow='hidden'; return ()=>{ document.body.style.overflow=''; }; }, []);

  useEffect(() => {
    if (!schedForm.start_date) return;
    api.get(`/scheduled-work-orders/preview?start_date=${schedForm.start_date}&frequency_unit=${schedForm.frequency_unit}&frequency_interval=${schedForm.frequency_interval}&skip_weekends=${schedForm.skip_weekends}&count=3`)
      .then(res => setSchedPreview((res as any).dates ?? []));
  }, [schedForm.start_date, schedForm.frequency_unit, schedForm.frequency_interval, schedForm.skip_weekends]);

  async function handleSubmit() {
    if (!schedForm.title.trim()) { setError('Title is required'); setSchedTab('details'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        title: schedForm.title,
        description: schedForm.description || null,
        category: schedForm.category,
        priority: schedForm.priority,
        area_label: schedForm.area_label || null,
        location_label: schedForm.location_label || null,
        ticket_item_id: schedForm.ticket_item_id ? Number(schedForm.ticket_item_id) : null,
        ticket_item_label: schedForm.ticket_item_label || null,
        assigned_to: schedForm.assigned_to ? Number(schedForm.assigned_to) : null,
        frequency_unit: schedForm.frequency_unit,
        frequency_interval: schedForm.frequency_interval,
        start_date: schedForm.start_date,
        end_date: schedForm.end_date || null,
        prior_days_notice: schedForm.prior_days_notice,
        skip_weekends: schedForm.skip_weekends,
        estimated_hours: schedForm.estimated_hours ? Number(schedForm.estimated_hours) : null,
        notes: schedForm.notes || null,
        custom_form_schema: schedCustomFields.filter(f => f.label.trim()).length > 0
          ? schedCustomFields.filter(f => f.label.trim()) : null,
        linked_hvac_filter_id: schedForm.linked_hvac_filter_id ? Number(schedForm.linked_hvac_filter_id) : null,
        consumption_qty: schedForm.linked_hvac_filter_id ? (Number(schedForm.consumption_qty) || 1) : null,
      };
      if (isEdit) await api.patch(`/scheduled-work-orders/${editTarget!.id}`, payload);
      else await api.post('/scheduled-work-orders', payload);
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Save failed');
    } finally { setSaving(false); }
  }

  function applyFreqMode(mode: typeof freqMode) {
    setFreqMode(mode);
    if (mode==='daily')   setSchedForm(p=>({...p,frequency_unit:'days',  frequency_interval:1}));
    if (mode==='weekly')  setSchedForm(p=>({...p,frequency_unit:'weeks', frequency_interval:1}));
    if (mode==='monthly') setSchedForm(p=>({...p,frequency_unit:'months',frequency_interval:1}));
    if (mode==='yearly')  setSchedForm(p=>({...p,frequency_unit:'years', frequency_interval:1}));
  }

  const fmtPrev = (d: string) => {
    const [y,m,day] = d.split('-').map(Number);
    return new Date(y,m-1,day).toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric',year:'numeric'});
  };

  const fieldLabel: React.CSSProperties = {
    fontSize:10, textTransform:'uppercase' as const, letterSpacing:'0.05em', color:'var(--text-muted)', marginBottom:3, display:'block',
  };
  const inpStyle: React.CSSProperties = {
    width:'100%', fontSize:13, padding:'6px 10px', borderRadius:6, background:'var(--bg-elevated)', border:'1px solid var(--border-default)', color:'var(--text-primary)',
  };

  return ReactDOM.createPortal(
    <>
      <TakeoverFocusEffect />
      <div style={TAKEOVER_SHELL_STYLE}>
        <TakeoverAccentBar />
        {/* Header */}
        <div style={{padding:'20px 28px 0',flexShrink:0}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
            <h3 style={{margin:0,fontSize:'1.4rem',fontWeight:800,letterSpacing:'-0.02em',color:'var(--text-primary)'}}>{isEdit?'Edit Schedule':'New Scheduled Work Order'}</h3>
            <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-muted)',padding:4}}><X size={20}/></button>
          </div>
          {/* Tab bar */}
          <div style={{display:'flex',gap:0,borderBottom:'1px solid rgba(255,255,255,0.08)',marginBottom:0}}>
            {([{key:'details' as const,label:'📋 Details'},{key:'schedule' as const,label:'🔁 Schedule'}]).map(t=>(
              <button key={t.key} onClick={()=>setSchedTab(t.key)}
                style={{padding:'8px 16px',fontSize:13,fontWeight:500,border:'none',cursor:'pointer',background:'none',
                  color:schedTab===t.key?'var(--accent-primary)':'var(--text-secondary)',
                  borderBottom:schedTab===t.key?'2px solid var(--accent-primary)':'2px solid transparent',
                  marginBottom:-1,transition:'color 0.15s,border-color 0.15s'}}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{flex:1,overflowY:'auto',padding:'18px 28px',display:'flex',flexDirection:'column',gap:12,maxWidth:720,width:'100%',margin:'0 auto'}}>
          {error&&<div style={{padding:'8px 12px',borderRadius:6,background:'rgba(239,68,68,0.12)',border:'1px solid rgba(239,68,68,0.35)',color:'#ef4444',fontSize:13}}>{error}</div>}

          {/* TAB 1 — Details */}
          {schedTab==='details'&&(<>
            <div>
              <label style={fieldLabel}>Title *</label>
              <input className="form-input" value={schedForm.title} onChange={e=>setSchedForm(p=>({...p,title:e.target.value}))} placeholder="e.g. HVAC Filter Replacement" autoFocus/>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div>
                <label style={fieldLabel}>Category</label>
                <select className="form-input" value={schedForm.category} onChange={e=>setSchedForm(p=>({...p,category:e.target.value}))}>
                  {['general','plumbing','electrical','hvac','life_safety','vendor','doors','flooring','other'].map(c=>(
                    <option key={c} value={c}>{c.replace(/_/g,' ').replace(/\b\w/g,(l:string)=>l.toUpperCase())}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={fieldLabel}>Priority</label>
                <select className="form-input" value={schedForm.priority} onChange={e=>setSchedForm(p=>({...p,priority:e.target.value}))}>
                  {['urgent','high','medium','low'].map(p=><option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
                </select>
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div>
                <label style={fieldLabel}>Area</label>
                <select className="form-input" value={schedForm.area_id}
                  onChange={e=>{
                    const opt=(areaOptions as any[]).find((a:any)=>String(a.id)===e.target.value);
                    setSchedForm(p=>({...p,area_id:e.target.value,area_label:opt?.label??'',location_label:''}));
                  }}>
                  <option value="">— All areas —</option>
                  {(areaOptions as any[]).map((a:any)=><option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
              </div>
              <div>
                <label style={fieldLabel}>Location</label>
                <select className="form-input" value={schedForm.location_label}
                  onChange={e=>setSchedForm(p=>({...p,location_label:e.target.value}))}>
                  <option value="">— Select —</option>
                  {(schedLocOpts as any[]).map((l:any)=><option key={l.value} value={l.label}>{l.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label style={fieldLabel}>Ticket Item</label>
              <select className="form-input" value={schedForm.ticket_item_id}
                onChange={e=>{
                  const opt=(ticketItems as any[]).find((t:any)=>String(t.id)===e.target.value);
                  setSchedForm(p=>({...p,ticket_item_id:e.target.value,ticket_item_label:opt?.label??''}));
                }}>
                <option value="">— None —</option>
                {(ticketItems as any[]).map((t:any)=><option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={fieldLabel}>Assigned To</label>
              <div style={{ marginTop: 4 }}>
                <UserAvatarPicker
                  userId={schedForm.assigned_to ? Number(schedForm.assigned_to) : null}
                  facilityUsers={facilityUsers as FacilityUser[]}
                  onChange={(userId) => setSchedForm(p => ({ ...p, assigned_to: userId != null ? String(userId) : '' }))}
                  size={26}
                />
              </div>
            </div>
            <div>
              <label style={fieldLabel}>Description</label>
              <RichTextEditor
                value={schedForm.description}
                onChange={v => setSchedForm(p => ({...p, description: v}))}
                placeholder="Describe the scheduled work..."
                rows={2}
              />
            </div>
            <div>
              <label style={fieldLabel}>Est. Hours</label>
              <input type="number" min="0" step="0.5" className="form-input" value={schedForm.estimated_hours} onChange={e=>setSchedForm(p=>({...p,estimated_hours:e.target.value}))} placeholder="0.0"/>
            </div>
            {/* v145 — item C. Investigated first: recurring tasks had NO existing way to link a
                part/filter + a per-occurrence consumption quantity (confirmed against
                work_orders/scheduled_work_orders schema), so this is new. Optional -- leaving it
                unset means closing occurrences of this schedule never auto-logs anything, exactly
                like today. Every work order /generate creates from this schedule inherits both
                fields verbatim; closing one with a link set auto-logs facility_hvac_filter_usage
                (work-orders.ts PATCH /:id) using the qty entered here. */}
            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
              <label style={fieldLabel}>Linked HVAC Filter (optional)</label>
              <select
                className="form-select"
                value={schedForm.linked_hvac_filter_id}
                onChange={e => setSchedForm(p => ({ ...p, linked_hvac_filter_id: e.target.value }))}
              >
                <option value="">— None (no auto-logging) —</option>
                {hvacFilterOptions.map((opt: { id: number; label: string }) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
              {schedForm.linked_hvac_filter_id && (
                <div style={{ marginTop: 8 }}>
                  <label style={fieldLabel}>Consumption Qty (per occurrence)</label>
                  <input type="number" min="1" className="form-input" style={{ maxWidth: 120 }}
                    value={schedForm.consumption_qty}
                    onChange={e => setSchedForm(p => ({ ...p, consumption_qty: e.target.value }))} />
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    Logged automatically to Facility Supplies &gt; HVAC &gt; Filter Usage every time an occurrence of this task is closed.
                  </p>
                </div>
              )}
            </div>
            {/* Ad-hoc checklist/fields — authored once on the schedule itself, copied verbatim
                onto every generated occurrence's own custom_form_schema by POST /generate, so
                the checklist doesn't need re-authoring on each individual instance. */}
            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
              <FormSchemaBuilder
                fields={schedCustomFields}
                onChange={setSchedCustomFields}
                stableKeys={(editTarget?.custom_form_schema ?? []).map(f => f.key)}
                label="Task checklist / fields (optional)"
                addLabel="Add checklist item / field"
                emptyLabel="No checklist items yet — every generated occurrence will inherit whatever's defined here."
              />
            </div>
          </>)}

          {/* TAB 2 — Schedule */}
          {schedTab==='schedule'&&(<>
            <div>
              <label style={fieldLabel}>Frequency</label>
              <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:10}}>
                {([
                  {key:'daily'  as const,label:'Daily'},
                  {key:'weekly' as const,label:'Weekly'},
                  {key:'monthly'as const,label:'Monthly'},
                  {key:'yearly' as const,label:'Yearly'},
                  {key:'custom' as const,label:'Custom'},
                ]).map(f=>(
                  <button key={f.key} type="button" onClick={()=>applyFreqMode(f.key)}
                    style={{padding:'5px 14px',borderRadius:20,border:'1px solid var(--border-default)',cursor:'pointer',fontSize:12,fontWeight:500,
                      background:freqMode===f.key?'var(--accent-primary)':'transparent',
                      color:freqMode===f.key?'#fff':'var(--text-secondary)'}}>
                    {f.label}
                  </button>
                ))}
              </div>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:13,color:'var(--text-muted)'}}>Every</span>
                <input type="number" min={1} value={schedForm.frequency_interval}
                  onChange={e=>setSchedForm(p=>({...p,frequency_interval:Math.max(1,parseInt(e.target.value)||1)}))}
                  style={{...inpStyle,width:60}}/>
                <select value={schedForm.frequency_unit} onChange={e=>setSchedForm(p=>({...p,frequency_unit:e.target.value as any}))}
                  style={{...inpStyle,width:'auto'}}>
                  <option value="days">days</option>
                  <option value="weeks">weeks</option>
                  <option value="months">months</option>
                  <option value="years">years</option>
                </select>
              </div>
            </div>
            <div>
              <label style={fieldLabel}>Start Date</label>
              <DatePicker value={schedForm.start_date} onChange={v=>setSchedForm(p=>({...p,start_date:v}))} placeholder="Select a date" />
            </div>
            <div>
              <label style={fieldLabel}>End Date <span style={{color:'var(--text-muted)',fontWeight:400}}>(optional)</span></label>
              <DatePicker value={schedForm.end_date} onChange={v=>setSchedForm(p=>({...p,end_date:v}))} placeholder="Select a date" />
            </div>
            <div>
              <label style={fieldLabel}>Prior Days Notice</label>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:13,color:'var(--text-muted)'}}>Generate</span>
                <input type="number" min={0} value={schedForm.prior_days_notice}
                  onChange={e=>setSchedForm(p=>({...p,prior_days_notice:Math.max(0,parseInt(e.target.value)||0)}))}
                  style={{...inpStyle,width:60}}/>
                <span style={{fontSize:13,color:'var(--text-muted)'}}>days before due date</span>
              </div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <div onClick={()=>setSchedForm(p=>({...p,skip_weekends:!p.skip_weekends}))}
                style={{width:32,height:18,borderRadius:99,background:schedForm.skip_weekends?'#3b82f6':'rgba(255,255,255,0.15)',position:'relative',cursor:'pointer',transition:'background 0.2s',flexShrink:0}}>
                <div style={{width:14,height:14,borderRadius:'50%',background:'#fff',position:'absolute',top:2,left:schedForm.skip_weekends?16:2,transition:'left 0.2s',boxShadow:'0 1px 3px rgba(0,0,0,0.4)'}}/>
              </div>
              <span style={{fontSize:13,color:'var(--text-secondary)'}}>Skip weekends</span>
            </div>
            <div style={{background:'var(--bg-elevated)',borderRadius:8,padding:'10px 12px',border:'1px solid var(--border-subtle)'}}>
              <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:8}}>Next 3 occurrences</div>
              {schedPreview.length===0
                ?<div style={{fontSize:12,color:'var(--text-muted)',fontStyle:'italic'}}>Set a start date to preview</div>
                :schedPreview.map((d,i)=>(
                  <div key={i} style={{fontSize:13,color:'var(--text-primary)',marginBottom:4,fontWeight:500}}>{fmtPrev(d)}</div>
                ))
              }
            </div>
          </>)}
        </div>

        {/* Footer */}
        <div style={{flexShrink:0,padding:'14px 28px',borderTop:'1px solid var(--border-subtle)',display:'flex',gap:8,justifyContent:'flex-end',alignItems:'center'}}>
          {schedTab==='details'&&(<>
            <button type="button" className="btn btn-ghost combo-cancel-jello" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-primary combo2"
              disabled={!schedForm.title.trim()}
              onClick={()=>{
                if(!schedForm.title.trim()){setError('Title is required');return;}
                setError(''); setSchedTab('schedule');
              }}>
              Next: Schedule →
            </button>
          </>)}
          {schedTab==='schedule'&&(<>
            <button type="button" className="btn btn-ghost combo-cancel-jello" onClick={()=>setSchedTab('details')}>← Back</button>
            <button type="button" className="btn btn-primary combo2" disabled={saving} onClick={handleSubmit}>
              {saving?'Saving…':isEdit?'Save Changes':'Create Schedule'}
            </button>
          </>)}
        </div>
      </div>
    </>,
    document.body
  );
}

function SyncModal({reqqerBase,onBaseChange,onClose,onSynced}:{
  reqqerBase:string; onBaseChange:(v:string)=>void;
  onClose:()=>void; onSynced:()=>void;
}) {
  const [baseUrl, setBaseUrl] = useState(reqqerBase);
  const [json, setJson] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState<{upserted:number;skipped:number}|null>(null);
  const [error, setError] = useState('');

  async function handleSync(e:React.FormEvent) {
    e.preventDefault(); setSyncing(true); setError(''); setResult(null);
    try {
      let tickets: unknown[];
      const trimmed = json.trim();
      if (!trimmed) { setError('Paste ticket JSON data'); setSyncing(false); return; }
      try { tickets = JSON.parse(trimmed); if(!Array.isArray(tickets)) tickets=[tickets]; }
      catch { setError('Invalid JSON'); setSyncing(false); return; }
      onBaseChange(baseUrl);
      const token=localStorage.getItem('accessToken');
      const r=await fetch('/api/tasks/manager/sync',{method:'POST',headers:{'Content-Type':'application/json',Authorization:`Bearer ${token}`},body:JSON.stringify({tickets})});
      const data=await r.json();
      if(data.success) { setResult(data.data); setTimeout(onSynced,1200); }
      else setError(data.error||'Sync failed');
    } catch(e:any) { setError(e.message); }
    finally { setSyncing(false); }
  }

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.65)',backdropFilter:'blur(4px)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'var(--bg-elevated)',borderRadius:'var(--radius-xl)',width:'100%',maxWidth:560,border:'1px solid var(--border-default)',overflow:'auto',maxHeight:'90vh',zIndex:10000}}>
        <div style={{padding:'18px 24px',borderBottom:'1px solid var(--border-subtle)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <h3 style={{margin:0}}>Sync Reqqer Tickets</h3>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-secondary)'}}><X size={18}/></button>
        </div>
        <form onSubmit={handleSync} style={{padding:'20px 24px',display:'flex',flexDirection:'column',gap:14}}>
          <div>
            <label className="form-label">Reqqer Base URL</label>
            <input className="form-input" value={baseUrl} onChange={e=>setBaseUrl(e.target.value)} placeholder="https://yourreqqer.com"/>
            <div style={{fontSize:'0.75rem',color:'var(--text-muted)',marginTop:4}}>
              Used for ticket links: [base]/req/HTMLReqForm.cfm?ReqIDs=XXXXXXX
            </div>
          </div>
          <div>
            <label className="form-label">Ticket Data (JSON)</label>
            <textarea className="form-input" rows={10} value={json} onChange={e=>setJson(e.target.value)}
              placeholder={'Paste JSON array of tickets:\n[\n  {"req_id":"4265496","title":"Water Temps","wing":"EAST WING","area":"221","category":"Routine Maint","status":"open","assigned_to":"Liviu Paul","assigned_initials":"LP"}\n]'}
              style={{fontFamily:'monospace',fontSize:'0.75rem'}}/>
          </div>
          <div style={{padding:'10px 14px',background:'var(--bg-elevated)',borderRadius:'var(--radius-sm)',fontSize:'0.8125rem',color:'var(--text-muted)'}}>
            Accepted fields: req_id, title, wing, area, location, assigned_to, assigned_initials, category, status, priority, created_date, due_date
          </div>
          {error && <div style={{color:'var(--accent-danger)',fontSize:'0.875rem'}}>{error}</div>}
          {result && (
            <div style={{padding:'12px 16px',background:'rgba(34,197,94,0.1)',borderRadius:'var(--radius-sm)',color:'#22c55e',fontSize:'0.875rem'}}>
              Synced {result.upserted} tickets ({result.skipped} skipped)
            </div>
          )}
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={syncing}><RefreshCw size={14}/> {syncing?'Syncing…':'Sync'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ReassignModal({item,onClose,onSaved}:{item:ManagerItem;onClose:()=>void;onSaved:()=>void}) {
  const [form,setForm]=useState({assigned_to:item.assigned_to||'',assigned_initials:item.assigned_initials||'',status:item.status,priority:item.priority});
  const [saving,setSaving]=useState(false);
  async function handleSubmit(e:React.FormEvent){
    e.preventDefault(); setSaving(true);
    try { await api.put(`/tasks/manager/${item.id}`,form); onSaved(); }
    finally { setSaving(false); }
  }
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.65)',backdropFilter:'blur(4px)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'var(--bg-elevated)',borderRadius:'var(--radius-xl)',width:'100%',maxWidth:440,border:'1px solid var(--border-default)',zIndex:10000}}>
        <div style={{padding:'18px 24px',borderBottom:'1px solid var(--border-subtle)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <h3 style={{margin:0}}>Edit / Reassign</h3>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-secondary)'}}><X size={18}/></button>
        </div>
        <form onSubmit={handleSubmit} style={{padding:'20px 24px',display:'flex',flexDirection:'column',gap:14}}>
          <div style={{padding:'10px 14px',background:'var(--bg-elevated)',borderRadius:'var(--radius-sm)',fontSize:'0.875rem',fontWeight:600}}>
            #{item.req_id} — {item.title}
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div><label className="form-label">Assigned To</label>
              <input className="form-input" value={form.assigned_to} onChange={e=>setForm(p=>({...p,assigned_to:e.target.value}))}/></div>
            <div><label className="form-label">Initials</label>
              <input className="form-input" value={form.assigned_initials} onChange={e=>setForm(p=>({...p,assigned_initials:e.target.value}))}/></div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div><label className="form-label">Status</label>
              <select className="form-input" value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))}>
                {['open','active','overdue','pending','completed'].map(s=><option key={s}>{s}</option>)}
              </select></div>
            <div><label className="form-label">Priority</label>
              <select className="form-input" value={form.priority} onChange={e=>setForm(p=>({...p,priority:e.target.value}))}>
                {['normal','low','medium','high','urgent'].map(s=><option key={s}>{s}</option>)}
              </select></div>
          </div>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end',paddingTop:4}}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving?'Saving…':'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
